/**
 * Guided survey walkthrough content derived from:
 * - CMS Conditions for Coverage (CfCs) — Appendix L, 42 CFR 416.25–416.52
 * - TJC Ambulatory Care / Office-Based Surgery standards
 * - AAAHC accreditation standards
 *
 * Each area contains domain-specific inspection steps with narrative guidance
 * telling the surveyor exactly what to look for, what to photograph, and what
 * questions to ask. Surveyors can skip any step or mark "All good."
 */

import type { FacilityArea, SurveyDomain } from './services/survey'
import type { AppendixLRef } from './appendixLReferences'

// ── Types ───────────────────────────────────────────────────────────────────

/**
 * One Yes/No/N/A row inside a guided-survey inspection item. PR-A
 * scaffolding (issue #1227) introduces this type so subsequent child
 * stories (#1228–#1231) can author per-item checklist prompts mapped to
 * specific 42 CFR §416 citations. Until then, items rely on the legacy
 * narrative-parser fallback in `getInspectionChecklistPrompts`.
 */
export interface ChecklistPrompt {
  id: string
  text: string
  appendixLRef: AppendixLRef
}

/**
 * Fallback shape returned by the narrative parser when no authored
 * `checklistPrompts` exist on an item. Distinct from `ChecklistPrompt`
 * because the parser cannot reliably synthesize an `appendixLRef` from
 * free-text narrative.
 */
export interface NarrativeChecklistPrompt {
  id: string
  text: string
}

export type RenderedChecklistPrompt = ChecklistPrompt | NarrativeChecklistPrompt

export interface InspectionItem {
  /** Short label for the checklist row */
  label: string
  /** Detailed narrative: what to look for, where, and why it matters */
  narrative: string
  /** Applicable CMS CfC tag (42 CFR 416.xx) */
  cmsTag: string
  /** TJC standard cross-reference (if applicable) */
  tjcRef?: string
  /** AAAHC standard cross-reference (if applicable) */
  aaahcRef?: string
  /** Suggested evidence type */
  evidence: ('photo' | 'document' | 'interview' | 'observation')[]
  /**
   * Authored Appendix-L checklist prompts for this item. Optional in
   * PR-A; when present, `getInspectionChecklistPrompts` returns these
   * verbatim instead of running the narrative parser. Reserved for
   * children #1228–#1231 to populate per-domain.
   */
  checklistPrompts?: ChecklistPrompt[]
}

export interface DomainSection {
  domain: SurveyDomain
  domainLabel: string
  items: InspectionItem[]
}

export type PhotoAngle =
  | 'entry'        // From doorway looking in
  | 'far-wall'     // From far wall looking back toward entry
  | 'left-wall'    // Left wall coverage
  | 'right-wall'   // Right wall coverage
  | 'equip-45L'    // Equipment 45° from front-left
  | 'equip-45R'    // Equipment 45° from front-right
  | 'equip-front'  // Equipment straight-on
  | 'closeup'      // Close-up detail (dates, labels, condition)
  | 'overview'     // Overall area shot

export interface PhotoRequirement {
  /** Short label shown in checklist */
  label: string
  /** Instruction for the surveyor */
  description: string
  /** Angle/type of shot */
  angle: PhotoAngle
  /** Minimum photos needed (>1 for full-wall coverage) */
  minCount: number
}

export interface AreaWalkthrough {
  area: FacilityArea
  areaLabel: string
  /** Brief orientation: what this area is and why it matters */
  introduction: string
  /** Suggested walk order within the area */
  walkOrder: string
  /** Mandatory photo requirements before inspection begins */
  requiredPhotos: PhotoRequirement[]
  /** Can this area be repeated for multiple rooms (e.g., OR 1, OR 2)? */
  isRepeatable?: boolean
  /** If repeatable, which rooms can be selected */
  repeatableRooms?: FacilityArea[]
  /** Estimated inspection time in minutes */
  estimatedMinutes?: number
  sections: DomainSection[]
}

// ── Standard photo sets ─────────────────────────────────────────────────────

/** 4-wall baseline required for every room/area */
const FOUR_WALL_BASELINE: PhotoRequirement[] = [
  { label: 'Entry view', description: 'Stand in the doorway. Photograph the full room from the entry point looking in. If the wall is too wide for one shot, take multiple overlapping photos.', angle: 'entry', minCount: 1 },
  { label: 'Far wall view', description: 'Walk to the far wall. Turn around and photograph back toward the entry. Capture the full width — take multiple overlapping shots if needed.', angle: 'far-wall', minCount: 1 },
  { label: 'Left wall view', description: 'Face the left wall from center of room. Photograph the entire wall length. Multiple overlapping shots if needed to cover full length.', angle: 'left-wall', minCount: 1 },
  { label: 'Right wall view', description: 'Face the right wall from center of room. Photograph the entire wall length. Multiple overlapping shots if needed to cover full length.', angle: 'right-wall', minCount: 1 },
]

/** SPD equipment photo requirements */
const SPD_EQUIPMENT_PHOTOS: PhotoRequirement[] = [
  { label: 'Equipment — 45° front-left', description: 'For each piece of reprocessing equipment (sterilizer, washer-disinfector, ultrasonic), photograph from 45 degrees to the front-left. Include the full unit and any attached gauges/displays.', angle: 'equip-45L', minCount: 1 },
  { label: 'Equipment — 45° front-right', description: 'Same equipment, photograph from 45 degrees to the front-right.', angle: 'equip-45R', minCount: 1 },
  { label: 'Equipment — straight-on', description: 'Same equipment, photograph straight-on from the front. Include control panel and any status displays.', angle: 'equip-front', minCount: 1 },
  { label: 'Cleaning area close-up', description: 'Close-up photos of cleaning/decontamination sinks and surfaces. Look for mineral deposits, biofilm, discoloration, standing water, or rust. Get close enough to see water quality issues clearly.', angle: 'closeup', minCount: 2 },
]

/** Medication storage photo requirements */
const MED_STORAGE_PHOTOS: PhotoRequirement[] = [
  { label: 'Medication shelves — date-readable', description: 'Photograph medication shelves at close enough range that expiration dates on labels are legible in the image. Take multiple shots to cover different shelf sections.', angle: 'closeup', minCount: 3 },
  { label: 'Medication cart overview', description: 'Photograph each medication cart or automated dispensing cabinet showing the overall unit and its surroundings.', angle: 'overview', minCount: 1 },
  { label: 'Refrigerator contents', description: 'Open the medication refrigerator and photograph contents. Dates on vials and bags must be readable. Include the temperature display.', angle: 'closeup', minCount: 2 },
]

// ── Common life safety items (checked in every area) ───────────────────────

const COMMON_LIFE_SAFETY_ITEMS: InspectionItem[] = [
  {
    label: 'Fire extinguisher — visible, accessible, current inspection',
    narrative:
      'Locate the nearest fire extinguisher in this area. Verify: (1) it is mounted on the wall and visible, not hidden behind furniture or equipment, (2) it has a current annual inspection tag (check the date — must be within 12 months), (3) the pressure gauge is in the green zone, (4) the pull pin and tamper seal are intact, (5) there is at least 36 inches of clearance in front. Photograph the inspection tag.',
    cmsTag: '§416.44(b), NFPA 10',
    tjcRef: 'LS.02.01.30',
    aaahcRef: 'Ch. 8 — Facilities and Environment',
    evidence: ['photo', 'observation'],
  },
  {
    label: 'Smoke detectors and sprinkler heads',
    narrative:
      'Look up at the ceiling in this area. Verify smoke detectors are present and not covered, painted over, or obstructed. If the facility has a sprinkler system, check that sprinkler heads are not blocked — there must be at least 18 inches of clearance below each sprinkler head. No items should be hung from or touching sprinkler piping. Note any missing or damaged heads.',
    cmsTag: '§416.44(b), NFPA 13/72',
    tjcRef: 'LS.02.01.10, LS.02.01.35',
    aaahcRef: 'Ch. 8 — Facilities and Environment',
    evidence: ['photo', 'observation'],
  },
  {
    label: 'EXIT signage and egress path',
    narrative:
      'Verify illuminated EXIT signs are visible from this area and point toward a clear path of egress. Check that the egress path is not blocked by furniture, equipment, or supplies. Exit doors must not be locked against egress. Note any burned-out EXIT sign bulbs.',
    cmsTag: '§416.44(b), LSC NFPA 101',
    tjcRef: 'LS.02.01.20',
    aaahcRef: 'Ch. 8 — Facilities and Environment',
    evidence: ['photo', 'observation'],
  },
  {
    label: 'Power strips, extension cords, and electrical safety',
    narrative:
      'Check every power strip and electrical cord in this area. Per §416.44(b) and NFPA 99, patient care areas require hospital-grade power strips. Look for: (1) flip the power strip over and check the UL listing — it must be UL 1363A (healthcare facility grade) or UL 60601-1; a standard commercial power strip rated UL 1363 (without the "A") is NOT acceptable in patient care areas, (2) check the plug for a green dot indicating hospital-grade, (3) verify there is an integrated circuit breaker, (4) NO daisy-chaining — a power strip plugged into another power strip is an automatic finding, (5) NO extension cords used as permanent wiring — extension cords are for temporary use only (typically less than 24 hours), (6) cords must not run under carpets, through walls or ceilings, or create trip hazards. Common finding: consumer-grade surge protectors from retail stores (Belkin, APC home models, etc.) are almost never UL 1363A rated. Check under desks, behind equipment, and along walls.',
    cmsTag: '§416.44(b), NFPA 99',
    tjcRef: 'EC.02.05.01, LS.02.01.20',
    aaahcRef: 'Ch. 8 — Facilities and Environment',
    evidence: ['photo', 'observation'],
  },
]

const COMMON_LIFE_SAFETY_PROMPTS_BY_AREA: Record<
  string,
  Record<string, ChecklistPrompt[]>
> = {
  'Admin Office': {
    'Fire extinguisher — visible, accessible, current inspection': [
      {
        id: 'admin-lsc-fire-extinguisher-visible-accessible',
        text: 'Is a fire extinguisher visibly mounted and immediately accessible in the area?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Smoke detectors and sprinkler heads': [
      {
        id: 'admin-lsc-smoke-detectors-sprinkler-heads-clear',
        text: 'Are smoke detectors and sprinkler heads free of obstructions and in required locations?',
        appendixLRef: '416.44(b)',
      },
    ],
    'EXIT signage and egress path': [
      {
        id: 'admin-lsc-exit-signage-visible',
        text: 'Are exit signs illuminated and visible with a clear egress path from this area?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Power strips, extension cords, and electrical safety': [
      {
        id: 'admin-lsc-power-strips-safe-use',
        text: 'Are power strips and extension cords used safely and labeled in this area?',
        appendixLRef: '416.44(b)',
      },
    ],
  },
  'Pre-Op': {
    'Fire extinguisher — visible, accessible, current inspection': [
      {
        id: 'preop-lsc-fire-extinguisher-visible-accessible',
        text: 'Is a fire extinguisher visibly mounted and immediately accessible in Pre-Op?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Smoke detectors and sprinkler heads': [
      {
        id: 'preop-lsc-smoke-detectors-sprinkler-heads-clear',
        text: 'Are smoke detectors and sprinkler heads unobstructed and present in Pre-Op?',
        appendixLRef: '416.44(b)',
      },
    ],
    'EXIT signage and egress path': [
      {
        id: 'preop-lsc-exit-signage-visible',
        text: 'Are exit signs illuminated and the egress path clear from Pre-Op?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Power strips, extension cords, and electrical safety': [
      {
        id: 'preop-lsc-power-strips-safe-use',
        text: 'Are power strips and extension cords in Pre-Op used safely and in good condition?',
        appendixLRef: '416.44(b)',
      },
    ],
  },
  PACU: {
    'Fire extinguisher — visible, accessible, current inspection': [
      {
        id: 'pacu-lsc-fire-extinguisher-visible-accessible',
        text: 'Is a fire extinguisher visible and accessible in PACU?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Smoke detectors and sprinkler heads': [
      {
        id: 'pacu-lsc-smoke-detectors-sprinkler-heads-clear',
        text: 'Are smoke detectors and sprinkler heads unobstructed and in required locations in PACU?',
        appendixLRef: '416.44(b)',
      },
    ],
    'EXIT signage and egress path': [
      {
        id: 'pacu-lsc-exit-signage-visible',
        text: 'Are PACU exit signs illuminated and the egress path clear?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Power strips, extension cords, and electrical safety': [
      {
        id: 'pacu-lsc-power-strips-safe-use',
        text: 'Are power strips and extension cords in PACU safe and not misused?',
        appendixLRef: '416.44(b)',
      },
    ],
  },
  SPD: {
    'Fire extinguisher — visible, accessible, current inspection': [
      {
        id: 'spd-lsc-fire-extinguisher-visible-accessible',
        text: 'Is a fire extinguisher visible and accessible in SPD?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Smoke detectors and sprinkler heads': [
      {
        id: 'spd-lsc-smoke-detectors-sprinkler-heads-clear',
        text: 'Are smoke detectors and sprinkler heads present and clear of obstructions in SPD?',
        appendixLRef: '416.44(b)',
      },
    ],
    'EXIT signage and egress path': [
      {
        id: 'spd-lsc-exit-signage-visible',
        text: 'Are exit signs illuminated and the egress path clear in SPD?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Power strips, extension cords, and electrical safety': [
      {
        id: 'spd-lsc-power-strips-safe-use',
        text: 'Are power strips and extension cords in SPD managed safely and labelled?',
        appendixLRef: '416.44(b)',
      },
    ],
  },
  'Med Storage': {
    'Fire extinguisher — visible, accessible, current inspection': [
      {
        id: 'medstorage-lsc-fire-extinguisher-visible-accessible',
        text: 'Is a fire extinguisher visible and accessible in medication storage?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Smoke detectors and sprinkler heads': [
      {
        id: 'medstorage-lsc-smoke-detectors-sprinkler-heads-clear',
        text: 'Are smoke detectors and sprinkler heads clear of obstructions in medication storage?',
        appendixLRef: '416.44(b)',
      },
    ],
    'EXIT signage and egress path': [
      {
        id: 'medstorage-lsc-exit-signage-visible',
        text: 'Are exit signs illuminated and the egress path clear in medication storage?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Power strips, extension cords, and electrical safety': [
      {
        id: 'medstorage-lsc-power-strips-safe-use',
        text: 'Are power strips and extension cords in medication storage safe and compliant?',
        appendixLRef: '416.44(b)',
      },
    ],
  },
  'Clean Utility': {
    'Fire extinguisher — visible, accessible, current inspection': [
      {
        id: 'cleanutility-lsc-fire-extinguisher-visible-accessible',
        text: 'Is a fire extinguisher visible and accessible in clean utility?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Smoke detectors and sprinkler heads': [
      {
        id: 'cleanutility-lsc-smoke-detectors-sprinkler-heads-clear',
        text: 'Are smoke detectors and sprinkler heads present and not obstructed in clean utility?',
        appendixLRef: '416.44(b)',
      },
    ],
    'EXIT signage and egress path': [
      {
        id: 'cleanutility-lsc-exit-signage-visible',
        text: 'Are exit signs illuminated and the egress path clear in clean utility?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Power strips, extension cords, and electrical safety': [
      {
        id: 'cleanutility-lsc-power-strips-safe-use',
        text: 'Are power strips and extension cords in clean utility safe and appropriately used?',
        appendixLRef: '416.44(b)',
      },
    ],
  },
  'Soiled Utility': {
    'Fire extinguisher — visible, accessible, current inspection': [
      {
        id: 'soiledutility-lsc-fire-extinguisher-visible-accessible',
        text: 'Is a fire extinguisher visible and accessible in soiled utility?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Smoke detectors and sprinkler heads': [
      {
        id: 'soiledutility-lsc-smoke-detectors-sprinkler-heads-clear',
        text: 'Are smoke detectors and sprinkler heads unobstructed in soiled utility?',
        appendixLRef: '416.44(b)',
      },
    ],
    'EXIT signage and egress path': [
      {
        id: 'soiledutility-lsc-exit-signage-visible',
        text: 'Are exit signs illuminated and the egress path clear in soiled utility?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Power strips, extension cords, and electrical safety': [
      {
        id: 'soiledutility-lsc-power-strips-safe-use',
        text: 'Are power strips and extension cords in soiled utility safe and compliant?',
        appendixLRef: '416.44(b)',
      },
    ],
  },
  Restroom: {
    'Fire extinguisher — visible, accessible, current inspection': [
      {
        id: 'restroom-lsc-fire-extinguisher-visible-accessible',
        text: 'Is a fire extinguisher visible and accessible in the restroom area?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Smoke detectors and sprinkler heads': [
      {
        id: 'restroom-lsc-smoke-detectors-sprinkler-heads-clear',
        text: 'Are smoke detectors and sprinkler heads clear and present in restroom areas?',
        appendixLRef: '416.44(b)',
      },
    ],
    'EXIT signage and egress path': [
      {
        id: 'restroom-lsc-exit-signage-visible',
        text: 'Are exit signs visible and is the restroom egress path clear?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Power strips, extension cords, and electrical safety': [
      {
        id: 'restroom-lsc-power-strips-safe-use',
        text: 'Are power strips and extension cords in restroom areas used safely?',
        appendixLRef: '416.44(b)',
      },
    ],
  },
  'Staff Lounge': {
    'Fire extinguisher — visible, accessible, current inspection': [
      {
        id: 'stafflounge-lsc-fire-extinguisher-visible-accessible',
        text: 'Is a fire extinguisher visible and accessible in the staff lounge?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Smoke detectors and sprinkler heads': [
      {
        id: 'stafflounge-lsc-smoke-detectors-sprinkler-heads-clear',
        text: 'Are smoke detectors and sprinkler heads present and clear in the staff lounge?',
        appendixLRef: '416.44(b)',
      },
    ],
    'EXIT signage and egress path': [
      {
        id: 'stafflounge-lsc-exit-signage-visible',
        text: 'Are exit signs illuminated and the egress path clear in staff lounge?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Power strips, extension cords, and electrical safety': [
      {
        id: 'stafflounge-lsc-power-strips-safe-use',
        text: 'Are power strips and extension cords in the staff lounge used safely?',
        appendixLRef: '416.44(b)',
      },
    ],
  },
  'Lobby': {
    'Fire extinguisher — visible, accessible, current inspection': [
      {
        id: 'lobby-lsc-fire-extinguisher-visible-accessible',
        text: 'Is a fire extinguisher visibly mounted and immediately accessible in the lobby/reception area?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Smoke detectors and sprinkler heads': [
      {
        id: 'lobby-lsc-smoke-detectors-sprinkler-heads-clear',
        text: 'Are smoke detectors and sprinkler heads in the lobby unobstructed and intact?',
        appendixLRef: '416.44(b)',
      },
    ],
    'EXIT signage and egress path': [
      {
        id: 'lobby-lsc-exit-signage-visible',
        text: 'Are exit signs illuminated and visible with a clear egress path from the lobby?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Power strips, extension cords, and electrical safety': [
      {
        id: 'lobby-lsc-power-strips-safe-use',
        text: 'Are power strips and extension cords in the lobby hospital-grade where required and free of daisy-chaining or trip hazards?',
        appendixLRef: '416.44(b)',
      },
    ],
  },
  'OR 1': {
    'Fire extinguisher — visible, accessible, current inspection': [
      {
        id: 'or1-lsc-fire-extinguisher-visible-accessible',
        text: 'Is a fire extinguisher visibly mounted and immediately accessible inside or immediately outside this OR?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Smoke detectors and sprinkler heads': [
      {
        id: 'or1-lsc-smoke-detectors-sprinkler-heads-clear',
        text: 'Are smoke detectors and sprinkler heads in this OR unobstructed and intact?',
        appendixLRef: '416.44(b)',
      },
    ],
    'EXIT signage and egress path': [
      {
        id: 'or1-lsc-exit-signage-visible',
        text: 'Are exit signs illuminated and visible with a clear egress path from this OR?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Power strips, extension cords, and electrical safety': [
      {
        id: 'or1-lsc-power-strips-safe-use',
        text: 'Are all power strips in this OR UL 1363A hospital-grade with no daisy-chaining or extension cords used as permanent wiring?',
        appendixLRef: '416.44(b)',
      },
    ],
  },
  'Mechanical': {
    'Fire extinguisher — visible, accessible, current inspection': [
      {
        id: 'mech-lsc-fire-extinguisher-visible-accessible',
        text: 'Is a fire extinguisher visibly mounted and immediately accessible in the mechanical/utility area?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Smoke detectors and sprinkler heads': [
      {
        id: 'mech-lsc-smoke-detectors-sprinkler-heads-clear',
        text: 'Are smoke detectors and sprinkler heads in the mechanical area unobstructed and intact?',
        appendixLRef: '416.44(b)',
      },
    ],
    'EXIT signage and egress path': [
      {
        id: 'mech-lsc-exit-signage-visible',
        text: 'Are exit signs illuminated and visible with a clear egress path from the mechanical area?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Power strips, extension cords, and electrical safety': [
      {
        id: 'mech-lsc-power-strips-safe-use',
        text: 'Are power strips and electrical cords in the mechanical area used safely and free of daisy-chaining?',
        appendixLRef: '416.44(b)',
      },
    ],
  },
  'Corridor': {
    'Fire extinguisher — visible, accessible, current inspection': [
      {
        id: 'corr-lsc-fire-extinguisher-visible-accessible',
        text: 'Is a fire extinguisher visibly mounted and immediately accessible in the corridor?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Smoke detectors and sprinkler heads': [
      {
        id: 'corr-lsc-smoke-detectors-sprinkler-heads-clear',
        text: 'Are smoke detectors and sprinkler heads in the corridor unobstructed and intact?',
        appendixLRef: '416.44(b)',
      },
    ],
    'EXIT signage and egress path': [
      {
        id: 'corr-lsc-exit-signage-visible',
        text: 'Are exit signs illuminated and visible at every corridor turn and intersection?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Power strips, extension cords, and electrical safety': [
      {
        id: 'corr-lsc-power-strips-safe-use',
        text: 'Are corridor power strips and electrical cords used safely with no daisy-chaining or trip hazards?',
        appendixLRef: '416.44(b)',
      },
    ],
  },
  'Exterior': {
    'Fire extinguisher — visible, accessible, current inspection': [
      {
        id: 'ext-lsc-fire-extinguisher-visible-accessible',
        text: 'Is a fire extinguisher visibly mounted and accessible at exterior locations where required (e.g., near medical-gas bulk storage)?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Smoke detectors and sprinkler heads': [
      {
        id: 'ext-lsc-smoke-detectors-sprinkler-heads-clear',
        text: 'For sheltered exterior areas, are smoke detectors and sprinkler heads (where present) unobstructed and intact?',
        appendixLRef: '416.44(b)',
      },
    ],
    'EXIT signage and egress path': [
      {
        id: 'ext-lsc-exit-signage-visible',
        text: 'Are exterior exit-discharge paths illuminated, signed, and clear of obstructions?',
        appendixLRef: '416.44(b)',
      },
    ],
    'Power strips, extension cords, and electrical safety': [
      {
        id: 'ext-lsc-power-strips-safe-use',
        text: 'Are any exterior electrical fixtures or cords in good repair and rated for outdoor use (no consumer-grade indoor cords exposed to weather)?',
        appendixLRef: '416.44(b)',
      },
    ],
  },
}

function commonLifeSafetySection(area: string): DomainSection {
  const areaPrompts = COMMON_LIFE_SAFETY_PROMPTS_BY_AREA[area] ?? {}

  return {
    domain: 'LSC' as SurveyDomain,
    domainLabel: 'Life Safety Code',
    items: COMMON_LIFE_SAFETY_ITEMS.map((item) => {
      const prompts = areaPrompts[item.label]
      if (!prompts) {
        return item
      }

      return {
        ...item,
        checklistPrompts: prompts,
      }
    }),
  }
}

// ── Guide Data ──────────────────────────────────────────────────────────────

export const SURVEY_WALKTHROUGH: AreaWalkthrough[] = [
  // ═══════════════════════════════════════════════════════════════════════════
  // LOBBY / RECEPTION
  // ═══════════════════════════════════════════════════════════════════════════
  {
    area: 'Lobby',
    areaLabel: 'Lobby / Reception Area',
    introduction:
      'Start at the front entrance. The lobby is the first impression and tells you a lot about facility culture. You are looking at patient rights postings, emergency preparedness signage, ADA compliance, general cleanliness, and life safety.',
    walkOrder:
      'Enter through the main patient entrance. Scan walls for required postings. Check the reception desk area. Look at waiting room furniture and flooring. Check restrooms off the lobby if present. Note exit signage and fire extinguisher locations.',
    requiredPhotos: [...FOUR_WALL_BASELINE],
    estimatedMinutes: 15,
    sections: [
      {
        domain: 'COMP',
        domainLabel: 'Compliance / Patient Rights',
        items: [
          {
            label: 'Patient Rights posting',
            narrative:
              'Look for a clearly visible Patient Rights notice in the waiting area. Per CMS §416.50(a), the ASC must inform each patient or representative of the patient\'s rights in advance of the date of the procedure. Check that it is posted in English and any other language common to the patient population. The notice must include the right to: be fully informed about the procedure, participate in care decisions, formulate advance directives, privacy and confidentiality, be free from abuse/harassment, voice grievances, and non-discrimination. If you don\'t see it posted, ask staff where the notice is provided.',
            cmsTag: '§416.50(a), §416.42',
            tjcRef: 'RI.01.01.01',
            aaahcRef: 'Ch. 5 — Rights of Patients',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'lobby-comp-rights-patient-rights-posting',
                text: 'Is there a visible posting in the facility that clearly outlines patients\' rights and responsibilities?',
                appendixLRef: '416.50(a)',
              },
              {
                id: 'lobby-comp-rights-advance-directive-info',
                text: 'Does the patient rights posting include information about patients\' rights to make advance directives?',
                appendixLRef: '416.50(a)',
              },
              {
                id: 'lobby-comp-rights-language-accessibility',
                text: 'Is the Patient Rights notice posted in English and any other language common to the patient population?',
                appendixLRef: '416.50(a)',
              },
              {
                id: 'lobby-comp-rights-privacy-abuse-grievance',
                text: 'Does the posting cover the rights to privacy and confidentiality, freedom from abuse or harassment, to voice grievances, and to non-discrimination?',
                appendixLRef: '416.50(a)',
              },
              {
                id: 'lobby-comp-rights-timing-advance-of-procedure',
                text: 'Are patients informed of their rights in advance of the date of the procedure (not on the day of surgery)?',
                appendixLRef: '416.50(a)',
              },
            ],
          },
          {
            label: 'Grievance process posting',
            narrative:
              'Look for posted information on how patients can file a grievance. Per CMS §416.45, the ASC must have a grievance process, and must inform patients who to contact to file a grievance. The posting should include the phone number and address of the State agency (state health department survey agency) and should mention the right to contact the Medicare Beneficiary Ombudsman. Photograph the posting if present, or note its absence.',
            cmsTag: '§416.45',
            tjcRef: 'RI.01.07.01',
            aaahcRef: 'Ch. 5.I.B — Grievance/Complaint Mechanism',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'lobby-comp-grievance-grievance-process-posting',
                text: 'Is the facility\'s grievance process clearly posted in a visible location within the facility?',
                appendixLRef: '416.45',
              },
              {
                id: 'lobby-comp-grievance-state-agency-contact',
                text: 'Does the posted grievance process include instructions for submitting complaints about the quality of care provided by medical staff?',
                appendixLRef: '416.45',
              },
              {
                id: 'lobby-comp-grievance-state-survey-agency',
                text: 'Does the grievance posting include the phone number and address of the State survey agency (state health department) for filing complaints?',
                appendixLRef: '416.45',
              },
              {
                id: 'lobby-comp-grievance-medicare-ombudsman',
                text: 'Does the posting inform patients of their right to contact the Medicare Beneficiary Ombudsman?',
                appendixLRef: '416.45',
              },
            ],
          },
          {
            label: 'Non-discrimination notice (Section 1557)',
            narrative:
              'Check for a non-discrimination notice, typically near registration. Facilities receiving federal funds must post notice of non-discrimination. This is often combined with the Patient Rights posting. Look for language about non-discrimination based on race, color, national origin, sex, age, or disability.',
            cmsTag: '§416.50(a)',
            tjcRef: 'RI.01.01.01',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'lobby-comp-nondiscrim-non-discrimination-notice',
                text: 'Is a non-discrimination notice posted in all patient areas?',
                appendixLRef: '416.50(a)',
              },
              {
                id: 'lobby-comp-nondiscrim-language-access-posted',
                text: 'Are all patients provided with a written copy of the non-discrimination notice upon admission?',
                appendixLRef: '416.50(a)',
              },
            ],
          },
          {
            label: 'Advance Directives information',
            narrative:
              'Verify that the facility provides written information to patients about advance directives. Per CMS §416.44, the ASC must provide each patient with written information about their right under State law to make decisions about medical care, including the right to accept or refuse treatment and to formulate advance directives. Ask the receptionist: "Can you show me the advance directive information you provide to patients?" This is often in the admission packet.',
            cmsTag: '§416.44',
            tjcRef: 'RI.01.05.01',
            aaahcRef: 'Ch. 5.I.E — Advance Directives',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'lobby-comp-directives-advance-directives-info',
                text: 'Is there a written policy in place that outlines how the ASC will provide information about advance directives to patients and their representatives?',
                appendixLRef: '416.44',
              },
              {
                id: 'lobby-comp-directives-advance-directives-training',
                text: 'Have all staff members received training on the ASC\'s policy for providing information about advance directives?',
                appendixLRef: '416.44',
              },
            ],
          },
          {
            label: 'Physician ownership disclosure (§416.52)',
            narrative:
              'Per §416.52, if any physician who performs surgery at the ASC has a direct or indirect financial interest in the facility, the ASC must provide written notice to patients. Check: (1) is a disclosure notice posted in a visible location in the lobby or waiting area?, (2) is the disclosure included in the patient admission packet or provided before the procedure?, (3) does the notice clearly state which physicians have an ownership or investment interest?, (4) does it inform the patient of their right to choose an alternative facility? Ask the front desk: "Do any of the surgeons here have an ownership interest in this facility? How do you inform patients about that?" This is a commonly missed requirement.',
            cmsTag: '§416.52',
            tjcRef: 'RI.01.01.01',
            aaahcRef: 'Ch. 1 — Governance',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'lobby-comp-ownership-physician-ownership-disclosure',
                text: 'Is there a written policy that discloses physician ownership of the facility or services provided?',
                appendixLRef: '416.52',
              },
              {
                id: 'lobby-comp-ownership-patient-choice-included',
                text: 'Are all physicians who own a direct or indirect interest in the facility or services required to disclose their ownership in writing to patients and their representatives?',
                appendixLRef: '416.52',
              },
            ],
          },
        ],
      },
      {
        domain: 'LSC',
        domainLabel: 'Life Safety Code',
        items: [
          {
            label: 'Exit signage and path of egress',
            narrative:
              'From the lobby entrance, verify illuminated EXIT signs are visible and point toward a clear path of egress. Check that exit doors are not blocked, locked against egress, or obstructed by furniture, carts, or equipment. The path should be unimpeded all the way to the exterior. Check that exit signs are lit (not burned out). Look above every door that leads to an exit — each should have a sign.',
            cmsTag: '§416.44(b), LSC NFPA 101',
            tjcRef: 'LS.02.01.20',
            aaahcRef: 'Ch. 8 — Facilities and Environment',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'lobby-lsc-exit-signage-path-exit-signs-visibility',
                text: 'Are illuminated EXIT signs visible from the lobby entrance and pointing toward a clear path of egress?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'lobby-lsc-exit-signage-path-exit-door-obstruction',
                text: 'Are exit doors unobstructed and not locked against egress, with no furniture, carts, or equipment blocking the path to the exterior?',
                appendixLRef: '416.44(b)',
              },
            ],
          },
          {
            label: 'Fire extinguisher — visible, accessible, current inspection',
            narrative:
              'Locate the nearest fire extinguisher. Verify: (1) it is mounted on the wall and visible, not hidden behind furniture, (2) it has a current annual inspection tag (check the date — must be within 12 months), (3) the pressure gauge is in the green zone, (4) the pull pin and tamper seal are intact, (5) there is at least 36 inches of clearance in front. Photograph the inspection tag date.',
            cmsTag: '§416.44(b), NFPA 10',
            tjcRef: 'LS.02.01.30',
            evidence: ['photo'],
            checklistPrompts: [
              {
                id: 'lobby-lsc-fire-extinguisher-visible-fire-extinguisher-visible',
                text: 'Is the fire extinguisher mounted on the wall and visible, not hidden behind furniture or obstructions?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'lobby-lsc-fire-extinguisher-visible-fire-extinguisher-inspection-tag',
                text: 'Does the fire extinguisher have a current annual inspection tag with a date within the last 12 months?',
                appendixLRef: '416.44(b)',
              },
            ],
          },
          {
            label: 'Smoke detectors and sprinkler heads',
            narrative:
              'Look up. Verify smoke detectors are present and not covered, painted over, or obstructed. If the facility has a sprinkler system, check that sprinkler heads are not blocked — there must be at least 18 inches of clearance below each sprinkler head. No items should be hung from or touching sprinkler piping.',
            cmsTag: '§416.44(b), NFPA 13/72',
            tjcRef: 'LS.02.01.10, LS.02.01.35',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'lobby-lsc-smoke-detectors-sprinkler-smoke-detectors-present',
                text: 'Are all smoke detectors present and not covered, painted over, or obstructed?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'lobby-lsc-smoke-detectors-sprinkler-sprinkler-clearance',
                text: 'Is there at least 18 inches of clearance below each sprinkler head?',
                appendixLRef: '416.44(b)',
              },
            ],
          },
        ],
      },
      {
        domain: 'ENV',
        domainLabel: 'Environment of Care',
        items: [
          {
            label: 'General cleanliness and maintenance',
            narrative:
              'Scan the lobby floor, walls, ceiling, and furniture. Look for: stained or damaged ceiling tiles (water stains can indicate roof leaks or plumbing issues), cracked or chipped flooring, damaged wall surfaces, torn or soiled upholstery on waiting room chairs. The facility must maintain a clean, safe, and functional environment per CMS §416.44(a). Note any strong odors — chemical smells may indicate HVAC issues.',
            cmsTag: '§416.44(a)(1)',
            tjcRef: 'EC.02.06.01',
            aaahcRef: 'Ch. 8.II — Safety',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'lobby-env-general-cleanliness-maintenance-lobby-cleanliness',
                text: 'Are the lobby ceiling tiles free of stains and damage that could indicate roof leaks or plumbing issues?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'lobby-env-general-cleanliness-maintenance-flooring-condition',
                text: 'Is the flooring in the lobby free from cracks or chips?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'lobby-env-walls-and-upholstery',
                text: 'Are lobby wall surfaces undamaged and waiting room chair upholstery free of tears or soiling?',
                appendixLRef: '416.44(a)(1)',
              },
            ],
          },
          {
            label: 'ADA accessibility',
            narrative:
              'Quickly assess ADA compliance: Is the entrance accessible (ramp or level entry, automatic or easy-open doors)? Are doorways at least 32 inches wide? Is the reception counter at an accessible height or is there a lowered section? Are waiting room aisles navigable by wheelchair? These are general observations — note anything that appears to create a barrier to access.',
            cmsTag: '§416.44(a)',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'lobby-env-ada-accessibility-entrance-accessibility',
                text: 'Is the entrance accessible with a ramp or level entry and automatic or easy-open doors?',
                appendixLRef: '416.44(a)',
              },
              {
                id: 'lobby-env-ada-accessibility-doorway-width-compliance',
                text: 'Are all doorways at least 32 inches wide to meet ADA requirements?',
                appendixLRef: '416.44(a)',
              },
            ],
          },
        ],
      },
      {
        domain: 'ADA' as SurveyDomain,
        domainLabel: 'ADA Accessibility',
        items: [
          {
            label: 'ADA parking compliance',
            narrative:
              'Walk out to the parking area and verify ADA-compliant accessible parking per the Americans with Disabilities Act Accessibility Guidelines (ADAAG). Check: (1) the correct number of van-accessible spaces based on total lot capacity, (2) International Symbol of Accessibility (ISA) signage is posted on a sign pole (not just painted on the ground) at each accessible space, with "Van Accessible" designation where required, (3) an adjacent access aisle of at least 60 inches (96 inches for van-accessible) is marked and free of obstructions, (4) the path of travel from accessible parking to the building entrance is smooth, firm, stable, and free of trip hazards — curb cuts with detectable warnings are present where needed. Photograph the accessible spaces, signage, and path of travel.',
            cmsTag: '28CFR36.502',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'lobby-ada-parking-ada-parking-compliance',
                text: 'Is there at least one van-accessible parking space provided in the facility\'s parking area?',
                appendixLRef: '28CFR36.502',
              },
              {
                id: 'lobby-ada-parking-accessible-spaces-van-aisle',
                text: 'Does the facility provide a clear path of at least 36 inches in width from the parking area to the entrance?',
                appendixLRef: '28CFR36.502',
              },
            ],
          },
          {
            label: 'Entrance accessibility',
            narrative:
              'Check the main patient entrance for ADA compliance per ADAAG standards. Verify: (1) at least one entrance door provides a minimum 32-inch clear width when open at 90 degrees, (2) door thresholds are less than 1/2 inch in height and beveled, (3) door hardware is operable with one hand without tight grasping, pinching, or twisting — lever handles are preferred, (4) if the door is heavy or manual, check whether a power-assist opener or automatic door operator is available and functioning. Test it. Note any barriers that would prevent a patient using a wheelchair, walker, or other mobility device from entering independently.',
            cmsTag: '28CFR36.502',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'lobby-ada-entrance-entrance-accessible-path',
                text: 'Is there a minimum 36-inch clear path of travel from the parking area to the entrance?',
                appendixLRef: '28CFR36.502',
              },
              {
                id: 'lobby-ada-entrance-entrance-hardware-type',
                text: 'Are all entrance hardware, such as door handles and push plates, lever-style and operable with one hand?',
                appendixLRef: '28CFR36.502',
              },
              {
                id: 'lobby-ada-entrance-thresholds-beveled',
                text: 'Are entrance door thresholds less than 1/2 inch in height and beveled to permit wheelchair and walker passage?',
                appendixLRef: '28CFR36.502',
              },
            ],
          },
          {
            label: 'Communication accessibility',
            narrative:
              'Verify the facility can effectively communicate with patients who have sensory or cognitive disabilities per ADA Title III requirements. Check: (1) ask the front desk staff about sign language interpreter availability — the facility should have a contract or on-call arrangement with a qualified interpreter service, (2) TTY/TDD (teletypewriter/telecommunications device for the deaf) or video relay service is available for phone communication, (3) large-print versions of key documents are available on request — patient rights, consent forms, discharge instructions. Ask the receptionist: "If a deaf patient arrives for surgery, how do you communicate with them? What about a patient who is visually impaired — do you have large-print documents?"',
            cmsTag: '28CFR36.503',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'lobby-ada-comm-accessible-parking',
                text: 'Does the facility have a contract or on-call arrangement with a qualified interpreter service, and is TTY/TDD or video relay service available for phone communication with deaf or hard-of-hearing patients?',
                appendixLRef: '28CFR36.503',
              },
              {
                id: 'lobby-ada-comm-clear-path-of-travel',
                text: 'Are large-print versions of key patient documents — including patient rights, consent forms, and discharge instructions — available on request for patients with visual or cognitive disabilities?',
                appendixLRef: '28CFR36.503',
              },
            ],
          },
        ],
      },
      {
        domain: 'IC' as SurveyDomain,
        domainLabel: 'Infection Control — Respiratory Hygiene',
        items: [
          {
            label: 'Respiratory hygiene signage',
            narrative:
              'Check the waiting area for respiratory hygiene and cough etiquette signage per CDC guidelines for infection control in healthcare settings. Per CDC IC recommendations, facilities should post visual alerts at entrances instructing patients and visitors with signs or symptoms of respiratory infection to: (1) cover their mouth and nose when coughing or sneezing, (2) use tissues and dispose of them properly, (3) perform hand hygiene after contact with respiratory secretions. Look for posted signage in the waiting room and at the reception desk. Tissues and hand sanitizer should be available near the signage. Masks should be offered to symptomatic patients. Photograph the posted signage or note its absence.',
            cmsTag: '416.51(a)',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'respiratory-hygiene-protocols-posted',
                text: 'Are respiratory hygiene and cough-etiquette visual alerts posted at the entrance, reception desk, and waiting area?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'respiratory-hygiene-tissues-disposal',
                text: 'Are tissues and a no-touch disposal receptacle available near the respiratory hygiene signage?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'respiratory-hygiene-hand-sanitizer',
                text: 'Is hand sanitizer or handwashing access available in the waiting and reception areas for patients and visitors with respiratory symptoms?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'respiratory-hygiene-mask-offering',
                text: 'Are masks available and offered to symptomatic patients on arrival?',
                appendixLRef: '416.51(b)',
              },
            ],
          },
        ],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ADMIN OFFICE
  // ═══════════════════════════════════════════════════════════════════════════
  {
    area: 'Admin Office',
    areaLabel: 'Administrative / Business Office',
    introduction:
      'The administrative area is where you verify governance, credentialing, policies, QAPI program documentation, and personnel files. Most of your document review happens here. Ask to sit at a table and have files brought to you.',
    walkOrder:
      'Request to review documents in a private area. Start with governing body and QAPI, then move to credentialing, personnel files, policy and procedure manuals, and emergency management plans.',
    requiredPhotos: [...FOUR_WALL_BASELINE],
    estimatedMinutes: 30,
    sections: [
      {
        domain: 'COMP',
        domainLabel: 'Governing Body & Management',
        items: [
          {
            label: 'Governing body documentation',
            narrative:
              'Request the governing body bylaws or operating agreement. Per CMS §416.41, the ASC must have an effective governing body that is legally responsible for the conduct of the ASC. Verify: (1) the governing body is identified, (2) there are meeting minutes showing oversight activity, (3) the GB has appointed a qualified administrator, (4) the GB has adopted patient care policies. Ask: "Who is the governing body? How often do they meet? Can I see recent meeting minutes?"',
            cmsTag: '§416.41',
            tjcRef: 'LD.01.03.01',
            aaahcRef: 'Ch. 1 — Governance',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'admin-comp-govbody-governing-body-documentation',
                text: 'Is there documentation showing the governing body is legally responsible for the ASC\'s policy, medical staff appointments, QAPI oversight, and compliance?',
                appendixLRef: '416.41',
              },
              {
                id: 'admin-comp-govbody-governing-body-effectiveness',
                text: 'Does the governing body have written policies and procedures demonstrating effectiveness in overseeing the ASC\'s operations?',
                appendixLRef: '416.41',
              },
            ],
          },
          {
            label: 'QAPI program documentation',
            narrative:
              'Request the Quality Assessment and Performance Improvement (QAPI) program plan. Per CMS §416.43, the ASC must develop, implement, and maintain an effective, ongoing, data-driven QAPI program. Verify: (1) the program is written and current, (2) it addresses patient safety and quality indicators, (3) it includes adverse event tracking and analysis, (4) there is evidence of corrective action taken on identified issues, (5) the governing body has reviewed QAPI reports. Ask: "Can you show me your last two quarterly QAPI reports?" Look for measurable indicators, tracked data, and documented improvement actions.',
            cmsTag: '§416.43',
            tjcRef: 'PI.01.01.01',
            aaahcRef: 'Ch. 4 — Quality Management and Improvement',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'admin-comp-qapi-plan-written-current',
                text: 'Is the QAPI program plan written and current?',
                appendixLRef: '416.43',
              },
              {
                id: 'admin-comp-qapi-qapi-program-docs',
                text: 'Does the QAPI program address patient safety and track quality indicators such as adverse events, infections, and outcomes?',
                appendixLRef: '416.43',
              },
              {
                id: 'admin-comp-qapi-adverse-event-analysis',
                text: 'Does the QAPI program include adverse event tracking and analysis?',
                appendixLRef: '416.43',
              },
              {
                id: 'admin-comp-qapi-corrective-action-evidence',
                text: 'Is there documented evidence of corrective action taken on issues identified through the QAPI program?',
                appendixLRef: '416.43',
              },
              {
                id: 'admin-comp-qapi-governing-body-review',
                text: 'Has the governing body reviewed QAPI reports, as reflected in meeting minutes or signed acknowledgments?',
                appendixLRef: '416.43',
              },
            ],
          },
          {
            label: 'Pathology results tracking and follow-up',
            narrative:
              'Per §416.42(a) and §416.43, the facility must have a process to ensure pathology results are received, reviewed, and acted upon for every specimen sent. Request the facility\'s pathology results tracking policy and verify: (1) there is a reconciliation process — the facility matches specimens sent against results received, and follows up on any missing results, (2) there is a defined turnaround time expectation and a process for escalating overdue results, (3) results are reviewed by the surgeon and documented in the patient\'s medical record, (4) for critical or unexpected findings (e.g., unexpected malignancy on what was clinically expected to be benign), there is a defined notification process — who contacts the patient, within what timeframe, and how is the notification documented?, (5) discrepancies between the clinical diagnosis and pathological diagnosis are tracked and reviewed as part of QAPI, (6) lost or mislabeled specimen events are tracked as adverse events. Ask: "How do you know that every specimen you send gets a result back? What happens if a pathology report shows unexpected findings — who calls the patient and how quickly? Show me an example of a closed-loop result."',
            cmsTag: '§416.42(a), §416.43',
            tjcRef: 'PI.01.01.01, PC.03.01.01',
            aaahcRef: 'Ch. 4 — Quality Management and Improvement',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'admin-comp-pathology-pathology-results-tracking',
                text: 'Are pathology results tracked and documented in the patient\'s medical record within 24 hours of receipt?',
                appendixLRef: '416.42(a)',
              },
              {
                id: 'admin-comp-pathology-follow-up-procedures',
                text: 'Is there a written policy and procedure in place for follow-up on abnormal pathology results within 48 hours?',
                appendixLRef: '416.42(a)',
              },
              {
                id: 'admin-comp-pathology-reconciliation',
                text: 'Does the facility have a reconciliation process that matches specimens sent against results received, with follow-up on any missing results?',
                appendixLRef: '416.42(a)',
              },
              {
                id: 'admin-comp-pathology-critical-findings-notification',
                text: 'Is there a defined notification process for critical or unexpected pathology findings, specifying who contacts the patient, within what timeframe, and how the notification is documented?',
                appendixLRef: '416.42(a)',
              },
              {
                id: 'admin-comp-pathology-qapi-discrepancy-tracking',
                text: 'Are clinical-vs-pathologic diagnosis discrepancies and lost or mislabeled specimen events tracked and reviewed as part of QAPI?',
                appendixLRef: '416.42(a)',
              },
            ],
          },
          {
            label: 'Patient transfer agreement or hospital privileges',
            narrative:
              'Per CMS §416.41(b), the ASC must have an effective procedure for the immediate transfer of patients requiring emergency medical care beyond what the ASC can provide. Verify: (1) there is a written transfer agreement with a local hospital, OR (2) all physicians performing surgery at the ASC have admitting privileges at a local hospital. Ask to see the transfer agreement and verify it is current (signed, dated, and not expired). If using hospital privileges instead, ask to see documentation of admitting privileges for each surgeon.',
            cmsTag: '§416.41(b)',
            tjcRef: 'LD.04.01.07',
            aaahcRef: 'Ch. 9 — Transfer Agreement',
            evidence: ['document'],
            checklistPrompts: [
              {
                id: 'admin-comp-transfer-agreement-or-privileges',
                text: 'Is there a current, signed, and dated transfer agreement with a local hospital on file, OR documentation that every physician performing surgery holds admitting privileges at a local hospital?',
                appendixLRef: '416.41(b)',
              },
              {
                id: 'admin-comp-transfer-emergency-procedure',
                text: 'Does the ASC have a written procedure for the immediate transfer of patients requiring emergency medical care beyond what the ASC can provide?',
                appendixLRef: '416.41(b)',
              },
            ],
          },
          {
            label: 'Formulary — specialty-specific, annually adopted',
            narrative:
              'Request the facility\'s drug formulary. Per §416.48, the ASC must maintain a formulary that is appropriate to the surgical specialties performed at the facility. Verify: (1) the formulary is limited to medications actually used at this facility — an orthopedic ASC should not carry the same formulary as a GI or ophthalmology center, (2) the formulary was reviewed and adopted by the medical staff or pharmacy & therapeutics committee, (3) the adoption date is within the past 12 months, (4) the governing body has approved the current formulary. Ask: "Who reviews and approves your formulary? When was it last updated? How do you add or remove a medication from the formulary?" Request meeting minutes showing the annual formulary review.',
            cmsTag: '§416.48, §416.45',
            tjcRef: 'MM.02.01.01',
            aaahcRef: 'Ch. 9.III — Pharmaceutical Services',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'admin-comp-formulary-formulary-specialty-specific',
                text: 'Is the formulary specialty-specific and adopted annually?',
                appendixLRef: '416.48',
              },
              {
                id: 'admin-comp-formulary-formulary-annual-adoption',
                text: 'Are all specialty-specific formulary updates completed and documented within the required annual timeframe?',
                appendixLRef: '416.48',
              },
              {
                id: 'admin-comp-formulary-medical-staff-adoption',
                text: 'Was the formulary reviewed and adopted by the medical staff or pharmacy & therapeutics committee, with meeting minutes on file showing the annual review?',
                appendixLRef: '416.48',
              },
              {
                id: 'admin-comp-formulary-governing-body-approval',
                text: 'Has the governing body approved the current formulary within the past 12 months?',
                appendixLRef: '416.48',
              },
            ],
          },
        ],
      },
      {
        domain: 'HR',
        domainLabel: 'Human Resources / Credentialing',
        items: [
          {
            label: 'Physician credentialing files',
            narrative:
              'Request credentialing files for 2-3 physicians who perform procedures at the ASC. Per CMS §416.45, the medical staff must be accountable to the governing body. Verify each file includes: (1) current state medical license, (2) DEA registration, (3) board certification or eligibility documentation, (4) current privileges granted by the governing body, (5) evidence of malpractice insurance, (6) National Practitioner Data Bank (NPDB) query within 2 years, (7) OIG/SAM exclusion check. Note any gaps or expired documents.',
            cmsTag: '§416.45',
            tjcRef: 'MS.06.01.03',
            aaahcRef: 'Ch. 2 — Clinical Records and Health Information',
            evidence: ['document'],
            checklistPrompts: [
              {
                id: 'admin-hr-cred-governing-body-approval',
                text: 'Is the ASC\'s governing body approved of the medical staff bylaws, individual practitioner privileges, credentialing, and re-appraisal processes?',
                appendixLRef: '416.45',
              },
              {
                id: 'admin-hr-cred-medical-staff-credentials',
                text: 'Are all physician credentialing files current and maintained in the medical staff\'s official records?',
                appendixLRef: '416.45',
              },
              {
                id: 'admin-hr-cred-license-dea-board',
                text: 'Does each credentialing file contain a current state medical license, current DEA registration, and board certification (or board-eligibility documentation)?',
                appendixLRef: '416.45',
              },
              {
                id: 'admin-hr-cred-privileges-malpractice',
                text: 'Does each file document current privileges granted by the governing body and evidence of active malpractice insurance coverage?',
                appendixLRef: '416.45',
              },
              {
                id: 'admin-hr-cred-npdb-oig-checks',
                text: 'Is a National Practitioner Data Bank (NPDB) query completed within the past 2 years on file, along with an OIG/SAM exclusion check?',
                appendixLRef: '416.45',
              },
            ],
          },
          {
            label: 'Nursing and staff personnel files',
            narrative:
              'Request 2-3 personnel files for nursing staff. Verify: (1) current state nursing license (RN/LPN), (2) BLS/ACLS certification current, (3) orientation documentation, (4) annual competency evaluations, (5) health screening records (TB test, Hepatitis B vaccination or declination, fit testing if applicable). Per CMS §416.46, the ASC must have adequate nursing staff. Note: CRNAs must have current state CRNA license and national certification.',
            cmsTag: '§416.46',
            tjcRef: 'HR.01.02.01',
            aaahcRef: 'Ch. 6 — Administration',
            evidence: ['document'],
            checklistPrompts: [
              {
                id: 'admin-hr-personnel-nursing-staff-availability',
                text: 'Is a registered nurse immediately available during all patient care hours?',
                appendixLRef: '416.46',
              },
              {
                id: 'admin-hr-personnel-nursing-policies-approval',
                text: 'Are the nursing policies approved by the facility\'s governing body?',
                appendixLRef: '416.46',
              },
              {
                id: 'admin-hr-personnel-license-bls-acls',
                text: 'Does each nursing personnel file contain a current state nursing license (RN/LPN) and current BLS/ACLS certification appropriate to the role?',
                appendixLRef: '416.46',
              },
              {
                id: 'admin-hr-personnel-orientation-competency',
                text: 'Are orientation documentation and annual competency evaluations on file for each nursing staff member?',
                appendixLRef: '416.46',
              },
              {
                id: 'admin-hr-personnel-health-screening',
                text: 'Are health-screening records — TB testing and respiratory fit-testing where applicable — on file for each staff member? (Hepatitis B vaccination records are verified under the Bloodborne Exposure Control Plan item.)',
                appendixLRef: '416.46',
              },
            ],
          },
          {
            label: 'Ongoing Professional Practice Evaluation (OPPE/FPPE)',
            narrative:
              'Ask about the process for evaluating physician performance. The facility must have a process for both focused evaluation (for new privileges or concerns) and ongoing evaluation of practitioner performance per §416.45. Ask: "How do you monitor physician performance? Do you track complications, returns to OR, or unplanned transfers by physician?" Review any tracking reports if available.',
            cmsTag: '§416.45',
            tjcRef: 'MS.08.01.01, MS.08.01.03',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'admin-hr-oppe-governing-body-approval',
                text: 'Is practitioner-specific performance data — including complications, returns to OR, and unplanned transfers — tracked and reviewed at credentialing intervals as part of the OPPE process?',
                appendixLRef: '416.45',
              },
              {
                id: 'admin-hr-oppe-medical-staff-approval',
                text: 'Is a focused performance evaluation (FPPE) initiated when a practitioner competency concern is identified or new privileges are granted?',
                appendixLRef: '416.45',
              },
            ],
          },
        ],
      },
      {
        domain: 'EM',
        domainLabel: 'Emergency Management',
        items: [
          {
            label: 'Emergency Management Plan',
            narrative:
              'Request the facility\'s emergency transfer policies and procedures. Per CMS §416.41(b), the ASC must have an effective procedure for immediate hospital transfer when a patient needs emergency medical care beyond ASC capabilities. Verify the policy addresses: (1) circumstances requiring transfer, (2) who makes the transfer decision, (3) documentation that accompanies the patient, (4) safe and expeditious transfer steps, and (5) communication with the receiving hospital. Ask: "Walk me through what happens if a patient requires emergency hospital transfer right now."',
            cmsTag: '§416.41(b)',
            tjcRef: 'EM.02.01.01',
            aaahcRef: 'Ch. 8.IV — Emergency Management',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'admin-em-plan-governing-body-responsibility',
                text: 'Does the ASC have a written procedure for immediate hospital transfer when a patient requires emergency medical care beyond the ASC\'s capabilities?',
                appendixLRef: '416.41(b)',
              },
              {
                id: 'admin-em-plan-effective-governing-body',
                text: 'Do the emergency transfer policies identify who makes the transfer decision, what documentation accompanies the patient, how transfer is accomplished safely and expeditiously, and how the receiving hospital is contacted?',
                appendixLRef: '416.41(b)',
              },
            ],
          },
          {
            label: 'Emergency drill records',
            narrative:
              'Interview staff and review transfer readiness records. Per §416.41(b), staff must be aware of and able to implement the ASC\'s emergency transfer policy immediately when a medical emergency develops. Verify staff know the transfer protocol, how to arrange emergency transport, and where local hospital contact/notice information is maintained. Ask: "How do you arrange emergency transport, and what information goes with the patient?"',
            cmsTag: '§416.41(b)',
            tjcRef: 'EM.03.01.03',
            aaahcRef: 'Ch. 8.IV.D — Drills',
            evidence: ['document'],
            checklistPrompts: [
              {
                id: 'admin-em-drills-emergency-drill-documentation',
                text: 'Can staff describe how to implement the ASC emergency transfer policy immediately when a medical emergency develops?',
                appendixLRef: '416.41(b)',
              },
              {
                id: 'admin-em-drills-emergency-drill-corrective-actions',
                text: 'Can staff explain how to arrange emergency transport and communicate with the receiving hospital during an emergency transfer?',
                appendixLRef: '416.41(b)',
              },
            ],
          },
        ],
      },
      {
        domain: 'IC',
        domainLabel: 'Infection Control — Exposure Prevention',
        items: [
          {
            label: 'Bloodborne pathogen exposure control plan',
            narrative:
              'Per §416.51(a) and OSHA 29 CFR 1910.1030, the facility must have a written Exposure Control Plan (ECP) for bloodborne pathogens. Request the plan and verify: (1) it is written, current, and reviewed at least annually, (2) it identifies job classifications with occupational exposure risk, (3) it describes the post-exposure protocol — what happens if a staff member sustains a needlestick or blood/body fluid exposure? Who do they report to? Is there a source patient testing protocol?, (4) the OSHA Sharps Injury Log (Form 300A) is maintained — review it for completeness and any trends, (5) Hepatitis B vaccination is offered to all at-risk employees — check personnel files for vaccination records or signed declination forms, (6) staff training on bloodborne pathogen prevention is documented annually. Ask: "Walk me through what happens if a nurse gets a needlestick in the OR right now. Who do they call? Where do they go?"',
            cmsTag: '§416.51(a)',
            tjcRef: 'IC.02.01.01',
            aaahcRef: 'Ch. 10 — Infection Prevention',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'exposure-control-plan-current',
                text: 'Is the written bloodborne pathogen Exposure Control Plan current and reviewed within the past 12 months, with job classifications having occupational exposure risk identified?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'exposure-control-post-exposure-protocol',
                text: 'Does the plan describe the post-exposure protocol — reporting chain, source-patient testing, and follow-up care — for staff who sustain a needlestick or blood/body-fluid exposure?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'exposure-control-sharps-injury-log',
                text: 'Is the OSHA Sharps Injury Log (Form 300A) maintained and reviewed for completeness and injury trends?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'exposure-control-hepb-vaccination',
                text: 'Is Hepatitis B vaccination offered to all at-risk employees, with vaccination records or signed declination forms on file?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'exposure-control-annual-training',
                text: 'Is annual staff training on bloodborne pathogen prevention documented for all employees with occupational exposure risk?',
                appendixLRef: '416.51(b)',
              },
            ],
          },
        ],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PRE-OP
  // ═══════════════════════════════════════════════════════════════════════════
  {
    area: 'Pre-Op',
    areaLabel: 'Pre-Operative Area',
    introduction:
      'The pre-op area is where patients are prepared for surgery. Focus on patient assessment, consent documentation, infection control practices, medication management, and privacy. Talk to nursing staff here — ask them to walk you through a typical patient admission.',
    walkOrder:
      'Enter the pre-op bay area. Observe the patient preparation process if cases are running. Check hand hygiene stations, privacy curtains, documentation flow, medication carts, and emergency equipment. Look at the pre-op assessment forms being used.',
    requiredPhotos: [...FOUR_WALL_BASELINE, ...MED_STORAGE_PHOTOS],
    estimatedMinutes: 20,
    sections: [
      {
        domain: 'IC',
        domainLabel: 'Infection Control',
        items: [
          {
            label: 'Hand hygiene stations and compliance',
            narrative:
              'Look for hand hygiene access points: alcohol-based hand rub dispensers should be at every bed/bay entry, and sinks with soap should be nearby. Per CMS §416.51(a), the ASC must maintain an infection control program. Observe staff — are they performing hand hygiene before and after patient contact? Check that dispensers are not empty. Photograph the locations. Ask a nurse: "Walk me through your hand hygiene protocol." Note any lapses you observe.',
            cmsTag: '§416.51(a)',
            tjcRef: 'IC.02.01.01',
            aaahcRef: 'Ch. 10 — Infection Prevention and Control',
            evidence: ['photo', 'observation', 'interview'],
            checklistPrompts: [
              {
                id: 'hand-hygiene-accessible-preop',
                text: 'Are soap-and-water or alcohol-based hand hygiene supplies readily accessible in pre-op patient care areas?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'hand-hygiene-observed-preop',
                text: 'Do staff perform hand hygiene before and after direct patient contact?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
          {
            label: 'PPE availability and donning',
            narrative:
              'Check that personal protective equipment is readily accessible: gloves in multiple sizes at each bay, gowns, masks, and eye protection. If staff are performing IV starts or blood draws, observe for proper glove use. Look for a PPE station or cart. Check that sharps containers are present at each bay and not overfilled (should not be filled past the fill line).',
            cmsTag: '§416.51(a)',
            tjcRef: 'IC.02.02.01',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'ppe-available-preop',
                text: 'Is task-appropriate PPE readily available where pre-op patient care occurs?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'gloves-used-and-removed-preop',
                text: 'Do staff wear gloves for blood or body-fluid contact and remove them before moving to the next task or patient?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
          {
            label: 'Standard precautions and pre-procedure SSI preparation',
            narrative:
              'Observe staff performing patient care tasks in the pre-op area and verify they are using the correct personal protective equipment for the task at hand per CDC Standard Precautions guidance. Gloves for any anticipated contact with blood or body fluids, gown if splashing is possible, mask and eye protection when appropriate. Staff should don PPE before entering the patient space and remove it before leaving. Verify pre-procedure SSI prevention preparation, including patient bathing instructions and hair-removal practices when hair removal is clinically necessary. Watch for common lapses: wearing gloves in the hallway between patients, not changing gloves between tasks on the same patient, touching clean surfaces with contaminated gloves, or removing hair in the OR. Ask a nurse: "What PPE do you use for an IV start versus a blood draw? How are patients instructed for pre-procedure bathing and hair removal?"',
            cmsTag: '416.51(a)',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'standard-precautions-competency-preop',
                text: 'Can staff describe and demonstrate standard precautions for anticipated blood or body-fluid exposure?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'preop-chg-bathing',
                text: 'Are patients instructed to bathe or shower with soap or an antiseptic agent before the procedure?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'hair-removal-clippers',
                text: 'If hair removal is required, is it performed with clippers or a depilatory agent outside the OR?',
                appendixLRef: '416.51(b)',
              },
            ],
          },
          {
            label: 'Injection safety: one needle, one syringe, one patient',
            narrative:
              'Observe the medication preparation area in pre-op per CDC Safe Injection Practices guidelines. Verify: (1) a new sterile syringe and needle are used for each injection and each patient — no reuse, (2) medication vials accessed with a new needle each time — look for needles left in vial stoppers (this is a finding), (3) syringes are not used to access shared medication containers for multiple patients. Watch an IV start or medication preparation if possible. Ask: "How do you ensure one-needle, one-syringe, one-patient in this area?"',
            cmsTag: '416.51(a)',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'needles-one-patient-preop',
                text: 'Are needles used for only one patient?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'syringes-one-patient-preop',
                text: 'Are syringes used for only one patient?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'vials-new-needle-syringe-preop',
                text: 'Are medication vials entered with a new needle and new syringe each time?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
          {
            label: 'Single-dose vials: one-patient-only labeling',
            narrative:
              'Inspect medication vials in the pre-op preparation area per CDC injection safety guidance. Single-dose vials (SDVs) must be used for one patient only and discarded immediately after use — they must not be stored for later use or combined for use on another patient. Check: (1) SDVs are labeled clearly as "single dose" or "single use," (2) no opened SDVs are being saved or stored in bins for reuse, (3) staff understand the distinction between single-dose and multi-dose vials. Ask a nurse: "Can you show me how you handle a single-dose vial? What happens to the leftover medication?"',
            cmsTag: '416.51(a)',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'single-dose-vials-one-patient-preop',
                text: 'Are single-dose or single-use medication vials used for only one patient?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'single-dose-vials-clearly-labeled',
                text: 'Are single-dose vials clearly labeled as "single dose" or "single use" for staff identification?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'single-dose-vials-not-saved-or-stored',
                text: 'Opened single-dose vials are never saved or stored in bins for reuse on another patient.',
                appendixLRef: '416.51(a)',
              },
            ],
          },
          {
            label: 'Multi-dose vials: opening date, storage, and discard date',
            narrative:
              'Check multi-dose vials in the pre-op area per CDC guidance and manufacturer instructions. Verify: (1) every multi-dose vial has an "opened" or "first puncture" date written on the label, (2) vials requiring refrigeration are stored at the correct temperature (2-8°C), (3) vials are discarded by the manufacturer-specified timeframe after opening — typically 28 days unless the manufacturer states otherwise, (4) vials are not being used beyond their expiration date even if the 28-day window has not elapsed. Pull 3-5 multi-dose vials and check each one. Photograph any vials missing an opening date or past their discard window.',
            cmsTag: '416.51(a)',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'multi-dose-vials-dated-discarded-preop',
                text: 'Are multi-dose vials dated when opened and discarded within the required beyond-use period?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'multi-dose-vials-stored-outside-patient-care-preop',
                text: 'Are shared multi-dose vials stored outside immediate patient care areas?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'multi-dose-vials-refrigeration-temp',
                text: 'Are multi-dose vials requiring refrigeration stored at the correct temperature (2–8°C / 36–46°F)?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'multi-dose-vials-honor-shorter-expiry',
                text: 'Are multi-dose vials discarded by the manufacturer expiration date even if the 28-day post-opening window has not elapsed?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
        ],
      },
      {
        domain: 'ADA' as SurveyDomain,
        domainLabel: 'ADA Accessibility',
        items: [
          {
            label: 'Adjustable exam surfaces for mobility-impaired patients',
            narrative:
              'Per ADA requirements and the 2010 ADA Standards for Accessible Design, the pre-operative area should have at least one adjustable-height exam surface (stretcher or bed) that can lower to 17-19 inches from the floor to allow patients with mobility impairments to transfer independently or with minimal assistance. Check: (1) at least one bay has a stretcher or exam table that adjusts to accessible height, (2) transfer aids (sliding boards, grab bars on stretcher) are available if needed, (3) staff are trained on assisting patients with mobility devices. Ask: "How do you accommodate a patient who uses a wheelchair for transfer to the pre-op stretcher?"',
            cmsTag: '28CFR36.502',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'preop-ada-surfaces-adjustable-exam-surfaces',
                text: 'Is there at least one adjustable exam surface available for use by mobility-impaired patients?',
                appendixLRef: '28CFR36.502',
              },
              {
                id: 'preop-ada-surfaces-exam-surface-height',
                text: 'Does the adjustable exam surface have a height range that can be modified to accommodate a mobility-impaired patient\'s needs?',
                appendixLRef: '28CFR36.502',
              },
            ],
          },
          {
            label: 'Accessible patient call system',
            narrative:
              'Verify that the patient call system in the pre-operative bays is accessible to patients with limited hand dexterity or mobility per ADA requirements. Check: (1) call buttons or cords are within reach of a patient lying on the stretcher, (2) the control requires minimal force and does not require tight grasping or pinching to activate, (3) patients are oriented to the call device on arrival. Ask: "How does a patient with limited hand strength call for help from the pre-op bay?"',
            cmsTag: '28CFR36.502',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'preop-ada-call-patient-call-system-accessible',
                text: 'Is the pre-op bay call button or cord within reach of a patient lying on the stretcher, with activation requiring no tight grasping or pinching?',
                appendixLRef: '28CFR36.502',
              },
              {
                id: 'preop-ada-call-path-of-travel-clearance',
                text: 'Are patients oriented to the pre-op call device on arrival so they know how to summon staff assistance from the bay?',
                appendixLRef: '28CFR36.502',
              },
            ],
          },
          {
            label: 'Large-print consent forms and discharge instructions',
            narrative:
              'Per ADA effective communication requirements, the facility must make key documents available in accessible formats. Check: (1) large-print versions (minimum 18-point font) of consent forms are available on request, (2) large-print discharge instructions are available, (3) staff know how to access or produce these formats when a visually impaired patient presents. Ask the pre-op nurse: "If a patient with low vision needs to read and sign their consent form, what do you do? Do you have large-print versions available?"',
            cmsTag: '28CFR36.503',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'preop-ada-print-large-print-consent-forms',
                text: 'Are large-print consent forms available in the facility for patients with visual impairments?',
                appendixLRef: '28CFR36.503',
              },
              {
                id: 'preop-ada-print-discharge-instructions-accessibility',
                text: 'Are discharge instructions provided in large print for patients with visual impairments?',
                appendixLRef: '28CFR36.503',
              },
            ],
          },
        ],
      },
      {
        domain: 'CR',
        domainLabel: 'Clinical Records / Patient Assessment',
        items: [
          {
            label: 'Pre-operative assessment completeness',
            narrative:
              'Ask to review 2-3 patient charts (with patient identifiers covered). Per CMS §416.52(a), a physician must examine the patient immediately before surgery to evaluate the risk of the procedure and anesthesia. Verify each chart includes: (1) H&P within 30 days with update within 24 hours, (2) pre-anesthesia assessment by a qualified practitioner, (3) nursing assessment including allergies, current medications, NPO status, (4) pregnancy screening per facility policy, (5) vital signs baseline. Note any missing elements.',
            cmsTag: '§416.52(a)',
            tjcRef: 'PC.01.02.03',
            aaahcRef: 'Ch. 7 — Clinical Records',
            evidence: ['document', 'observation'],
          
    checklistPrompts: [
      {
        id: 'preop-cr-assess-1-complete',
        text: 'Is there a policy in place that requires a pre-operative assessment to be completed by a qualified individual for each patient admitted for surgery?',
        appendixLRef: '416.52(a)',
      },
      {
        id: 'preop-cr-assess-2-elements',
        text: 'Are all pre-operative assessments for patients admitted for surgery completed by a qualified individual and documented in the patient\'s medical record?',
        appendixLRef: '416.52(a)',
      },
      {
        id: 'preop-cr-assess-hp-current',
        text: 'If an H&P is required by ASC policy, is it placed in the patient\'s medical record before the surgical procedure?',
        appendixLRef: '416.52(a)',
      },
      {
        id: 'preop-cr-assess-anesthesia-nursing',
        text: 'Does the pre-surgical assessment document pre-existing medical conditions, appropriate test results, and allergies or lack of known allergies to drugs and biologicals?',
        appendixLRef: '416.52(a)',
      },
      {
        id: 'preop-cr-assess-pregnancy-vitals',
        text: 'Does the medical record document that the patient was examined before the start of surgery?',
        appendixLRef: '416.52(a)',
      },
    ],
},
          {
            label: 'Informed consent verification',
            narrative:
              'Review consent forms in the charts. Per CMS §416.50(e), the patient must be fully informed about the treatment or procedure and expected outcome before it is performed, and informed consent must be signed and placed in the medical record before surgery. Verify: (1) the correct procedure is named (matches the schedule), (2) the performing surgeon is identified, (3) risks, benefits, and alternatives are listed, (4) the patient (or legal representative) has signed and dated, (5) a witness signature is present, (6) the consent was obtained before sedation. Check for separate anesthesia consent. Ask: "Who obtains consent here — the surgeon or a nurse? When is it obtained?"',
            cmsTag: '§416.50(e), §416.47(b)',
            tjcRef: 'RI.01.03.01',
            aaahcRef: 'Ch. 5.I.D — Informed Consent',
            evidence: ['document', 'interview'],
          
    checklistPrompts: [
      {
        id: 'preop-cr-consent-1-written',
        text: 'Is the patient or representative fully informed about the treatment or procedure and expected outcome before the procedure is performed?',
        appendixLRef: '416.50(e)',
      },
      {
        id: 'preop-cr-consent-2-documented',
        text: 'Is the signed informed consent form placed in the patient\'s medical record before surgery?',
        appendixLRef: '416.50(e)',
      },
      {
        id: 'preop-cr-consent-procedure-surgeon-match',
        text: 'Does the consent form name the correct procedure (matching the schedule) and identify the performing surgeon?',
        appendixLRef: '416.50(e)',
      },
      {
        id: 'preop-cr-consent-risks-benefits-alternatives',
        text: 'Are risks, benefits, and alternatives to the procedure listed on the consent form?',
        appendixLRef: '416.50(e)',
      },
      {
        id: 'preop-cr-consent-signed-dated-witnessed',
        text: 'Is the consent signed and dated by the patient (or legal representative) with a witness signature present?',
        appendixLRef: '416.50(e)',
      },
      {
        id: 'preop-cr-consent-before-sedation',
        text: 'Was informed consent obtained before any sedation was administered, and is a separate anesthesia consent on file?',
        appendixLRef: '416.50(e)',
      },
    ],
},
          {
            label: 'Site marking and time-out process',
            narrative:
              'If a laterality or multi-level procedure is scheduled, verify site marking and time-out safeguards. Ask staff: "Walk me through your Universal Protocol / time-out process. Who participates? When does the mark go on? What happens if there is a discrepancy?" Per §416.42, surveyors observe whether standard procedures are used to avoid wrong-site, wrong-procedure, and wrong-patient errors, and §416.42(a) requires a physician exam to evaluate procedural risk before surgery.',
            cmsTag: '§416.42, §416.42(a)',
            tjcRef: 'UP.01.01.01, UP.01.02.01, UP.01.03.01',
            aaahcRef: 'Ch. 9.II — Surgical and Procedural Safety',
            evidence: ['observation', 'interview'],
          
    checklistPrompts: [
      {
        id: 'cr-site-marking-and-time-out-process-physician-exam',
        text: 'Are standard procedures used before the procedure to avoid wrong-site, wrong-procedure, and wrong-patient surgical errors?',
        appendixLRef: '416.42',
      },
      {
        id: 'cr-site-marking-and-time-out-process-exam-timing',
        text: 'Did a physician examine the patient before surgery to evaluate the risk of the procedure to be performed?',
        appendixLRef: '416.42(a)',
      },
    ],
},
        ],
      },
      {
        domain: 'MED',
        domainLabel: 'Medication Management',
        items: [
          {
            label: 'Medication reconciliation on admission',
            narrative:
              'Check that a medication reconciliation is performed on admission. Per §416.48, the ASC must obtain and document a complete list of the patient\'s current medications and compare it with medications ordered. Look at the charts — is there a medication reconciliation form? Is it complete? Ask: "How do you verify the patient\'s home medications before anesthesia?"',
            cmsTag: '§416.48',
            tjcRef: 'NPSG.03.06.01',
            aaahcRef: 'Ch. 9 — Surgical and Related Services',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'med-reconcil-medication-reconciliation-form',
                text: 'Is a medication reconciliation form present in each chart documenting a complete list of the patient\'s current medications on admission?',
                appendixLRef: '416.48',
              },
              {
                id: 'med-reconcil-medication-reconciliation-complete',
                text: 'Is the reconciliation complete — home medications compared against medications ordered, with discrepancies resolved before anesthesia?',
                appendixLRef: '416.48',
              },
              {
                id: 'med-reconcil-home-med-verification',
                text: 'Does the facility have a documented process for verifying the patient\'s home medications (patient interview, pharmacy contact, or family confirmation) before anesthesia is administered?',
                appendixLRef: '416.48',
              },
            ],
          },
          {
            label: 'Pre-op medication storage and labeling',
            narrative:
              'Check the medication storage area in pre-op. Look for: (1) medications stored in a locked cabinet or automated dispensing machine, (2) no expired medications — pull a few from the shelf and check expiration dates, (3) look-alike/sound-alike medications separated or flagged, (4) multi-dose vials dated when opened (must be discarded per manufacturer guidelines, typically 28 days), (5) no patient-specific medications left unattended on counters. If you find any expired medications, photograph the label with the expiration date visible.',
            cmsTag: '§416.48(a)',
            tjcRef: 'MM.03.01.01',
            aaahcRef: 'Ch. 9.III — Pharmaceutical Services',
            evidence: ['photo', 'observation'],
          
    checklistPrompts: [
      {
        id: 'med-pre-op-medication-storage-and-labeli-1-are-drugs-and-biologicals-stored-in-secure-locations',
        text: 'Are drugs and biologicals stored in secure locations to prevent unauthorized access?',
        appendixLRef: '416.48(a)',
      },
      {
        id: 'med-pre-op-medication-storage-and-labeli-2-are-all-drugs-and-biologicals-properly-labeled-with-their-name-and-expiration-date',
        text: 'Are all drugs and biologicals properly labeled with their name and expiration date?',
        appendixLRef: '416.48(a)',
      },
      {
        id: 'med-pre-op-medication-storage-no-expired',
        text: 'Are all medications within expiration date, with no expired items present on the shelves?',
        appendixLRef: '416.48(a)',
      },
      {
        id: 'med-pre-op-medication-storage-lasa-separated',
        text: 'Are look-alike / sound-alike (LASA) medications separated or flagged, and are patient-specific medications never left unattended on counters?',
        appendixLRef: '416.48(a)',
      },
    ],
},
        ],
      },
      {
        domain: 'ENV',
        domainLabel: 'Environment of Care',
        items: [
          {
            label: 'Privacy and dignity',
            narrative:
              'Observe the physical layout. Are privacy curtains adequate — do they fully close? Can conversations be overheard from the hallway or waiting area? Are patient belongings secured? Per CMS §416.50(a)(1), patients have the right to privacy. Check that monitors displaying patient information are not visible to unauthorized persons. Note if patient names are posted on whiteboards visible to other patients or visitors.',
            cmsTag: '§416.50(a)(1)',
            tjcRef: 'RI.01.01.01',
            evidence: ['observation'],
            checklistPrompts: [
              {
                id: 'preop-env-privacy-dignity-privacy-curtains',
                text: 'Are the privacy curtains in the patient rooms fully closed and do they provide adequate coverage to ensure patient privacy?',
                appendixLRef: '416.50(a)(1)',
              },
              {
                id: 'preop-env-privacy-dignity-monitor-visibility',
                text: 'Are patient monitors displaying personal health information positioned so that only authorized personnel can view the data?',
                appendixLRef: '416.50(a)(1)',
              },
            ],
          },
          {
            label: 'Emergency equipment — crash cart',
            narrative:
              'Locate the crash cart or emergency supply cart in or near pre-op. Verify: (1) it is easily accessible and not blocked, (2) the seal/lock is intact (if lock is broken, the cart may have been opened and not restocked), (3) check logs — is the cart checked daily per policy? Look at the log sheet: are there gaps in daily checks? (4) AED/defibrillator is present and has a current battery indicator, (5) suction is available and functioning. Photograph the daily check log, especially noting any gaps.',
            cmsTag: '§416.42(b)',
            tjcRef: 'EC.02.04.01',
            aaahcRef: 'Ch. 9.I — Anesthesia Services',
            evidence: ['photo', 'document'],
            checklistPrompts: [
              {
                id: 'preop-env-emergency-equipment-crash-crash-cart-accessibility',
                text: 'Is the crash cart or emergency supply cart located in or near pre-op and easily accessible without being blocked?',
                appendixLRef: '416.42(b)',
              },
              {
                id: 'preop-env-emergency-equipment-crash-crash-cart-daily-checks',
                text: 'Is the crash cart daily check log complete with no gaps, showing all required inspections have been performed?',
                appendixLRef: '416.42(b)',
              },
            ],
          },
        ],
      },
      {
        domain: 'ENV',
        domainLabel: 'Environmental Controls — Pre-Op',
        items: [
          {
            label: 'Ventilation and temperature',
            narrative:
              'The pre-operative area requires a minimum of 6 air changes per hour per §416.44(a)(1) and ASHRAE 170. Temperature should be maintained at 70-75°F with humidity between 20-60%. The area should be at neutral or positive pressure relative to corridors. Perform a tissue test at the door to check airflow direction. Check: (1) is there a temperature log or digital monitor?, (2) is the area comfortable for patients in gowns?, (3) are warming blankets available? Ask: "How do you monitor temperature and ventilation in pre-op?"',
            cmsTag: '§416.44(a)(1), ASHRAE 170',
            tjcRef: 'EC.02.05.01',
            aaahcRef: 'Ch. 8.II — HVAC and Ventilation',
            evidence: ['observation', 'interview'],
          
    checklistPrompts: [
      {
        id: 'preop-env-vent-temperature-humidity-records',
        text: 'Are records maintained showing Pre-Op temperature (70-75°F), humidity (20-60%), and airflow (6 ACH) within ASHRAE/CMS requirements?',
        appendixLRef: '416.44(a)(1)',
      },
      {
        id: 'preop-env-vent-humidity-range',
        text: 'Is the relative humidity in the Pre-Op area maintained between 20% and 60%?',
        appendixLRef: '416.44(a)(1)',
      },
    ],
},
        ],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // OPERATING ROOMS
  // ═══════════════════════════════════════════════════════════════════════════
  {
    area: 'OR 1',
    areaLabel: 'Operating Room(s)',
    introduction:
      'The OR is where the highest infection control, life safety, and clinical standards converge. If a case is in progress, observe from the doorway without disrupting the sterile field. If rooms are between cases, you can enter and inspect closely. Repeat this checklist for each OR you inspect.',
    walkOrder:
      'Start outside the OR doors — look at traffic patterns and restricted-access signage. Enter an unoccupied OR. Check from ceiling to floor: lights, vents, walls, equipment, sterile supplies, flooring. Then check the anesthesia workstation. Finally, observe turnover cleaning if possible.',
    requiredPhotos: [
      ...FOUR_WALL_BASELINE,
      ...MED_STORAGE_PHOTOS,
      { label: 'Anesthesia workstation', description: 'Photograph the anesthesia cart and workstation. Include medication vials with readable dates. Show the MH kit location.', angle: 'overview', minCount: 2 },
      { label: 'Sterile supply storage', description: 'Photograph sterile supply shelving showing package integrity, storage height off floor, and clearance below sprinkler heads.', angle: 'overview', minCount: 1 },
    ],
    isRepeatable: true,
    repeatableRooms: ['OR 1', 'OR 2', 'OR 3', 'OR 4'],
    estimatedMinutes: 25,
    sections: [
      {
        domain: 'IC',
        domainLabel: 'Infection Control',
        items: [
          {
            label: 'Surgical attire and traffic control',
            narrative:
              'Observe OR attire compliance. Per CMS §416.51 and AORN guidelines: (1) all persons in the semi-restricted and restricted areas should wear surgical scrubs, (2) hair must be completely covered by a cap, (3) masks are required in restricted areas where sterile supplies are open, (4) shoe covers if required by facility policy. Check for a clear delineation between unrestricted, semi-restricted, and restricted zones. Are doors kept closed during procedures? Ask: "What is your policy on OR traffic during a case?"',
            cmsTag: '§416.51(a)',
            tjcRef: 'IC.02.02.01',
            aaahcRef: 'Ch. 10.II — Infection Prevention',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'or-attire-clean',
                text: 'Do personnel wear clean surgical attire before entering the OR or invasive procedure room?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'or-attire-traffic-controlled',
                text: 'Are surgical attire and traffic controls followed in restricted and semi-restricted OR areas?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'or-attire-hair-covered',
                text: 'Is hair completely covered by a cap for all persons in semi-restricted and restricted areas?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'or-attire-masks-in-restricted',
                text: 'Are masks worn in restricted areas whenever sterile supplies are open?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'or-attire-doors-closed-during-cases',
                text: 'Are OR doors kept closed during procedures with clear delineation between unrestricted, semi-restricted, and restricted zones?',
                appendixLRef: '416.51(b)',
              },
            ],
          },
          {
            label: 'Sterile supply storage and integrity',
            narrative:
              'Inspect sterile supply storage in the OR. Look for: (1) peel-pouches and wrapped trays should have intact seals — look for any tears, moisture damage, or discoloration on sterilization indicators, (2) supplies should be stored off the floor (at least 8-10 inches), away from walls, and below sprinkler heads (18 inches), (3) check that external chemical indicators (tape or integrated indicators) have changed color appropriately, (4) look for event-related sterility — no arbitrary expiration dating unless the integrity is compromised. Ask: "How do you determine if a sterile package is still sterile?"',
            cmsTag: '§416.51(b)',
            tjcRef: 'IC.02.02.01',
            aaahcRef: 'Ch. 10.III — Sterilization',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'or-sterile-packages-intact',
                text: 'Are sterile packages intact and stored so sterility is not compromised before use?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'or-chemical-indicators-present',
                text: 'Are chemical indicators present and showing appropriate processing for sterile items?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'or-sterile-storage-clearance',
                text: 'Are sterile supplies stored at least 8–10 inches off the floor, away from walls, and at least 18 inches below sprinkler heads?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'or-sterile-event-related-sterility',
                text: 'Does the facility apply event-related sterility (no arbitrary expiration dating) unless package integrity is compromised?',
                appendixLRef: '416.51(b)',
              },
            ],
          },
          {
            label: 'Surgical site infection prevention',
            narrative:
              'Ask about SSI prevention practices. Key items: (1) skin prep — what agent is used, is it allowed to dry per manufacturer directions?, (2) surgical hand antisepsis — observe if possible, (3) prophylactic antibiotics — are they administered within 1 hour before incision (2 hours for vancomycin)?, (4) normothermia — is patient temperature monitored and warming devices used?, (5) hair removal — facility should use clippers, not razors. Ask: "What is your antibiotic prophylaxis protocol? How do you ensure timely administration?"',
            cmsTag: '§416.51(a)',
            tjcRef: 'IC.02.02.01, NPSG.07.05.01',
            aaahcRef: 'Ch. 10.II — Surgical Site Infection Prevention',
            evidence: ['interview', 'observation'],
            checklistPrompts: [
              {
                id: 'ssi-prevention-skin-prep-dry',
                text: 'Is the skin-prep agent applied and allowed to dry per manufacturer directions before draping?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'ssi-prevention-hand-antisepsis',
                text: 'Is surgical hand antisepsis performed by the operative team per facility protocol?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'ssi-prevention-prophylactic-antibiotics',
                text: 'Are prophylactic antibiotics administered within 60 minutes before incision (120 minutes for vancomycin or fluoroquinolones)?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'ssi-prevention-normothermia-warming',
                text: 'Is patient temperature monitored and are warming devices used to maintain perioperative normothermia?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'ssi-prevention-hair-removal-clippers',
                text: 'Does the facility use clippers (not razors) when preoperative hair removal is clinically indicated?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'surgical-safety-checklist-used',
                text: 'Is a surgical safety checklist completed before induction and incision?',
                appendixLRef: '416.51(b)',
              },
            ],
          },
          {
            label: 'Antibiotic prophylaxis timing verification',
            narrative:
              'Per CDC Surgical Site Infection Prevention guidelines, prophylactic antibiotics must be administered within 60 minutes before surgical incision (120 minutes for vancomycin or fluoroquinolones) to achieve adequate tissue levels at the time of incision. Review 2-3 patient charts and verify: (1) the antibiotic administration time is documented, (2) incision time is documented, (3) the interval between administration and incision falls within the required window. If antibiotics were given too early or too late, this represents a deviation from evidence-based practice. Ask the anesthesia provider: "How do you coordinate antibiotic timing with the surgical team?"',
            cmsTag: '416.51(a)',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'antibiotic-prophylaxis-timing-verified',
                text: 'Does the chart-review sample confirm the antibiotic administration time, incision time, and interval documented for each applicable case?',
                appendixLRef: '416.51(b)',
              },
            ],
          },
          {
            label: 'Skin antisepsis protocol',
            narrative:
              'Per CDC SSI prevention recommendations, chlorhexidine-alcohol is the preferred skin antiseptic agent for surgical site preparation (unless contraindicated). Verify: (1) the facility uses an alcohol-based chlorhexidine prep as the default agent, (2) staff allow adequate dry time before draping — alcohol-based preps must be completely dry to prevent surgical fire risk, (3) dry time is documented or verbally confirmed before draping, (4) alternative agents (povidone-iodine) are available for patients with chlorhexidine sensitivity. Observe a prep if a case is in progress, or ask: "What skin prep agent do you use? How do you confirm it is dry before draping?"',
            cmsTag: '416.51(a)',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'skin-antisepsis-dry-time-followed',
                text: 'Is chlorhexidine-alcohol used as the default surgical skin antiseptic agent, with an alternative (e.g., povidone-iodine) available for patients with chlorhexidine sensitivity?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'skin-antisepsis-dry-before-drape',
                text: 'Is the skin-prep agent allowed to dry completely before draping to prevent surgical fire risk?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'skin-antisepsis-dry-time-documented',
                text: 'Is the dry time verbally confirmed or documented before draping begins?',
                appendixLRef: '416.51(b)',
              },
            ],
          },
          {
            label: 'Normothermia maintenance',
            narrative:
              'Per CDC SSI prevention guidelines, maintaining normothermia (core body temperature ≥35.5°C/96°F) during surgery reduces surgical site infection risk. Verify: (1) a forced-air warming device (e.g., Bair Hugger) is available in each OR, (2) patient temperature is monitored intraoperatively — check for temperature probes and documentation in the anesthesia record, (3) warming blankets are applied before induction when possible. Ask: "How do you monitor patient temperature during cases? What is your target temperature?"',
            cmsTag: '416.51(a)',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'normothermia-maintained',
                text: 'Does the anesthesia record document intraoperative temperature readings and use of warming interventions?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'normothermia-warming-blankets-available',
                text: 'Are forced-air warming blankets (e.g., Bair Hugger) available in each OR and applied before induction when possible?',
                appendixLRef: '416.51(b)',
              },
            ],
          },
          {
            label: 'Glycemic control protocol for diabetic patients',
            narrative:
              'Per CDC SSI prevention recommendations, perioperative blood glucose control in diabetic patients reduces infection risk. Verify: (1) the facility has a glycemic control protocol or policy for surgical patients with diabetes, (2) a blood glucose target range is documented (typically <200 mg/dL perioperatively per SCIP/CDC guidance), (3) for diabetic patients, blood glucose is checked preoperatively and intraoperatively as indicated, (4) the protocol includes instructions for insulin administration if glucose exceeds the target. Review a diabetic patient chart if available, or ask: "What is your protocol for managing blood glucose in diabetic surgical patients?"',
            cmsTag: '416.51(a)',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'glycemic-control-protocol-available',
                text: 'Is a perioperative glycemic-control protocol available and used for diabetic surgical patients?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'glycemic-target-range-documented',
                text: 'Is a perioperative blood-glucose target range documented (typically <200 mg/dL per SCIP/CDC guidance)?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'glycemic-preop-intraop-checks',
                text: 'Are diabetic patients\' blood glucose levels checked preoperatively and intraoperatively per protocol?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'glycemic-insulin-instructions',
                text: 'Does the protocol include instructions for insulin administration when glucose exceeds the target?',
                appendixLRef: '416.51(b)',
              },
            ],
          },
          {
            label: 'Terminal cleaning checklist',
            narrative:
              'Per CDC environmental infection control guidelines, terminal cleaning of the OR between patients and at the end of the day is critical for SSI prevention. Verify: (1) a written terminal cleaning checklist exists and is used — it should specify between-patient steps (all horizontal surfaces, surgical light heads, equipment, anesthesia workstation, floor) and a more thorough end-of-day deep clean, (2) the checklist is posted or readily available in the OR, (3) staff initial or sign the checklist after completing cleaning tasks, (4) observe a room turnover if possible — are all surfaces addressed? Ask: "Can you show me your terminal cleaning checklist? How do you verify cleaning was completed correctly?"',
            cmsTag: '416.51(a)',
            evidence: ['document', 'observation'],
            checklistPrompts: [
              {
                id: 'or-terminal-cleaning-daily',
                text: 'Are operating rooms terminally cleaned daily?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'or-terminal-cleaning-written-checklist',
                text: 'Is a written terminal cleaning checklist in use that specifies both between-patient steps and a more thorough end-of-day deep clean?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'or-terminal-cleaning-checklist-accessible',
                text: 'Is the terminal cleaning checklist posted or readily available in the OR during turnovers?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'or-terminal-cleaning-signed-initialed',
                text: 'Do staff initial or sign the terminal cleaning checklist after completing the required cleaning tasks?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
          {
            label: 'High-touch surface disinfection list and frequency',
            narrative:
              'Per CDC guidelines for environmental cleaning in healthcare, high-touch surfaces require more frequent disinfection because they are most likely to harbor and transmit pathogens. Check: (1) the facility has identified high-touch surfaces in the OR — door handles, light switches, IV poles, bed rails, monitor touchscreens, anesthesia machine surfaces, OR table controls, (2) a disinfection frequency is posted or documented (between every patient at minimum), (3) the specific disinfectant and required contact time are identified for these surfaces. Ask: "What surfaces do you consider high-touch in the OR? How often are they wiped down during the day?"',
            cmsTag: '416.51(a)',
            evidence: ['document', 'observation'],
            checklistPrompts: [
              {
                id: 'or-high-touch-surfaces-disinfected',
                text: 'Are high-touch environmental surfaces in patient care areas cleaned and disinfected on a regular basis and when contaminated?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'or-high-touch-surfaces-identified',
                text: 'Has the facility identified the OR high-touch surfaces (door handles, light switches, IV poles, bed rails, monitor touchscreens, anesthesia machine surfaces, OR table controls)?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'or-high-touch-frequency-disinfectant',
                text: 'Is the disinfection frequency posted (between every patient at minimum) along with the specific disinfectant and required contact time?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
          {
            label: 'EPA-registered disinfectant in OR',
            narrative:
              'Verify that the disinfectant used for OR cleaning is EPA-registered and used according to label directions per EPA Federal Insecticide, Fungicide, and Rodenticide Act (FIFRA) requirements. Check: (1) the product container displays a visible EPA registration number (format: EPA Reg. No. XXXXX-XX), (2) the product is being used at the correct dilution — check the label or SDS, (3) the required contact time (wet dwell time) is being observed — staff must not wipe the surface dry before the contact time elapses, (4) the product is approved for healthcare/hospital use. Photograph the EPA registration number on the container and observe whether staff allow appropriate contact time during room turnover.',
            cmsTag: '416.51(a)',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'or-epa-disinfectant-used',
                text: 'Is an EPA-registered disinfectant used for OR cleaning and disinfection after procedures?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'or-epa-reg-number-visible',
                text: 'Does the disinfectant container display a visible EPA registration number (format EPA Reg. No. XXXXX-XX) and is the product approved for healthcare/hospital use?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'or-epa-dilution-correct',
                text: 'Is the disinfectant used at the correct dilution per label or SDS instructions?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'or-epa-contact-time-observed',
                text: 'Is the required contact (wet dwell) time observed, with staff not wiping surfaces dry before the contact time elapses?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
        ],
      },
      {
        domain: 'COMP',
        domainLabel: 'Patient Safety — Universal Protocol',
        items: [
          {
            label: 'Pre-induction verification — patient participates',
            narrative:
              'This step occurs BEFORE anesthesia is administered, while the patient is still alert and able to participate. Per §416.42(a), the circulating nurse initiates the pre-induction verification with the patient. Observe: (1) the circulator greets the patient and asks them to state their full name and date of birth — the patient must verbally identify themselves (staff should not state it for them), (2) the circulator asks the patient to state in their own words what procedure they are here for today, (3) the circulator reviews and confirms: the procedure, the surgical site and laterality ("which knee are we operating on today?"), allergies, NPO status, and the consent form, (4) the site marking is verified with the patient — "I see the mark on your right knee, does that match your understanding?", (5) any discrepancy between what the patient states and the chart must stop the process. Note: this is NOT the surgical time-out — this is the patient\'s last opportunity to catch a wrong-site or wrong-procedure error while they are conscious. If the patient appears confused or their answers do not match the chart, it must be resolved before induction.',
            cmsTag: '§416.42(a)',
            tjcRef: 'UP.01.01.01',
            aaahcRef: 'Ch. 9.I — Surgical Services',
            evidence: ['observation'],
            checklistPrompts: [
              {
                id: 'or-comp-pre-induction-patient-identity',
                text: 'Is the circulating nurse asking the patient to state their full name and date of birth, without the staff stating it for them?',
                appendixLRef: '416.42(a)',
              },
              {
                id: 'or-comp-pre-induction-site-confirmation',
                text: "Does the circulating nurse verify the surgical site marking with the patient by asking, 'I see the mark on your right knee, does that match your understanding?'?",
                appendixLRef: '416.42(a)',
              },
              {
                id: 'or-comp-pre-induction-patient-states-procedure',
                text: 'Is the patient asked to state in their own words which procedure they are having today (rather than staff stating it for them)?',
                appendixLRef: '416.42(a)',
              },
              {
                id: 'or-comp-pre-induction-chart-reconciliation',
                text: 'Does the circulator review and confirm the procedure, surgical site/laterality, allergies, NPO status, and consent form against the chart with the patient?',
                appendixLRef: '416.42(a)',
              },
              {
                id: 'or-comp-pre-induction-stop-on-discrepancy',
                text: 'Is any discrepancy between the patient\'s statements and the chart resolved before induction begins?',
                appendixLRef: '416.42(a)',
              },
            ],
          },
          {
            label: 'Surgical time-out — immediately before incision',
            narrative:
              'The surgical time-out occurs AFTER induction but immediately BEFORE the first incision. Per §416.42(a), the entire team must pause. Observe: (1) all team members stop what they are doing and actively participate — surgeon, anesthesia provider, circulating nurse, scrub tech, (2) patient identity is confirmed using two identifiers, (3) correct procedure is stated aloud, (4) correct surgical site is confirmed and matches the site marking, (5) relevant safety concerns are verbalized — allergies, antibiotic administration and timing (was it given within the required window?), implants/special equipment needed, anticipated blood loss, VTE prophylaxis, (6) the time-out is documented in the medical record. Note: if any team member is not paying attention, multitasking, or the time-out is rushed through as a formality, this is a finding. Ask the circulator: "What happens if someone identifies a discrepancy during the time-out? Has that ever happened here?"',
            cmsTag: '§416.42(a)',
            tjcRef: 'UP.01.01.01, UP.01.02.01, UP.01.03.01',
            aaahcRef: 'Ch. 9.I — Surgical Services',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'or-comp-timeout-surgical-time-out-procedure',
                text: 'Is a surgical time-out conducted immediately before the first incision for all surgical procedures?',
                appendixLRef: '416.42(a)',
              },
              {
                id: 'or-comp-timeout-surgical-time-out-documentation',
                text: 'Are the results of the surgical time-out documented in the patient\'s medical record?',
                appendixLRef: '416.42(a)',
              },
              {
                id: 'or-comp-timeout-team-participation',
                text: 'Do all team members (surgeon, anesthesia provider, circulating nurse, scrub tech) stop activity and actively participate in the time-out?',
                appendixLRef: '416.42(a)',
              },
              {
                id: 'or-comp-timeout-patient-id-two-identifiers',
                text: 'Is patient identity confirmed aloud during the time-out using two identifiers?',
                appendixLRef: '416.42(a)',
              },
              {
                id: 'or-comp-timeout-procedure-and-site-match',
                text: 'Are the correct procedure and correct surgical site stated aloud and confirmed to match the site marking?',
                appendixLRef: '416.42(a)',
              },
              {
                id: 'or-comp-timeout-safety-concerns-verbalized',
                text: 'Are relevant safety concerns verbalized during the time-out — allergies, antibiotic administration window, implants/special equipment, anticipated blood loss, and VTE prophylaxis?',
                appendixLRef: '416.42(a)',
              },
            ],
          },
          {
            label: 'Surgical count process',
            narrative:
              'Observe or ask about the surgical count process. Per §416.42(a), the facility must have procedures to prevent retained surgical items. Verify: (1) initial count is performed by the circulator and scrub tech together before the procedure begins, (2) counts include sponges, sharps, instruments, and any other items that could be retained, (3) a closing count is performed before wound closure, (4) if there is a count discrepancy, a standardized process is followed (typically an X-ray before the patient leaves the OR), (5) counts are documented in the operative record. Ask: "What happens if your count is off?"',
            cmsTag: '§416.42(a)',
            tjcRef: 'PC.03.01.01',
            aaahcRef: 'Ch. 9.I — Surgical Services',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'or-comp-surgical-count-process-surgical-count-process',
                text: 'Is the initial surgical count performed by both the circulator and scrub tech before the procedure begins?',
                appendixLRef: '416.42(a)',
              },
              {
                id: 'or-comp-surgical-count-process-surgical-count-discrepancy',
                text: 'Is a standardized process, such as an X-ray, followed if there is a discrepancy in the surgical count?',
                appendixLRef: '416.42(a)',
              },
              {
                id: 'or-comp-surgical-count-items-covered',
                text: 'Do surgical counts include sponges, sharps, instruments, and any other items that could be retained in the patient?',
                appendixLRef: '416.42(a)',
              },
              {
                id: 'or-comp-surgical-count-closing-count',
                text: 'Is a closing count performed before wound closure?',
                appendixLRef: '416.42(a)',
              },
              {
                id: 'or-comp-surgical-count-documented',
                text: 'Are surgical counts and any discrepancies documented in the operative record?',
                appendixLRef: '416.42(a)',
              },
            ],
          },
          {
            label: 'Specimen labeling and handling',
            narrative:
              'If a surgical specimen is obtained during the procedure, observe or verify the specimen handling process. Per §416.42(a), specimens must be properly identified and handled. Check: (1) the specimen container is labeled with two patient identifiers (name and DOB) at the point of collection — the circulator and surgeon should verbally confirm the specimen identity, (2) the specimen is placed in the correct fixative or transport medium with proper formalin-to-tissue ratio per the receiving lab\'s requirements, (3) a pathology requisition accompanies the specimen with: patient identifiers, date, procedure, specimen source/site, laterality if applicable, surgeon name, (4) there is a specimen log that tracks chain of custody — time collected, who collected it, time picked up by courier or transported, who transported it, (5) the facility has a written policy on which specimens go to pathology vs. which are discarded, and patient consent is obtained for tissue disposal when specimens are not sent, (6) ask to see the specimen pickup schedule — who is the courier? What happens if the courier doesn\'t show? Is there a backup plan? Ask: "What happens if a specimen is mislabeled or lost? Has that ever happened here? How do you confirm the lab actually received the specimen?"',
            cmsTag: '§416.42(a)',
            tjcRef: 'NPSG.01.01.01, PC.03.01.01',
            aaahcRef: 'Ch. 9.I — Surgical Services',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'or-comp-specimen-labeling-verification',
                text: 'Is the specimen container labeled with two patient identifiers (name and DOB) at the point of collection?',
                appendixLRef: '416.42(a)',
              },
              {
                id: 'or-comp-specimen-handling-policy',
                text: 'Does the facility have a written policy specifying which specimens are sent to pathology and which are discarded?',
                appendixLRef: '416.42(a)',
              },
              {
                id: 'or-comp-specimen-fixative-requisition',
                text: 'Is each specimen placed in the correct fixative or transport medium, and does a pathology requisition accompany it with patient identifiers, date, procedure, specimen source/site, laterality, and surgeon name?',
                appendixLRef: '416.42(a)',
              },
              {
                id: 'or-comp-specimen-chain-of-custody-log',
                text: 'Is there a specimen chain-of-custody log tracking time collected, collector, time picked up or transported, and transporter?',
                appendixLRef: '416.42(a)',
              },
              {
                id: 'or-comp-specimen-pickup-backup-plan',
                text: 'Does the specimen pickup schedule identify the courier, and is there a documented backup plan if the courier does not arrive?',
                appendixLRef: '416.42(a)',
              },
            ],
          },
          {
            label: 'Implant tracking and documentation',
            narrative:
              'If the procedure involves an implantable device (joint hardware, mesh, IOL, pacemaker, etc.), verify the implant tracking process. Per §416.42(a), the facility must maintain records sufficient to identify each implant. Check: (1) the implant sticker or label with lot number, manufacturer, catalog number, and expiration date is placed in the patient\'s medical record, (2) the facility maintains an implant log or registry that allows tracking by patient or by lot number for recall purposes, (3) the implant was verified during the time-out (correct implant, correct size, not expired), (4) sterilization of implant instrument trays — biological indicator results must be obtained BEFORE the implant is released for use (implant loads are held until BI is negative). Ask: "How do you track implants for recalls? Show me where the implant sticker goes in the chart. What is your process if a manufacturer issues a recall?"',
            cmsTag: '§416.42(a)',
            tjcRef: 'PC.03.01.01',
            aaahcRef: 'Ch. 9.I — Surgical Services',
            evidence: ['document', 'observation'],
            checklistPrompts: [
              {
                id: 'or-comp-implant-tracking-documentation-implant-tracking-recall',
                text: 'Is there a system in place to track implants by lot number for recall purposes?',
                appendixLRef: '416.42(a)',
              },
              {
                id: 'or-comp-implant-tracking-documentation-implant-sticker-medical-record',
                text: "Is the implant sticker or label with lot number, manufacturer, catalog number, and expiration date placed in the patient's medical record?",
                appendixLRef: '416.42(a)',
              },
              {
                id: 'or-comp-implant-time-out-verified',
                text: 'Is each implant verified during the time-out for correct device, correct size, and that it is not expired?',
                appendixLRef: '416.42(a)',
              },
              {
                id: 'or-comp-implant-bi-held-until-negative',
                text: 'Are sterilized implant instrument trays held until the biological indicator (BI) result is confirmed negative before the implant is released for use?',
                appendixLRef: '416.42(a)',
              },
            ],
          },
        ],
      },
      {
        domain: 'LSC',
        domainLabel: 'Life Safety Code',
        items: [
          {
            label: 'OR fire safety — ESU, oxygen, and fire risk',
            narrative:
              'The OR has the highest fire risk in the ASC due to the fire triangle: ignition source (electrosurgical unit/ESU, laser), fuel (drapes, prep solution vapors, patient hair), and oxidizer (supplemental oxygen, nitrous oxide). Check: (1) is there a fire risk assessment performed for each case?, (2) are staff trained in the OR fire protocol?, (3) is there a fire extinguisher inside or immediately outside each OR?, (4) is the ESU holster present and used (not just laid on the drape)? Ask: "Walk me through what happens if there is an airway fire during a case."',
            cmsTag: '§416.44(b)',
            tjcRef: 'EC.02.02.01',
            aaahcRef: 'Ch. 8.III — Fire Safety',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'or-lsc-fire-safety-esu-fire-risk-assessment',
                text: 'Is a fire risk assessment performed for each surgical case in the OR?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'or-lsc-fire-safety-esu-esu-holster-use',
                text: 'Is the ESU holster present and used in the OR, rather than just being laid on the drape?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'or-lsc-fire-safety-staff-training-protocol',
                text: 'Have OR staff been trained on the facility\'s OR fire protocol, including airway-fire response?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'or-lsc-fire-safety-extinguisher-access',
                text: 'Is a fire extinguisher present inside or immediately outside each OR and readily accessible?',
                appendixLRef: '416.44(b)',
              },
            ],
          },
          {
            label: 'Electrical safety and equipment inspection',
            narrative:
              'Check electrical equipment in the OR. Look for: (1) biomedical engineering (BME) inspection stickers on all plugged-in medical equipment — stickers should show a current inspection date (typically annual), (2) no unapproved extension cords or power strips in patient care areas (hospital-grade power strips with built-in circuit breaker are acceptable), (3) check that wall outlets are hospital-grade (green dot), (4) no frayed cords or damaged plugs. Photograph any equipment with missing or expired BME stickers.',
            cmsTag: '§416.44(a)(2)',
            tjcRef: 'EC.02.04.01, LS.02.01.20',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'or-lsc-electrical-safety-equipment-bme-inspection-stickers',
                text: 'Are all plugged-in medical equipment in the OR labeled with current BME inspection stickers showing a valid inspection date?',
                appendixLRef: '416.44(a)(2)',
              },
              {
                id: 'or-lsc-electrical-safety-equipment-extension-cords-patient-care',
                text: 'Are all extension cords and power strips in patient care areas hospital-grade with built-in circuit breakers, with no unapproved devices present?',
                appendixLRef: '416.44(a)(2)',
              },
              {
                id: 'or-lsc-electrical-safety-hospital-grade-outlets',
                text: 'Are the wall outlets in the OR hospital-grade (green-dot) receptacles?',
                appendixLRef: '416.44(a)(2)',
              },
              {
                id: 'or-lsc-electrical-safety-cord-plug-integrity',
                text: 'Are all power cords and plugs in the OR free of fraying, damage, or makeshift repairs?',
                appendixLRef: '416.44(a)(2)',
              },
            ],
          },
          {
            label: 'Wall, ceiling, and floor integrity',
            narrative:
              'Inspect the OR surfaces closely. Look for: (1) any holes, cracks, or gaps in walls — especially around pipes, conduit, or where equipment is mounted (these compromise the fire barrier and infection control), (2) damaged or missing ceiling tiles, (3) floor integrity — no cracks, chips, or unsealed seams where moisture and bacteria can accumulate, (4) evidence of water damage or staining on ceiling tiles, (5) any tape repairs on walls (these are not acceptable — damage must be properly repaired). Photograph any deficiencies.',
            cmsTag: '§416.44(a)(1), §416.44(b)',
            tjcRef: 'EC.02.06.01, LS.02.01.10',
            evidence: ['photo'],
            checklistPrompts: [
              {
                id: 'or-lsc-wall-ceiling-floor-wall-cracks-holes',
                text: 'Are the OR walls free of holes, cracks, and gaps around pipes, conduit, and equipment mounting points?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'or-lsc-wall-ceiling-floor-floor-seams-integrity',
                text: 'Is the OR floor free of cracks, chips, and unsealed seams where moisture and bacteria could accumulate?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'or-lsc-ceiling-tiles-intact',
                text: 'Are ceiling tiles present, intact, and free of water damage or staining?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'or-lsc-no-tape-repairs',
                text: 'Are OR walls free of tape repairs (damage properly repaired rather than taped over)?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'or-lsc-equipment-mount-seal',
                text: 'Are equipment mounting points and penetrations around pipes or conduit sealed so the fire barrier and infection-control envelope are intact?',
                appendixLRef: '416.44(a)(1)',
              },
            ],
          },
        ],
      },
      {
        domain: 'MED',
        domainLabel: 'Medication Management / Anesthesia',
        items: [
          {
            label: 'Anesthesia workstation and medications',
            narrative:
              'Inspect the anesthesia cart and workstation. Per §416.42(a), anesthesia must be administered by a qualified practitioner. Check: (1) medications on the anesthesia cart — are any expired? Pull several vials and check dates, (2) are drawn-up syringes labeled (drug name, concentration, date/time prepared, expiration)? Syringes not immediately administered must be labeled per §416.48(a), (3) is the Malignant Hyperthermia (MH) cart or kit accessible? Ask: "Where is your MH kit? What is the dose of Dantrolene you carry?", (4) controlled substance log — is it current and reconciled?',
            cmsTag: '§416.42(a), §416.48',
            tjcRef: 'MM.05.01.07, NPSG.03.04.01',
            aaahcRef: 'Ch. 9.I — Anesthesia Services',
            evidence: ['photo', 'document', 'interview'],
          
    checklistPrompts: [
      {
        id: 'med-anesthesia-anesthesia-cart-meds',
        text: 'Are anesthesia cart medications and syringes labeled with name/date-time prepared, is the cart locked when unattended, and is MH treatment access immediately available?',
        appendixLRef: '416.42(a)',
      },
      {
        id: 'med-anesthesia-mh-kit-accessible',
        text: 'Is the Malignant Hyperthermia (MH) cart or kit accessible, with Dantrolene dosing known to anesthesia staff?',
        appendixLRef: '416.42(a)',
      },
      {
        id: 'med-anesthesia-vials-not-expired',
        text: 'Are anesthesia cart medication vials checked for expiration with no expired items present?',
        appendixLRef: '416.42(a)',
      },
      {
        id: 'med-anesthesia-controlled-substance-log',
        text: 'Is the controlled substance log on the anesthesia cart current and reconciled?',
        appendixLRef: '416.42(a)',
      },
    ],
},
          {
            label: 'Medication labeling on the sterile field',
            narrative:
              'If a case is in progress or being set up, check whether all medications and solutions on the sterile field are labeled. Per §416.48(a), any medication or solution transferred from the original container to another (basins, syringes, cups) must be labeled with: drug name, strength, quantity, diluent/volume, preparation date/time, and expiration. The only exception is when a medication is immediately administered. Ask the scrub tech: "How do you label medications on your field?"',
            cmsTag: '§416.48(a)',
            tjcRef: 'NPSG.03.04.01',
            evidence: ['observation', 'interview'],
          
    checklistPrompts: [
      {
        id: 'med-medication-labeling-on-the-sterile-f-1-are-medications-properly-labeled-with-their-generic-name-and-strength',
        text: 'Are medications properly labeled with their generic name and strength on the sterile field?',
        appendixLRef: '416.48(a)',
      },
      {
        id: 'med-medication-labeling-on-the-sterile-f-2-are-there-security-and-accountability-measures-in-place-for-medications-on-the-sterile-field',
        text: 'Are there security and accountability measures in place for medications on the sterile field?',
        appendixLRef: '416.48(a)',
      },
    ],
},
        ],
      },
      {
        domain: 'ENV',
        domainLabel: 'Environment of Care',
        items: [
          {
            label: 'OR temperature, humidity, and air exchanges',
            narrative:
              'Check OR environmental controls. Per AORN and FGI guidelines: (1) OR temperature should be 68-75°F (20-24°C), (2) relative humidity should be 20-60% (varies by jurisdiction — check state requirements), (3) minimum 20 air exchanges per hour with at least 4 being outside air, (4) positive pressure relative to corridors. Ask: "Where do you monitor temperature and humidity? How often is it logged?" Review the log — look for out-of-range readings and whether corrective actions are documented. Photograph the log.',
            cmsTag: '§416.44(a)(1)',
            tjcRef: 'EC.02.06.01',
            aaahcRef: 'Ch. 8.II — Environmental Controls',
            evidence: ['photo', 'document'],
            checklistPrompts: [
              {
                id: 'or-env-temperature-humidity-air-temperature-humidity-monitoring',
                text: 'Are temperature and humidity monitored in the operating room using a device that provides continuous readings?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'or-env-temperature-humidity-air-environmental-controls-log',
                text: 'Is there a log documenting temperature and humidity readings in the operating room, with entries made at least every 30 minutes?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'or-env-temp-range-68-75',
                text: 'Is the OR temperature maintained at 68–75°F (20–24°C) per AORN/FGI guidelines?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'or-env-positive-pressure-corridor',
                text: 'Is the OR maintained at positive pressure relative to adjacent corridors and spaces?',
                appendixLRef: '416.44(a)(1)',
              },
            ],
          },
          {
            label: 'OR cleaning and turnover',
            narrative:
              'If you can observe room turnover between cases, note the cleaning process. Check: (1) are all horizontal surfaces wiped down?, (2) is the floor mopped?, (3) is the correct disinfectant being used at the correct dilution and contact time?, (4) are suction canisters changed?, (5) is soiled linen removed and replaced? Ask: "What disinfectant do you use for OR turnover? What is the contact time? How do you verify dilution?" Check the product label or SDS for contact time and verify staff allow it.',
            cmsTag: '§416.51(a)',
            tjcRef: 'IC.02.02.01',
            aaahcRef: 'Ch. 10.II — Environmental Cleaning',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'or-env-cleaning-turnover-disinfectant-contact-time',
                text: 'Is the required wet dwell time for the disinfectant allowed before wiping surfaces dry during OR turnover?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'or-env-cleaning-turnover-terminal-cleaning-checklist',
                text: 'Is a completed turnover checklist or equivalent documented log signed off after each case turnover?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'or-cleaning-horizontal-surfaces',
                text: 'Are all horizontal surfaces (OR table, equipment tops, anesthesia workstation) wiped with disinfectant during turnover?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'or-cleaning-floor-mopped',
                text: 'Is the OR floor mopped with EPA-registered disinfectant between cases?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'or-cleaning-suction-canisters-linen',
                text: 'Are suction canisters changed and soiled linen removed/replaced between cases?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
        ],
      },
      {
        domain: 'ENV',
        domainLabel: 'Environmental Controls — OR',
        items: [
          {
            label: 'Positive pressure verification',
            narrative:
              'The OR must maintain positive pressure relative to adjacent corridors and spaces per §416.44(a)(1) and FGI/ASHRAE guidelines. This prevents contaminated corridor air from entering the sterile surgical environment. Perform a tissue test: close the OR door and hold a tissue at the crack along the bottom — it should blow outward (away from the OR), confirming positive pressure inside. If there is a differential pressure monitor or ball-in-tube indicator at the door, check that it reads correctly. Ask: "How do you verify this OR is under positive pressure? How often is it checked?"',
            cmsTag: '§416.44(a)(1), ASHRAE 170',
            tjcRef: 'EC.02.05.01',
            aaahcRef: 'Ch. 8.II — HVAC and Ventilation',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'or-env-positive-pressure-verification-positive-pressure-verification',
                text: 'Is a tissue test performed by closing the OR door and holding a tissue at the crack along the bottom to confirm positive pressure?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'or-env-positive-pressure-verification-positive-pressure-monitoring',
                text: 'Is a differential pressure monitor or ball-in-tube indicator at the OR door checked to verify positive pressure?',
                appendixLRef: '416.44(a)(1)',
              },
            ],
          },
          {
            label: 'Air exchanges, temperature, and humidity',
            narrative:
              'The OR requires a minimum of 15 total air changes per hour (ACH) with at least 3 of those being fresh outside air per §416.44(a)(1) and ASHRAE 170. Temperature must be maintained at 68-75°F and humidity at 20-60%. Check: (1) is there a daily temperature and humidity log for this OR? Review the last 30 days for out-of-range readings and documented corrective actions, (2) if digital monitoring is used, verify it is functioning and alarmed, (3) ask to see the most recent HVAC air balancing report — it should document ACH rates for each OR. Ask: "What is the air exchange rate for this room? When was the last balancing report done?"',
            cmsTag: '§416.44(a)(1), ASHRAE 170',
            tjcRef: 'EC.02.05.01',
            aaahcRef: 'Ch. 8.II — HVAC and Ventilation',
            evidence: ['document', 'observation', 'interview'],
            checklistPrompts: [
              {
                id: 'or-env-air-exchanges-temperature-air-exchange-rate',
                text: 'Is the air exchange rate for this room documented in the most recent HVAC air balancing report?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'or-env-air-exchanges-daily-log-review',
                text: 'Is a daily OR temperature and humidity log on file, with 30-day review showing corrective action on any out-of-range readings?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'or-env-air-exchanges-digital-monitor-alarmed',
                text: 'If digital monitoring is used, is it functioning and alarmed for out-of-range temperature or humidity?',
                appendixLRef: '416.44(a)(1)',
              },
            ],
          },
          {
            label: 'ASHRAE 170 ACH verification: 15 air changes/hour in OR',
            narrative:
              'Per ASHRAE Standard 170 — Ventilation of Health Care Facilities, operating rooms require a minimum of 15 total air changes per hour (ACH). Request the most recent air balancing report and verify the documented ACH for this specific OR meets or exceeds 15. Check the date of the last balancing — it should be recent (within the last 1-2 years or after any HVAC modification). If the facility cannot produce a balancing report, this is a significant finding. Ask: "Can you show me the air balancing report for this OR? What date was it performed?"',
            cmsTag: 'ASHRAE170-2017',
            evidence: ['document'],
            checklistPrompts: [
              {
                id: 'ashrae-or-ach-balancing-report',
                text: 'Is the most recent air balancing report available and does it document ≥15 air changes per hour (ACH) for the OR?',
                appendixLRef: 'ASHRAE170-2017',
              },
              {
                id: 'ashrae-or-ach-report-current',
                text: 'Is the air balancing report current (within 1-2 years or after any HVAC modification)?',
                appendixLRef: 'ASHRAE170-2017',
              },
            ],
          },
          {
            label: 'Temperature 68-75°F, humidity 20-60% RH: daily log current',
            checklistPrompts: [
              {
                id: 'ashrae-or-temp-humidity-daily-log',
                text: 'Is the daily temperature and humidity log for the OR current and showing readings within 68-75°F and 20-60% RH range?',
                appendixLRef: 'ASHRAE170-2017',
              },
              {
                id: 'ashrae-or-temp-humidity-alarm-working',
                text: 'Is digital temperature/humidity monitoring functional with alarms set for out-of-range conditions?',
                appendixLRef: 'ASHRAE170-2017',
              },
            ],
            narrative:
              'Per ASHRAE Standard 170 and AORN guidelines, OR temperature must be maintained between 68-75°F (20-24°C) and relative humidity between 20-60%. Review the daily environmental monitoring log for this OR: (1) are readings recorded at least daily?, (2) are all readings within the acceptable range?, (3) for any out-of-range readings, is a corrective action documented?, (4) is the log current through today? Look for patterns — consistently high humidity may indicate HVAC issues, consistently high temperatures may affect surgical team performance and patient outcomes.',
            cmsTag: 'ASHRAE170-2017',
            evidence: ['document', 'photo'],
          },
        ],
      },
      {
        domain: 'ADA' as SurveyDomain,
        domainLabel: 'ADA Accessibility',
        items: [
          {
            label: 'Accessible stretcher/table for mobility-impaired patients',
            narrative:
              'Per ADA requirements, the facility must be able to accommodate patients with mobility impairments in the operating room. Verify: (1) the OR table or stretcher can be lowered to an accessible height (17-19 inches) for patient transfer, (2) transfer devices (sliding boards, draw sheets, mechanical lifts) are available for patients who cannot independently transfer, (3) staff are trained in safe transfer techniques for patients with disabilities. Ask: "How do you transfer a patient who uses a wheelchair to the OR table? Do you have a mechanical lift available?"',
            cmsTag: '28CFR36.502',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'or-ada-table-stretcher-accessibility',
                text: 'Is there at least one accessible stretcher or table available for use by mobility-impaired patients in the facility?',
                appendixLRef: '28CFR36.502',
              },
              {
                id: 'or-ada-table-stretcher-path-of-travel',
                text: 'Is there a clear, unobstructed path of at least 36 inches wide leading to the accessible stretcher or table from the nearest accessible entrance?',
                appendixLRef: '28CFR36.502',
              },
            ],
          },
        ],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PACU
  // ═══════════════════════════════════════════════════════════════════════════
  {
    area: 'PACU',
    areaLabel: 'Post-Anesthesia Care Unit (PACU / Recovery)',
    introduction:
      'PACU is critical for patient safety — this is where anesthesia complications manifest. Focus on monitoring capabilities, staffing ratios, discharge criteria, and emergency readiness. If patients are present, observe care being delivered.',
    walkOrder:
      'Enter the PACU. Count beds and note nurse-to-patient ratios. Check monitoring equipment at each bay. Locate the emergency equipment. Review discharge documentation. Check the discharge criteria tool being used.',
    requiredPhotos: [...FOUR_WALL_BASELINE, ...MED_STORAGE_PHOTOS],
    estimatedMinutes: 20,
    sections: [
      {
        domain: 'CR',
        domainLabel: 'Clinical Records / Discharge',
        items: [
          {
            label: 'Post-anesthesia assessment and monitoring',
            narrative:
              'Per CMS §416.52(b), the ASC must ensure each patient is properly assessed on entry to recovery. Verify: (1) vital signs are recorded at appropriate intervals (typically every 5-15 minutes), (2) a post-anesthesia assessment is performed by a qualified practitioner, (3) Aldrete scoring or equivalent discharge readiness tool is used, (4) pain is assessed and managed, (5) intake/output is monitored. Ask: "What assessment tool do you use for discharge readiness? What score is required for discharge?"',
            cmsTag: '§416.52(b)',
            tjcRef: 'PC.03.01.03',
            aaahcRef: 'Ch. 9.IV — Post-Operative Care',
            evidence: ['document', 'observation'],
          
    checklistPrompts: [
      {
        id: 'cr-post-anesthesia-assessment-policy',
        text: 'Does the ASC require that each patient receive a post-operative assessment prior to discharge from the ASC?',
        appendixLRef: '416.52(b)',
      },
      {
        id: 'cr-post-anesthesia-assessment-documented',
        text: 'Is there documentation in the patient\'s record that a post-operative assessment was conducted before the patient was discharged from the ASC?',
        appendixLRef: '416.52(b)',
      },
      {
        id: 'cr-post-anesthesia-vitals-intervals',
        text: 'Is the patient assessed for recovery from anesthesia after surgery when anesthesia was administered?',
        appendixLRef: '416.52(b)',
      },
      {
        id: 'cr-post-anesthesia-discharge-scoring-tool',
        text: 'Does the post-surgical assessment determine whether the patient is ready for discharge or needs further treatment or monitoring?',
        appendixLRef: '416.52(b)',
      },
      {
        id: 'cr-post-anesthesia-pain-io-monitored',
        text: 'Do the discharge notes document post-surgical needs identified and actions taken by the ASC to address those needs?',
        appendixLRef: '416.52(b)',
      },
    ],
},
          {
            label: 'OR-to-PACU handoff communication',
            narrative:
              'Observe the handoff from the OR team to the PACU nurse and review the transfer-of-care documentation. Per §416.52(b), post-surgical condition, recovery from anesthesia, identified needs, actions taken, and suitability for discharge or transfer must be assessed and documented. Verify the handoff supports continuity of care and that identified needs are addressed before discharge or transfer. Ask: "What information must be communicated before the anesthesia provider leaves?"',
            cmsTag: '§416.52(b)',
            tjcRef: 'PC.02.02.01',
            aaahcRef: 'Ch. 9.I — Surgical Services',
            evidence: ['observation', 'interview'],
          
    checklistPrompts: [
      {
        id: 'cr-or-to-pacu-handoff-communication-physician-exam',
        text: 'Does the PACU handoff or record document the patient\'s post-surgical condition, recovery-from-anesthesia status, identified needs, and actions needed for safe discharge or transfer?',
        appendixLRef: '416.52(b)',
      },
      {
        id: 'cr-or-to-pacu-handoff-communication-documentation',
        text: 'Are post-surgical needs identified during handoff addressed in the discharge notes or transfer documentation before the patient leaves the ASC?',
        appendixLRef: '416.52(b)',
      },
    ],
},
          {
            label: 'Discharge criteria and instructions',
            narrative:
              'Per CMS §416.52(c), the ASC must provide written discharge instructions. Review 2-3 discharge records. Verify: (1) standardized discharge criteria are met before release, (2) written instructions include: medications prescribed, activity restrictions, dietary guidance, wound care, when to call the surgeon, signs requiring emergency care, (3) a responsible adult is identified to accompany the patient home, (4) follow-up appointment documented, (5) a phone number for after-hours emergencies is provided. Ask a nurse: "What happens if a patient doesn\'t meet discharge criteria?"',
            cmsTag: '§416.52(c)',
            tjcRef: 'PC.04.01.05',
            aaahcRef: 'Ch. 9.V — Discharge Planning',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'pacu-cr-discharge-discharge-criteria-instructions',
                text: 'Are written discharge criteria and instructions provided to the patient upon discharge?',
                appendixLRef: '416.52(c)',
              },
              {
                id: 'pacu-cr-discharge-discharge-criteria-escort',
                text: 'Is an escort required for transportation as part of the discharge instructions?',
                appendixLRef: '416.52(c)',
              },
              {
                id: 'pacu-cr-discharge-written-instructions-contents',
                text: 'Do written discharge instructions include medications prescribed, activity restrictions, dietary guidance, wound care, and when to call the surgeon?',
                appendixLRef: '416.52(c)',
              },
              {
                id: 'pacu-cr-discharge-emergency-signs-contact',
                text: 'Do the instructions list signs or symptoms that require emergency care and provide an after-hours emergency phone number?',
                appendixLRef: '416.52(c)',
              },
              {
                id: 'pacu-cr-discharge-followup-appointment',
                text: 'Is a follow-up appointment documented in the discharge record before the patient leaves the facility?',
                appendixLRef: '416.52(c)',
              },
            ],
          },
        ],
      },
      {
        domain: 'ENV',
        domainLabel: 'Environment of Care',
        items: [
          {
            label: 'PACU monitoring equipment',
            narrative:
              'At each recovery bay, verify: (1) pulse oximetry is available and functioning, (2) cardiac monitor is accessible, (3) blood pressure monitoring is available, (4) oxygen delivery equipment (wall oxygen or portable tank with nasal cannula/mask), (5) suction is available and functioning at each bay. Check that all equipment has current biomedical inspection stickers. Note: at least one bay should have capnography capability.',
            cmsTag: '§416.42(b)',
            tjcRef: 'EC.02.04.01',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'pacu-env-pacu-monitoring-equipment-pulse-oximetry',
                text: 'Is pulse oximetry available and functioning at each recovery bay?',
                appendixLRef: '416.42(b)',
              },
              {
                id: 'pacu-env-pacu-monitoring-equipment-biomedical-inspection-stickers',
                text: 'Does all monitoring equipment in each recovery bay have current biomedical inspection stickers?',
                appendixLRef: '416.42(b)',
              },
              {
                id: 'pacu-env-monitoring-cardiac-bp',
                text: 'Are a cardiac monitor and blood-pressure monitoring accessible at each recovery bay?',
                appendixLRef: '416.42(b)',
              },
              {
                id: 'pacu-env-oxygen-and-suction',
                text: 'Are oxygen delivery (wall oxygen or portable tank with cannula/mask) and functioning suction available at each bay?',
                appendixLRef: '416.42(b)',
              },
              {
                id: 'pacu-env-capnography-available',
                text: 'Is capnography capability available in at least one PACU bay for patients requiring advanced monitoring?',
                appendixLRef: '416.42(b)',
              },
            ],
          },
          {
            label: 'Emergency equipment accessibility',
            narrative:
              'The crash cart and emergency supplies must be immediately accessible to PACU. Verify it is the same cart or a second cart — if the same one shared with pre-op, confirm it is positioned to serve both areas rapidly. Check that emergency airway equipment (laryngoscope, endotracheal tubes, supraglottic airways, bag-valve-mask) is available and functional. Reversal agents (naloxone, flumazenil) should be immediately available. Ask: "Where do you keep your reversal agents?"',
            cmsTag: '§416.42(b)',
            tjcRef: 'PC.03.01.07',
            aaahcRef: 'Ch. 9.I — Emergency Equipment',
            evidence: ['photo', 'interview'],
            checklistPrompts: [
              {
                id: 'pacu-env-emergency-equipment-accessibility-crash-cart-location',
                text: 'Is the crash cart positioned to serve both the PACU and pre-op areas rapidly if it is shared between them?',
                appendixLRef: '416.42(b)',
              },
              {
                id: 'pacu-env-emergency-equipment-accessibility-reversal-agents-access',
                text: 'Are reversal agents (naloxone, flumazenil) immediately available in the PACU?',
                appendixLRef: '416.42(b)',
              },
            ],
          },
        ],
      },
      {
        domain: 'IC',
        domainLabel: 'Infection Control',
        items: [
          {
            label: 'Hand hygiene and standard precautions in PACU',
            narrative:
              'Same standards as pre-op apply. Observe nursing staff: are they performing hand hygiene between patients? PACU can be high-turnover with nurses moving between bays quickly — lapses are common here. Check that hand hygiene supplies are at each bay. Verify sharps containers are present and not overfull. Check that soiled linen receptacles are available.',
            cmsTag: '§416.51(a)',
            tjcRef: 'IC.02.01.01, NPSG.07.01.01',
            evidence: ['observation'],
            checklistPrompts: [
              {
                id: 'hand-hygiene-observed-pacu',
                text: 'Do PACU staff perform hand hygiene before and after direct patient contact?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'pacu-sharps-soiled-linen-contained',
                text: 'Are sharps and soiled linen contained to maintain a sanitary PACU environment?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
          {
            label: 'Standard precautions in recovery',
            narrative:
              'Per CDC Standard Precautions guidelines, staff in the PACU must use appropriate PPE and perform hand hygiene between every patient contact. Observe recovery nurses as they move between bays: (1) are they performing hand hygiene (alcohol-based rub or handwashing) before and after touching each patient?, (2) are gloves changed between patients — not worn continuously from bay to bay?, (3) are gowns used when contact with blood or body fluids is anticipated? Common lapses in PACU include wearing the same gloves to check on multiple patients or skipping hand hygiene when adjusting IV pumps or monitors between bays.',
            cmsTag: '416.51(a)',
            evidence: ['observation'],
            checklistPrompts: [
              {
                id: 'standard-precautions-observed-pacu',
                text: 'Do PACU staff use hand hygiene, glove changes, and task-appropriate PPE between patient contacts?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'standard-precautions-hand-hygiene-touchpoints',
                text: 'Are PACU staff performing hand hygiene when adjusting IV pumps, monitors, or other touchpoints between bays?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'standard-precautions-gowns-when-indicated',
                text: 'Are gowns used in PACU when contact with blood or body fluids is anticipated?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
          {
            label: 'Terminal cleaning between patients in PACU',
            narrative:
              'Per CDC environmental infection control guidelines, PACU bays must be thoroughly cleaned and disinfected between patients. Verify: (1) bed rails, stretcher surfaces, and side rails are wiped with an EPA-registered disinfectant between patients, (2) IV poles, monitor cables, and touchscreens are included in the cleaning process, (3) the call button/cord is cleaned between patients, (4) the correct contact time for the disinfectant is observed. Observe a bay turnover if possible, or ask: "Walk me through your between-patient cleaning process in PACU. What surfaces do you clean?"',
            cmsTag: '416.51(a)',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'pacu-bays-disinfected-between-patients',
                text: 'Are PACU bays and patient-care surfaces cleaned and disinfected between patients?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'pacu-clean-bed-rails-stretcher',
                text: 'Are bed rails, stretcher surfaces, and side rails wiped with an EPA-registered disinfectant between patients?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'pacu-clean-poles-monitors-call',
                text: 'Are IV poles, monitor cables, touchscreens, and call buttons/cords included in the between-patient cleaning process?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'pacu-clean-contact-time-observed',
                text: 'Is the correct disinfectant contact (wet dwell) time observed during PACU bay turnovers?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
        ],
      },
      {
        domain: 'MED',
        domainLabel: 'Medication Management',
        items: [
          {
            label: 'PACU medication management',
            narrative:
              'Check medication management in PACU. Look for: (1) medications stored securely (locked cabinet or automated dispenser), (2) medications given in PACU are documented in the patient record with time, dose, route, and practitioner, (3) controlled substances are double-counted and reconciled at shift change, (4) no pre-drawn syringes left unattended without labels, (5) patient-controlled analgesia (PCA) — if used, verify proper setup and monitoring. Check for any expired medications in the PACU drug stock.',
            cmsTag: '§416.48',
            tjcRef: 'MM.05.01.01',
            evidence: ['photo', 'observation'],
          
    checklistPrompts: [
      {
        id: 'med-pacu-medication-management-secure-storage',
        text: 'Are PACU medications stored securely in a locked cabinet or automated dispenser?',
        appendixLRef: '416.48',
      },
      {
        id: 'med-pacu-medication-management-administration-documented',
        text: 'Are medications administered in PACU documented in the patient record with time, dose, route, and practitioner?',
        appendixLRef: '416.48',
      },
      {
        id: 'med-pacu-medication-management-controlled-substance-reconcile',
        text: 'Are controlled substances double-counted and reconciled at shift change, with any discrepancy investigated and documented?',
        appendixLRef: '416.48',
      },
      {
        id: 'med-pacu-medication-management-pre-drawn-labels',
        text: 'Are pre-drawn syringes labeled (drug, concentration, prepared date/time) and never left unattended unlabeled?',
        appendixLRef: '416.48',
      },
    ],
},
        ],
      },
      {
        domain: 'ENV',
        domainLabel: 'Environmental Controls — PACU',
        items: [
          {
            label: 'Ventilation and temperature',
            narrative:
              'The PACU requires a minimum of 6 air changes per hour per §416.44(a)(1) and ASHRAE 170. Temperature should be 70-75°F with humidity between 20-60%. The area should maintain neutral or positive pressure relative to corridors. Perform a tissue test at the door. Check: (1) is there a temperature monitoring log or digital display?, (2) is the recovery area warm enough for post-anesthesia patients?, (3) are forced-air warming blankets available and used? Hypothermia is a common post-anesthesia complication. Ask: "How do you monitor and maintain temperature in the recovery area?"',
            cmsTag: '§416.44(a)(1), ASHRAE 170',
            tjcRef: 'EC.02.05.01',
            aaahcRef: 'Ch. 8.II — HVAC and Ventilation',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'pacu-env-ventilation-temperature-temperature-monitoring',
                text: 'Is there a temperature monitoring log or digital display in the PACU to ensure temperatures are maintained between 70-75°F?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'pacu-env-ventilation-temperature-recovery-area-warmth',
                text: 'Is the recovery area maintained at a warm enough temperature to prevent hypothermia in post-anesthesia patients?',
                appendixLRef: '416.44(a)(1)',
              },
            ],
          },
          {
            label: 'Temperature 70-75°F monitored and logged',
            narrative:
              'Per ASHRAE Standard 170, the PACU should maintain temperature between 70-75°F (21-24°C) for patient comfort and recovery. Post-anesthesia patients are particularly vulnerable to hypothermia, so temperature control is clinically important. Verify: (1) a temperature monitoring log or digital display is present and current, (2) readings are within the 70-75°F range, (3) any out-of-range readings have documented corrective actions. Photograph the log or display.',
            cmsTag: 'ASHRAE170-2017',
            evidence: ['photo', 'document'],
            checklistPrompts: [
              {
                id: 'pacu-env-temp-temperature-log',
                text: 'Is the PACU temperature monitor or logging device verified calibrated annually, with a documented response process for out-of-range readings?',
                appendixLRef: 'ASHRAE170-2017',
              },
              {
                id: 'pacu-env-temp-humidity-monitoring',
                text: 'Does the PACU humidity monitoring device alarm or flag out-of-range readings for corrective action?',
                appendixLRef: 'ASHRAE170-2017',
              },
            ],
          },
          {
            label: 'ASHRAE 170: 6 ACH in PACU',
            narrative:
              'Per ASHRAE Standard 170, the post-anesthesia care unit requires a minimum of 6 total air changes per hour (ACH). Request the most recent air balancing report and verify the documented ACH for the PACU meets or exceeds 6. The report should also confirm the pressure relationship — PACU should be at neutral or positive pressure relative to corridors. Ask: "What is the documented air exchange rate for the PACU? When was the last balancing report?"',
            cmsTag: 'ASHRAE170-2017',
            evidence: ['document'],
            checklistPrompts: [
              {
                id: 'pacu-env-ach-pacu-ach-requirement',
                text: 'Is the PACU maintained with a minimum of 6 air changes?',
                appendixLRef: 'ASHRAE170-2017',
              },
              {
                id: 'pacu-env-ach-pacu-air-balance-records',
                text: 'Are records of commissioning and periodic air balance for the PACU available and up to date?',
                appendixLRef: 'ASHRAE170-2017',
              },
            ],
          },
        ],
      },
      {
        domain: 'ADA' as SurveyDomain,
        domainLabel: 'ADA Accessibility',
        items: [
          {
            label: 'Accessible patient call system in each recovery bay',
            narrative:
              'Per ADA requirements for effective communication and accessible medical equipment, each PACU recovery bay must have a patient call system that is accessible to patients with limited mobility or hand dexterity. Verify: (1) the call button or cord is within reach of a patient lying in the recovery stretcher, (2) the control can be activated with minimal force and without tight grasping or pinching, (3) for patients who may have limited consciousness or mobility post-anesthesia, the call device is placed where the patient can reach it as they awaken. Ask: "How do you position the call button for patients recovering from anesthesia? What about patients with limited hand function?"',
            cmsTag: '28CFR36.502',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'pacu-ada-call-patient-call-system',
                text: 'Is there at least one accessible patient call system in each recovery bay that can be operated by a person with a disability without assistance?',
                appendixLRef: '28CFR36.502',
              },
              {
                id: 'pacu-ada-call-call-system-operation',
                text: 'Does the patient call system in each recovery bay have operable parts that are reachable and usable by a person with a disability, such as lever-style hardware or push-button controls within a reachable height range?',
                appendixLRef: '28CFR36.502',
              },
            ],
          },
        ],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // SPD
  // ═══════════════════════════════════════════════════════════════════════════
  {
    area: 'SPD',
    areaLabel: 'Sterile Processing Department (SPD)',
    introduction:
      'SPD is a critical infection control area. Improper reprocessing is one of the most common ASC deficiencies cited by CMS. You are looking at workflow (dirty to clean), equipment maintenance, documentation of sterilization cycles, and staff competency.',
    walkOrder:
      'Enter from the decontamination (dirty) side. Follow the workflow from soiled instrument receipt → manual cleaning → ultrasonic/automated washer → inspection/assembly → sterilization → sterile storage. This should be a one-way flow from dirty to clean with no cross-contamination.',
    requiredPhotos: [...FOUR_WALL_BASELINE, ...SPD_EQUIPMENT_PHOTOS],
    estimatedMinutes: 25,
    sections: [
      {
        domain: 'IC',
        domainLabel: 'Infection Control — Reprocessing',
        items: [
          {
            label: 'Workflow and physical separation',
            narrative:
              'Verify clear physical separation between decontamination (dirty) and clean/sterile areas. Per CMS §416.51(b), instruments must be cleaned, disinfected, and sterilized according to manufacturer instructions. Check: (1) is there a physical barrier (wall, pass-through window) between dirty and clean?, (2) is workflow unidirectional — dirty instruments never cross paths with clean/sterile?, (3) are decontamination staff wearing appropriate PPE (fluid-resistant gown, gloves, face protection)?, (4) is there negative pressure in the decontamination area relative to clean areas? Ask: "How do you verify your air pressure differentials?"',
            cmsTag: '§416.51(b)',
            tjcRef: 'IC.02.02.01',
            aaahcRef: 'Ch. 10.III — Sterilization and Disinfection',
            evidence: ['photo', 'observation', 'interview'],
            checklistPrompts: [
              {
                id: 'spd-dirty-clean-workflow-separated',
                text: 'Is SPD workflow physically separated so soiled instruments do not cross clean or sterile work paths?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'spd-physical-barrier-present',
                text: 'Is a physical barrier (wall or pass-through window) in place between the decontamination and clean/sterile areas?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'spd-decontam-ppe-worn',
                text: 'Are decontamination staff wearing fluid-resistant gown, gloves, and face protection?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'spd-decontam-negative-pressure',
                text: 'Is negative pressure maintained in the decontamination area relative to adjacent clean areas?',
                appendixLRef: '416.51(b)',
              },
            ],
          },
          {
            label: 'Instrument cleaning and IFU compliance',
            narrative:
              'Manufacturers\' Instructions for Use (IFU) are the law for instrument reprocessing. Ask: (1) "Where do you keep your IFUs? Show me an example for a complex instrument (e.g., endoscope, powered instrument).", (2) "How do you verify your enzymatic detergent concentration?", (3) "What is the water temperature for manual cleaning?", (4) "How do you clean lumened instruments?" Check that IFUs are accessible — they can be paper binders, electronic, or manufacturer apps. If staff cannot locate IFUs, this is a significant finding.',
            cmsTag: '§416.51(b)',
            tjcRef: 'IC.02.02.01',
            aaahcRef: 'Ch. 10.III.B — Cleaning',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'instrument-cleaning-ifu-available',
                text: 'Are manufacturer IFUs or evidence-based guidelines available and followed before sterilization or HLD?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
          {
            label: 'Sterilizer monitoring — biological indicators',
            narrative:
              'This is one of the most frequently cited deficiencies. Per §416.51(b), sterilization must be monitored with biological indicators (BIs). Verify: (1) BIs are run with EVERY load containing implants — implant loads must be HELD and not released for use until BI results are confirmed negative (typically 24-hour rapid-read or 48-hour traditional incubation), (2) BIs are run at minimum daily on every sterilizer for non-implant loads, (3) BI results are logged with the date, sterilizer ID, cycle number, BI lot number, incubation time, and result (pass/fail), (4) if a BI fails — what is the recall process? All items from that load and loads back to the last negative BI must be recalled. Ask: "When was the last BI failure? What did you do? Show me your recall procedure.", (5) chemical indicators (CIs) — internal CIs should be placed in every package, (6) sterilizer printouts are retained — review recent printouts for correct time, temperature, and pressure. Ask: "Do you use 24-hour rapid-read or 48-hour BIs? For implant loads, how do you hold the instruments until the BI clears?"',
            cmsTag: '§416.51(b)',
            tjcRef: 'IC.02.02.01',
            aaahcRef: 'Ch. 10.III.C — Sterilization Monitoring',
            evidence: ['document', 'photo', 'interview'],
            checklistPrompts: [
              {
                id: 'bi-monitoring-documented',
                text: 'Is biological indicator monitoring documented for each sterilizer at least weekly and for every implant load?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'bi-implant-load-hold-until-negative',
                text: 'Are implant loads held and not released for use until the biological indicator is confirmed negative?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'bi-log-complete',
                text: 'Does the BI log capture date, sterilizer ID, cycle number, BI lot number, incubation time, and result for each test?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'bi-failure-recall-process',
                text: 'Does the facility have a documented recall process that traces items back to the last negative BI when a BI failure occurs?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'bi-ci-internal-per-package',
                text: 'Is an internal chemical indicator placed in every package and are sterilizer printouts retained for review of time, temperature, and pressure?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
          {
            label: 'Sterilizer/washer maintenance and condition',
            narrative:
              'Inspect sterilizer and washer equipment. Look for: (1) rust, biofilm, or mineral deposits on door gaskets — these indicate inadequate preventive maintenance and compromise seal integrity, (2) current preventive maintenance records (typically annual or per manufacturer), (3) water quality testing if required by equipment IFU (many sterilizers require distilled or deionized water), (4) washer-disinfector validation records. Photograph any visible rust or deposits on equipment gaskets.',
            cmsTag: '§416.51(b)',
            tjcRef: 'IC.02.02.01, EC.02.04.01',
            evidence: ['photo', 'document'],
            checklistPrompts: [
              {
                id: 'sterilizer-maintenance-current',
                text: 'Are sterilizers and washer-disinfectors maintained and inspected according to manufacturer instructions?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'sterilizer-gaskets-clean',
                text: 'Are sterilizer door gaskets free of rust, biofilm, and mineral deposits that would compromise seal integrity?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'sterilizer-water-quality-testing',
                text: 'Is water quality testing performed where required by the equipment IFU (distilled or deionized water for sterilizers that require it)?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'sterilizer-washer-validation-records',
                text: 'Are washer-disinfector validation records on file and current?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
          {
            label: 'High-level disinfection (HLD) process',
            narrative:
              'If the facility performs endoscopy or uses semi-critical devices that cannot be sterilized, check the HLD process. Verify: (1) HLD solution is tested with minimum effective concentration (MEC) test strips before each use, (2) MEC results are logged, (3) soak time meets manufacturer requirements, (4) rinsing is performed with appropriate water quality, (5) HLD-processed items are stored to prevent recontamination. Ask: "What HLD agent do you use? What is the soak time? Show me your MEC testing log."',
            cmsTag: '§416.51(b)',
            tjcRef: 'IC.02.02.01',
            aaahcRef: 'Ch. 10.III.D — High-Level Disinfection',
            evidence: ['document', 'interview', 'photo'],
            checklistPrompts: [
              {
                id: 'hld-chemicals-tested-and-logged',
                text: 'Are HLD chemicals tested for appropriate concentration and documented according to manufacturer instructions?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'hld-time-temperature-followed',
                text: 'Are HLD exposure time and temperature followed according to manufacturer instructions or evidence-based guidelines?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'hld-mec-tested-before-each-use',
                text: 'Is the HLD solution tested with minimum effective concentration (MEC) test strips before each use?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'hld-rinsing-water-quality',
                text: 'Is HLD rinsing performed with appropriate water quality per the manufacturer IFU?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'hld-processed-items-stored',
                text: 'Are HLD-processed items stored in a manner that prevents recontamination before use?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
          {
            label: 'Endoscope storage — drying cabinets and hanging',
            narrative:
              'If the facility performs endoscopy or uses flexible scopes, check how scopes are stored after reprocessing. Per §416.51(b), scopes must be stored in a manner that prevents recontamination. Check: (1) are scopes stored in approved HEPA-filtered forced-air drying cabinets (e.g., Medivators, DSD Edge, or similar)? If so, verify the cabinet is functioning — air circulation active, HEPA filter current, no visible contamination inside, (2) is the cabinet cleaned and disinfected per manufacturer IFU? Ask to see the cleaning log, (3) if vertical hanging is used instead of a cabinet — are channels open and draining? Are scopes hanging freely without coiling? Are caps and valves removed?, (4) scopes must NOT be stored coiled in cases or drawers between uses — this traps moisture and promotes bacterial growth, (5) is the storage area clean, dry, and separate from soiled reprocessing? Photograph the storage setup.',
            cmsTag: '§416.51(b)',
            tjcRef: 'IC.02.02.01',
            aaahcRef: 'Ch. 10.III — Reprocessing',
            evidence: ['photo', 'observation', 'document'],
            checklistPrompts: [
              {
                id: 'endoscopes-stored-to-prevent-contamination',
                text: 'Are reprocessed endoscopes stored in a clean area and manner that prevents contamination?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'endoscope-storage-hepa-drying-cabinet',
                text: 'If a HEPA-filtered forced-air drying cabinet is used, is it functioning with active air circulation, current HEPA filter, and no visible contamination inside?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'endoscope-storage-cabinet-cleaning-log',
                text: 'Is the endoscope storage cabinet cleaned and disinfected per manufacturer IFU with a log on file?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'endoscope-storage-hang-open-channels',
                text: 'If vertical hanging is used, are scopes hanging freely without coiling, with channels open and draining, and caps and valves removed?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'endoscope-storage-not-coiled-in-cases',
                text: 'Are endoscopes never stored coiled in cases or drawers between uses, in a clean dry area separate from soiled reprocessing?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
          {
            label: 'Endoscope drying time and reprocessing log',
            narrative:
              'Per §416.51(b), all endoscope channels must be thoroughly dried with forced air before storage to prevent bacterial regrowth — particularly Pseudomonas and other waterborne organisms. Verify: (1) forced-air drying is performed on ALL channels (suction, biopsy, air/water, auxiliary) — not just flushed with alcohol, (2) minimum drying time follows the scope manufacturer\'s IFU — this varies by manufacturer and scope model, ask to see the IFU, (3) for scopes that will be stored (not used within a few hours), a full drying cycle is required; for immediate-use scopes (used within the same session), shorter drying may be acceptable per manufacturer guidance, (4) each scope should have a reprocessing log or tracking system: who reprocessed it, date/time, which AER cycle was used, drying time, storage location, and scope serial number or asset tag, (5) verify the log is complete — pull 3-4 recent entries and check for gaps. Ask: "What is your minimum drying time before storage? How do you track which scope was used on which patient?"',
            cmsTag: '§416.51(b)',
            tjcRef: 'IC.02.02.01',
            aaahcRef: 'Ch. 10.III.D — High-Level Disinfection',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'endoscope-drying-documented',
                text: 'Are endoscopes allowed to dry after HLD and tracked in a complete reprocessing log?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'endoscope-drying-all-channels-forced-air',
                text: 'Is forced-air drying performed on ALL channels (suction, biopsy, air/water, auxiliary) — not just an alcohol flush?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'endoscope-drying-time-meets-ifu',
                text: 'Does the minimum drying time follow the scope manufacturer IFU for stored vs. immediate-use scopes?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'endoscope-drying-log-complete',
                text: 'Does each reprocessing log entry capture reprocessor, date/time, AER cycle, drying time, storage location, and scope serial/asset tag?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
          {
            label: 'AAMI ST79 steam sterilization: decontamination workflow',
            narrative:
              'Per AAMI ST79 — Comprehensive Guide to Steam Sterilization and Sterility Assurance, the decontamination workflow must maintain strict dirty-to-clean separation. Verify: (1) soiled instruments enter the decontamination area through a designated intake point and never re-enter the clean side until fully reprocessed, (2) decontamination staff use appropriate PPE (fluid-resistant gown, heavy-duty gloves, face shield), (3) the decontamination sink has appropriate depth and water temperature for manual cleaning, (4) work flows in one direction — from dirty to clean — with no backtracking. Photograph the workflow layout.',
            cmsTag: 'AAMIST79-2017',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'aami-decontamination-workflow-separated',
                text: 'Does decontamination workflow move from dirty to clean without backtracking or cross-contamination?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'aami-decontamination-intake-point',
                text: 'Do soiled instruments enter through a designated intake point and stay on the decontamination side until fully reprocessed?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'aami-decontamination-staff-ppe',
                text: 'Are decontamination staff wearing fluid-resistant gown, heavy-duty gloves, and face shield?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'aami-decontamination-sink-parameters',
                text: 'Does the decontamination sink have appropriate depth and water temperature for manual cleaning per AAMI ST79?',
                appendixLRef: '416.51(b)',
              },
            ],
          },
          {
            label: 'Packaging inspection: seal integrity and chemical indicator',
            narrative:
              'Per AAMI ST79, all instrument packages must be inspected for seal integrity before sterilization and after. Verify: (1) peel-pouch seals are uniform, complete, and free of channels or gaps, (2) wrapped trays have intact wrapping with no tears or moisture, (3) an internal chemical indicator (CI) is placed inside every package — not just external tape, (4) staff visually inspect packages before loading the sterilizer and again after the cycle before placing in sterile storage. Ask: "How do you verify seal integrity? Where do you place the internal chemical indicator?"',
            cmsTag: 'AAMIST79-2017',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'packaging-seals-ci-inspected',
                text: 'Are package seals, wraps, and internal chemical indicators inspected before release to sterile storage?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'packaging-peel-pouch-uniform',
                text: 'Are peel-pouch seals uniform and complete, with no channels or gaps in the seal?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'packaging-wrapped-tray-intact',
                text: 'Are wrapped trays free of tears, moisture damage, and missing wrapper layers?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'packaging-ci-inside-every-package',
                text: 'Is an internal chemical indicator placed inside every package (not just external tape)?',
                appendixLRef: '416.51(b)',
              },
            ],
          },
          {
            label: 'BI placement protocol: PCD challenge pack with each load',
            narrative:
              'Per AAMI ST79, a process challenge device (PCD) containing a biological indicator (BI) must be used to monitor sterilization efficacy. Verify: (1) a PCD is placed in the most challenging location of each sterilizer load (typically the center-bottom of a full load), (2) the frequency of BI testing is documented — at minimum daily for each sterilizer and with every load containing implants, (3) BI results are logged with sterilizer ID, cycle number, BI lot number, incubation start/end time, and pass/fail result. Ask: "Where do you place the PCD in the load? How often do you run BIs?"',
            cmsTag: 'AAMIST79-2017',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'pcd-bi-placement-documented',
                text: 'Is a BI process challenge device placed in the most challenging sterilizer location at the required frequency?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'pcd-bi-frequency-documented',
                text: 'Is BI testing frequency documented as at minimum daily per sterilizer and with every load containing implants?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'pcd-bi-log-fields-complete',
                text: 'Does the BI log capture sterilizer ID, cycle number, BI lot number, incubation start/end time, and pass/fail per entry?',
                appendixLRef: '416.51(b)',
              },
            ],
          },
          {
            label: 'Sterile storage: temp/humidity monitored, shelf-life policy',
            narrative:
              'Per AAMI ST79, sterile storage areas must maintain controlled environmental conditions to preserve sterile package integrity. Verify: (1) temperature and humidity are monitored — sterile storage should be ≤75°F and <70% relative humidity, (2) a monitoring log or digital display is present and current, (3) the facility has a shelf-life policy posted — either event-related sterility (packages remain sterile unless the integrity of the packaging is compromised) or a time-related expiration system, (4) staff understand which system they use and can explain it. Ask: "What is your shelf-life policy for sterile packages? How do you determine when a package is no longer considered sterile?"',
            cmsTag: 'AAMIST79-2017',
            evidence: ['document', 'observation'],
            checklistPrompts: [
              {
                id: 'sterile-storage-conditions-monitored',
                text: 'Are sterile storage temperature, humidity, and shelf-life controls monitored to preserve package integrity?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'sterile-storage-off-floor-off-walls',
                text: 'Are sterile supplies stored at least 8–10 inches off the floor and at least 2 inches from outside walls to prevent condensation contact?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'sterile-storage-sprinkler-clearance',
                text: 'Are sterile supplies stored at least 18 inches below sprinkler heads?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'sterile-storage-shelving-wipeable',
                text: 'Is shelving clean and in good repair with no rust and no exposed wood or cardboard (surfaces wipeable)?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'sterile-storage-package-integrity-fifo',
                text: 'Are sterile packages free of tears or wet contamination, and is stock rotated FIFO so older items are used first?',
                appendixLRef: '416.51(b)',
              },
            ],
          },
          {
            label: 'Endoscope reprocessing: leak testing equipment',
            narrative:
              'Per AAMI ST91 — Flexible and Semi-Rigid Endoscope Processing, leak testing must be performed before every reprocessing cycle to detect damage to the endoscope that could compromise cleaning or patient safety. Verify: (1) a leak tester is present and in good working order — check calibration date if applicable, (2) staff perform the leak test before manual cleaning (before the scope is submerged in enzymatic detergent), (3) the leak test procedure follows the endoscope manufacturer IFU, (4) failed leak tests are documented and the scope is removed from service for repair. Ask: "Show me your leak tester. When do you perform the leak test in your workflow? What happens if a scope fails?"',
            cmsTag: 'AAMIST91-2010',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'endoscope-leak-testing-performed',
                text: 'Is leak testing performed before manual cleaning for each flexible endoscope reprocessing cycle?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'endoscope-leak-tester-working',
                text: 'Is the leak tester present, in good working order, and calibrated per applicable schedule?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'endoscope-leak-test-ifu-followed',
                text: 'Does the leak-test procedure follow the endoscope manufacturer IFU?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'endoscope-leak-failure-removed',
                text: 'When a leak test fails, is the event documented and the scope removed from service for repair?',
                appendixLRef: '416.51(b)',
              },
            ],
          },
          {
            label: 'Manual cleaning documented: enzymatic detergent and brushes',
            narrative:
              'Per AAMI ST91, manual cleaning is the most critical step in endoscope reprocessing — inadequate cleaning cannot be compensated for by high-level disinfection or sterilization. Verify: (1) enzymatic detergent is used at the correct concentration and temperature per manufacturer IFU, (2) appropriate-size channel brushes are available for each scope model — brushes must be sized to match the channel diameter, (3) all channels are brushed until visually clean, (4) cleaning steps are documented on a reprocessing log. Ask: "What enzymatic detergent do you use? How do you verify the correct brush size for each scope model?"',
            cmsTag: 'AAMIST91-2010',
            evidence: ['document', 'observation'],
            checklistPrompts: [
              {
                id: 'endoscope-manual-cleaning-documented',
                text: 'Is manual endoscope cleaning documented with correct detergent, brushing, and channel-cleaning steps?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'endoscope-enzymatic-detergent-ifu',
                text: 'Is enzymatic detergent used at the manufacturer-specified concentration and temperature?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'endoscope-brush-size-matched',
                text: 'Are channel brushes available in sizes matched to each endoscope model and channel diameter?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'endoscope-channels-brushed-visually-clean',
                text: 'Are all endoscope channels brushed until visibly clean during manual cleaning?',
                appendixLRef: '416.51(b)',
              },
            ],
          },
          {
            label: 'HLD cycle parameters logged',
            narrative:
              'Per AAMI ST91, high-level disinfection (HLD) cycle parameters must be documented for every cycle. Verify: (1) time of exposure meets manufacturer requirements for the HLD agent, (2) temperature during the HLD cycle is within the required range, (3) chemical concentration is tested with minimum effective concentration (MEC) test strips before each use and results logged, (4) all parameters (time, temperature, concentration) are recorded per cycle on a reprocessing log. Pull 3-4 recent log entries and verify completeness.',
            cmsTag: 'AAMIST91-2010',
            evidence: ['document', 'photo'],
            checklistPrompts: [
              {
                id: 'hld-cycle-parameters-logged',
                text: 'Are HLD cycle time, temperature, and concentration parameters logged for each cycle?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'hld-exposure-time-manufacturer',
                text: 'Does the HLD exposure time meet the manufacturer requirements for the selected agent?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'hld-temperature-in-range',
                text: 'Is the HLD cycle temperature maintained within the manufacturer-required range throughout the cycle?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'hld-mec-test-before-each-use',
                text: 'Is MEC test-strip verification performed before each HLD use with results logged per cycle?',
                appendixLRef: '416.51(b)',
              },
            ],
          },
          {
            label: 'Endoscope drying protocol: forced air, vertical hang, ventilated storage',
            narrative:
              'Per AAMI ST91, proper drying and storage of endoscopes is essential to prevent bacterial regrowth, particularly waterborne organisms like Pseudomonas. Verify: (1) all channels are dried with forced air after reprocessing — alcohol flush followed by forced air per manufacturer IFU, (2) scopes are hung vertically with tips pointing down to facilitate drainage, (3) scopes are stored in a ventilated storage cabinet with HEPA-filtered forced-air circulation, or hung freely in a clean, ventilated area with caps and valves removed, (4) scopes are NOT stored coiled in cases or trays. Photograph the storage setup.',
            cmsTag: 'AAMIST91-2010',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'endoscope-drying-storage-protocol-followed',
                text: 'Are endoscope channels dried and scopes stored vertically or in ventilated storage to prevent moisture retention?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'endoscope-forced-air-drying',
                text: 'Are all endoscope channels dried with forced air (alcohol flush followed by forced air) per manufacturer IFU?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'endoscope-vertical-hang-tips-down',
                text: 'Are scopes hung vertically with tips pointing down to facilitate drainage?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'endoscope-no-coiled-storage',
                text: 'Are endoscopes never stored coiled in cases or trays between uses?',
                appendixLRef: '416.51(b)',
              },
            ],
          },
          {
            label: 'Water quality: utility water for cleaning, critical water for final rinse',
            narrative:
              'Per AAMI TIR34 — Water for the Reprocessing of Medical Devices, water quality is critical for instrument reprocessing. Verify: (1) utility-quality water is acceptable for initial cleaning and rinsing, (2) AAMI-quality critical water (bacteria ≤10 CFU/mL, endotoxin ≤10 EU/mL) is used for the final rinse of instruments before sterilization or HLD, (3) the water treatment system (if present — RO, DI, or distillation) is maintained per manufacturer schedule. Ask: "What water do you use for the final rinse? Is it treated? How do you verify water quality?"',
            cmsTag: 'AAMIST108-2014',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'critical-water-final-rinse-used',
                text: 'Is water quality selected for each reprocessing stage, including critical water when required for final rinsing?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'critical-water-thresholds-known',
                text: 'Does the facility know its critical-water AAMI thresholds (bacteria ≤10 CFU/mL, endotoxin ≤10 EU/mL) and verify them at the final-rinse step?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'critical-water-treatment-system-maintained',
                text: 'If a water treatment system (RO, DI, or distillation) is in use, is it maintained per the manufacturer schedule with PM records on file?',
                appendixLRef: '416.51(b)',
              },
            ],
          },
          {
            label: 'Water quality testing schedule and results',
            narrative:
              'Per AAMI TIR34, water quality used in instrument reprocessing must be tested on a regular schedule. Verify: (1) a testing schedule is defined — typically monthly for microbial testing and quarterly for endotoxin, though frequency may vary by facility risk assessment, (2) current test results are posted or readily accessible, (3) results meet AAMI-quality thresholds (bacteria ≤10 CFU/mL, endotoxin ≤10 EU/mL for critical water), (4) corrective actions are documented for any out-of-specification results. Photograph the posted results or test log.',
            cmsTag: 'AAMIST108-2014',
            evidence: ['document', 'photo'],
            checklistPrompts: [
              {
                id: 'water-quality-monitoring-current',
                text: 'Are water treatment and point-of-use monitoring results current and within the facility-defined limits?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'water-quality-testing-schedule',
                text: 'Is there a defined water testing schedule (typically monthly microbial and quarterly endotoxin) documented in facility policy?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'water-quality-thresholds-met',
                text: 'Do water test results meet AAMI thresholds (bacteria ≤10 CFU/mL, endotoxin ≤10 EU/mL for critical water)?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'water-quality-corrective-action',
                text: 'Are corrective actions documented for any out-of-specification water quality results?',
                appendixLRef: '416.51(b)',
              },
            ],
          },
          {
            label: 'Low-temp sterilization (EtO/H2O2): cycle parameters logged',
            narrative:
              'Per AAMI ST58 — Chemical Sterilization and High-Level Disinfection, if the facility uses low-temperature sterilization (ethylene oxide/EtO or hydrogen peroxide/H2O2 plasma), cycle parameters must be documented for every load. Verify: (1) cycle type and parameters (time, temperature, gas concentration or plasma phase) are logged per load, (2) the sterilizer printout is retained and matches expected parameters, (3) the load contents are documented. Ask: "What low-temperature sterilization method do you use? Can you show me the cycle logs?"',
            cmsTag: 'AAMIST79-2017',
            evidence: ['document'],
            checklistPrompts: [
              {
                id: 'low-temp-cycle-parameters-logged',
                text: 'Are low-temperature sterilization cycle parameters and load contents documented for each load?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'low-temp-cycle-printout-retained',
                text: 'Is the sterilizer printout retained for each low-temp cycle and confirmed to match expected parameters?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'low-temp-load-contents-documented',
                text: 'Are the contents of each low-temperature sterilization load documented?',
                appendixLRef: '416.51(b)',
              },
            ],
          },
          {
            label: 'Aeration time documented and met before instrument release',
            narrative:
              'Per AAMI ST58, for ethylene oxide (EtO) sterilization, instruments must undergo a defined aeration period to allow residual EtO gas to dissipate before the instruments can safely be used on patients. Verify: (1) the required aeration time is documented per the sterilizer and packaging manufacturer IFUs, (2) aeration start and end times are logged for each EtO cycle, (3) instruments are held and not released for use until the full aeration period is completed, (4) there is no shortcutting of aeration time to meet case schedules. Ask: "What is your aeration time for EtO loads? How do you hold instruments until aeration is complete?"',
            cmsTag: 'AAMIST79-2017',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'eto-aeration-complete-before-release',
                text: 'Is EtO aeration documented and completed before instruments are released for patient use?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'eto-aeration-time-per-ifu',
                text: 'Is the required EtO aeration time documented per the sterilizer and packaging manufacturer IFUs?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'eto-aeration-no-shortcut',
                text: 'Are instruments held until the full aeration period is completed, with no shortcutting to meet case schedules?',
                appendixLRef: '416.51(b)',
              },
            ],
          },
          {
            label: 'BI with every low-temp load: documentation present',
            narrative:
              'Per AAMI ST58, a biological indicator (BI) must be included with every low-temperature sterilization load — unlike steam sterilization where daily BI testing may suffice for non-implant loads, low-temp processes require BI monitoring with each cycle due to the narrower margins of safety. Verify: (1) BI results are documented for every low-temp load, (2) each log entry includes sterilizer ID, cycle number, BI lot number, incubation time, and result, (3) loads are not released until BI results are confirmed negative. Ask: "Do you run a BI with every low-temp cycle? How long do you incubate before releasing the load?"',
            cmsTag: 'AAMIST79-2017',
            evidence: ['document'],
            checklistPrompts: [
              {
                id: 'low-temp-bi-every-load',
                text: 'Is a biological indicator documented with every low-temperature sterilization load?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'low-temp-bi-log-fields-complete',
                text: 'Does each low-temp BI entry include sterilizer ID, cycle number, BI lot number, incubation time, and result?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'low-temp-bi-load-held-until-negative',
                text: 'Are low-temp loads held and not released for use until BI results are confirmed negative?',
                appendixLRef: '416.51(b)',
              },
            ],
          },
          {
            label: 'Occupational exposure monitoring for EtO',
            narrative:
              'Per AAMI ST58 and OSHA 29 CFR 1910.1047, if the facility uses ethylene oxide (EtO) sterilization, occupational exposure monitoring is required. The OSHA permissible exposure limit (PEL) for EtO is 1 ppm as an 8-hour TWA and 5 ppm as a 15-minute excursion limit. Verify: (1) personal exposure monitoring has been performed for SPD staff who operate or work near the EtO sterilizer, (2) results are documented and within OSHA PEL, (3) engineering controls (dedicated exhaust ventilation for the EtO sterilizer, sealed door gaskets) are in place, (4) staff have been trained on EtO hazards and emergency procedures. Ask: "Do you use EtO sterilization? If so, can I see your exposure monitoring results? When was the last monitoring performed?"',
            cmsTag: 'AAMIST79-2017',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'eto-exposure-monitoring-documented',
                text: 'If EtO is used, are occupational exposure monitoring results documented and within required limits?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'eto-results-within-pel',
                text: 'Are EtO personal monitoring results within the OSHA PEL (1 ppm 8-hour TWA; 5 ppm 15-minute excursion)?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'eto-engineering-controls',
                text: 'Are engineering controls in place (dedicated exhaust ventilation for the EtO sterilizer, sealed door gaskets)?',
                appendixLRef: '416.51(b)',
              },
              {
                id: 'eto-staff-training',
                text: 'Have staff who operate or work near the EtO sterilizer been trained on EtO hazards and emergency procedures?',
                appendixLRef: '416.51(b)',
              },
            ],
          },
        ],
      },
      {
        domain: 'ENV',
        domainLabel: 'Environmental Controls — SPD',
        items: [
          {
            label: 'Directional airflow — decontam negative, clean positive',
            narrative:
              'SPD requires directional airflow to prevent cross-contamination per §416.44(a)(1) and ASHRAE 170. The decontamination area must be NEGATIVE pressure relative to adjacent spaces (contaminated air must not escape into clean areas). The clean/sterile processing and storage area must be POSITIVE pressure. Perform a tissue test at doors between decontam and clean areas — the tissue should pull toward the decontam side, confirming air flows from clean to dirty. Minimum air changes: decontam requires 6 ACH, clean/sterile requires 4 ACH. Ask: "How do you verify pressure relationships between decontam and clean sides? When was the last HVAC balancing report for SPD?"',
            cmsTag: '§416.44(a)(1), ASHRAE 170',
            tjcRef: 'EC.02.05.01',
            aaahcRef: 'Ch. 8.II — HVAC and Ventilation',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'spd-env-directional-airflow-decontam-directional-airflow-decontam',
                text: 'Is the decontamination area maintained at negative pressure relative to adjacent clean areas?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'spd-env-directional-airflow-decontam-directional-airflow-clean',
                text: 'Is the clean/sterile processing and storage area maintained at positive pressure relative to the decontamination area?',
                appendixLRef: '416.44(a)(1)',
              },
            ],
          },
          {
            label: 'SPD temperature, humidity, and environmental conditions',
            narrative:
              'Check the SPD environment: (1) temperature and humidity are monitored and within range (decontamination: 60-65°F, 30-60% humidity; sterile storage: 75°F max, below 70% humidity), (2) lighting is adequate for visual inspection of instruments, (3) surfaces are cleanable and in good repair — no exposed wood, cardboard, or porous surfaces in decontamination, (4) eyewash station is present and functional in the decontamination area (check the weekly flush log). Photograph the temp/humidity log.',
            cmsTag: '§416.44(a)(1)',
            tjcRef: 'EC.02.06.01',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'spd-env-spd-temperature-humidity-temperature-humidity-monitoring',
                text: 'Is the SPD temperature and humidity monitored and within the required ranges (decontamination: 60-65°F, 30-60% humidity; sterile storage: 75°F max, below 70% humidity)?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'spd-env-spd-temperature-humidity-eyewash-station-functional',
                text: 'Is the eyewash station in the decontamination area present and functional, with a weekly flush log showing it has been maintained as required?',
                appendixLRef: '416.44(a)(1)',
              },
            ],
          },
        ],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // MED STORAGE
  // ═══════════════════════════════════════════════════════════════════════════
  {
    area: 'Med Storage',
    areaLabel: 'Medication Storage / Pharmacy',
    introduction:
      'Medication storage is a high-yield inspection area. Expired medications, improper storage temperatures, and controlled substance documentation gaps are among the most common ASC survey deficiencies.',
    walkOrder:
      'Enter the medication storage room. Check security/access control first. Then systematically check: refrigerator, controlled substance storage, general stock shelves, and any automated dispensing machines.',
    requiredPhotos: [...FOUR_WALL_BASELINE, ...MED_STORAGE_PHOTOS],
    estimatedMinutes: 20,
    sections: [
      {
        domain: 'MED',
        domainLabel: 'Medication Management',
        items: [
          {
            label: 'Medication security and access control',
            narrative:
              'Per CMS §416.48(a), the ASC must store medications under proper conditions of security. Verify: (1) the medication storage area is locked and access is limited to authorized personnel, (2) controlled substances are in a separately locked cabinet or safe with double-lock mechanism, (3) controlled substance log shows accurate counts and is reconciled at each shift change, (4) a pharmacist or designated practitioner oversees the medication program. Ask: "Who has access to the medication room? How are controlled substances counted?"',
            cmsTag: '§416.48(a)',
            tjcRef: 'MM.03.01.01',
            aaahcRef: 'Ch. 9.III — Pharmaceutical Services',
            evidence: ['photo', 'observation', 'interview'],
          
    checklistPrompts: [
      {
        id: 'med-security-medication-storage-access',
        text: 'Are there written policies and procedures in place for the procurement, handling, storage, and dispensing of drugs and biologicals, including accountability and security measures?',
        appendixLRef: '416.48(a)',
      },
      {
        id: 'med-security-controlled-substances-locking',
        text: 'Is there a system in place to ensure that only authorized personnel have access to medications, and are there records of who accesses medications and when?',
        appendixLRef: '416.48(a)',
      },
    ],
},
          {
            label: 'Expired medication check',
            narrative:
              'This is one of the most commonly cited deficiencies. Systematically check expiration dates on medications. Pull at least 10-15 items from shelves and check dates. Focus on: (1) IV fluids and irrigation solutions — check dates on every bag, (2) topical preparations and ointments, (3) multi-dose vials — check for "opened" date (must be discarded per manufacturer guidelines, typically 28 days), (4) single-dose vials that have been opened, (5) emergency medications in the crash cart, (6) items pushed to the back of shelves. Photograph any expired items showing the expiration date on the label.',
            cmsTag: '§416.48(a)',
            tjcRef: 'MM.03.01.01',
            aaahcRef: 'Ch. 9.III.B — Storage',
            evidence: ['photo'],
          
    checklistPrompts: [
      {
        id: 'med-expired-medication-check-1-are-expired-medications-removed-from-the-storage-area',
        text: 'Are expired medications removed from the storage area and properly disposed of according to established policies and procedures?',
        appendixLRef: '416.48(a)',
      },
      {
        id: 'med-expired-medication-check-2-is-there-a-system-in-place-to-regularly-check-for-expired-medications',
        text: 'Is there a system in place to regularly check for expired medications, such as a scheduled inventory review or automated alerts?',
        appendixLRef: '416.48(a)',
      },
      {
        id: 'med-expired-iv-fluids-irrigation',
        text: 'Are IV fluids and irrigation solutions checked for expiration dates with no expired bags present on the shelves?',
        appendixLRef: '416.48(a)',
      },
      {
        id: 'med-expired-topical-preparations',
        text: 'Are topical preparations, ointments, and single-dose vials free of expired dating or opened-but-outdated items?',
        appendixLRef: '416.48(a)',
      },
      {
        id: 'med-expired-multi-dose-28-day',
        text: 'Are opened multi-dose vials dated and discarded per manufacturer guidelines (typically within 28 days of opening)?',
        appendixLRef: '416.48(a)',
      },
      {
        id: 'med-expired-crash-cart-emergency-meds',
        text: 'Are emergency medications in the crash cart checked for expiration and replaced before expiry?',
        appendixLRef: '416.48(a)',
      },
    ],
},
          {
            label: 'Refrigerator temperature monitoring',
            narrative:
              'Check the medication refrigerator. Per USP standards, refrigerated medications must be stored at 2-8°C (36-46°F). Verify: (1) a calibrated thermometer is present (digital min/max preferred), (2) temperature is logged daily (or continuous digital monitoring), (3) review the log — are there any out-of-range readings? If so, was corrective action documented?, (4) no food or personal items stored with medications, (5) the refrigerator is clean with no expired items. If vaccines are stored, verify compliance with the CDC Vaccine Storage and Handling Toolkit.',
            cmsTag: '§416.48(a)',
            tjcRef: 'MM.03.01.01',
            evidence: ['photo', 'document'],
          
    checklistPrompts: [
      {
        id: 'med-fridge-calibrated-thermometer',
        text: 'Is a calibrated fridge thermometer in use with daily temperature logs in the 36-46°F range and documented action for out-of-range readings?',
        appendixLRef: '416.48(a)',
      },
      {
        id: 'med-fridge-no-food-or-personal',
        text: 'Is the medication refrigerator free of food or personal items?',
        appendixLRef: '416.48(a)',
      },
      {
        id: 'med-fridge-clean-no-expired',
        text: 'Is the medication refrigerator interior clean and free of expired stock?',
        appendixLRef: '416.48(a)',
      },
      {
        id: 'med-fridge-vaccine-cdc-toolkit',
        text: 'If vaccines are stored, does the facility comply with the CDC Vaccine Storage and Handling Toolkit?',
        appendixLRef: '416.48(a)',
      },
      {
        id: 'med-fridge-temperature-logs',
        text: 'Are the refrigerators used for storing drugs and biologicals equipped with a temperature monitoring system that records and maintains temperature data?',
        appendixLRef: '416.48(a)',
      },
    ],
},
          {
            label: 'Formulary compliance — only specialty-appropriate drugs stocked',
            narrative:
              'Cross-reference the medications on the shelves against the facility\'s adopted formulary. Per §416.48, only medications appropriate to the surgical specialties performed at this facility should be present. Verify: (1) medications stocked match the approved formulary — flag any drugs not on the formulary list, (2) the inventory is appropriate for the facility\'s specialty (e.g., an orthopedic ASC should stock local anesthetics, NSAIDs, antibiotics, and reversal agents — not oncology drugs), (3) no medications are present that fall outside the facility\'s scope of services. Ask: "Are all medications here on your formulary? How do you handle a request for a non-formulary drug?"',
            cmsTag: '§416.48(a)',
            tjcRef: 'MM.02.01.01',
            aaahcRef: 'Ch. 9.III — Pharmaceutical Services',
            evidence: ['photo', 'document', 'interview'],
            checklistPrompts: [
              {
                id: 'med-formulary-compliance-only-specialty--procurement-handling-storage',
                text: 'Are only specialty-appropriate drugs and biologicals procured, handled, stored, and dispensed according to established policies and procedures?',
                appendixLRef: '416.48(a)',
              },
              {
                id: 'med-formulary-compliance-only-specialty--accountability-security',
                text: 'Are accountability and security measures in place to ensure that only specialty-appropriate drugs and biologicals are stocked and accessed within the ASC?',
                appendixLRef: '416.48(a)',
              },
            ],
          },
          {
            label: 'Look-alike/sound-alike and high-alert medications',
            narrative:
              'Per §416.48(a), look-alike/sound-alike (LASA) medications must have strategies to prevent errors. The LASA list should be specific to this facility\'s formulary — only drugs actually stocked need LASA safeguards. Check: (1) does the facility maintain a LASA list derived from their formulary?, (2) are LASA medications separated on shelves or flagged with tall-man lettering?, (3) are high-alert medications (concentrated electrolytes, neuromuscular blocking agents, anticoagulants) stored separately or with warning labels?, (4) concentrated potassium chloride must NOT be available in patient care areas unless special safeguards are in place. Ask: "What is your facility-specific LASA list? When was it last reviewed? How do you prevent mix-ups?"',
            cmsTag: '§416.48(a)',
            tjcRef: 'NPSG.03.04.01, MM.01.02.01',
            aaahcRef: 'Ch. 9.III.C — Medication Safety',
            evidence: ['photo', 'interview'],
            checklistPrompts: [
              {
                id: 'med-med-look-alike-sound-lasa-list-formulary',
                text: "Is the facility's look-alike/sound-alike (LASA) medication list specific to the drugs actually stocked in the facility's formulary?",
                appendixLRef: '416.48(a)',
              },
              {
                id: 'med-med-look-alike-sound-lasa-separation-methods',
                text: 'Are look-alike/sound-alike (LASA) medications separated on shelves or marked with tall-man lettering to prevent errors?',
                appendixLRef: '416.48(a)',
              },
            ],
          },
          {
            label: 'DEA registration (Form 224) posted or accessible',
            narrative:
              'Per 21 CFR 1301.35 (DEA Registration Requirements), any facility that dispenses, distributes, or administers controlled substances must hold a valid DEA registration. Verify: (1) the DEA Certificate of Registration (Form 224) is posted in a visible location or is immediately accessible upon request, (2) the registration is current — check the expiration date (DEA registrations are valid for 3 years), (3) the registered address matches this facility location. Photograph the certificate showing the registration number and expiration date.',
            cmsTag: '21CFR1301.02',
            evidence: ['photo', 'document'],
            checklistPrompts: [
              {
                id: 'medstorage-dea-form-dea-registration-posted',
                text: 'Is the DEA registration (Form 224) posted in a visible location within the facility?',
                appendixLRef: '21CFR1301.02',
              },
              {
                id: 'medstorage-dea-form-dea-registration-accessible',
                text: 'Is the DEA registration (Form 224) readily accessible to authorized personnel during inspections?',
                appendixLRef: '21CFR1301.02',
              },
            ],
          },
          {
            label: 'Registration scope matches substances present',
            narrative:
              'Per 21 CFR 1301.13, the DEA registration must authorize the specific schedules of controlled substances the facility possesses. Verify: (1) the registration lists the appropriate schedules (I-V) and activity codes matching the facility\'s operations, (2) cross-reference the controlled substances physically present in the facility against the registration scope — the facility should not possess scheduled substances outside its registered authority. Ask: "What schedules of controlled substances do you stock here? Can I compare that to your DEA registration?"',
            cmsTag: '21CFR1301.02',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'medstorage-dea-scope-registration-scope-substances',
                text: 'Are the DEA registration\'s authorized schedules and activity codes verified against the controlled substances physically present at the facility, confirming no drug is stocked outside the facility\'s registered authority?',
                appendixLRef: '21CFR1301.02',
              },
              {
                id: 'medstorage-dea-scope-substances-matched-registration',
                text: 'Are all controlled substances present at the site included in the DEA registration scope?',
                appendixLRef: '21CFR1301.02',
              },
            ],
          },
          {
            label: 'Schedule II double-lock requirement',
            narrative:
              'Per 21 CFR 1301.72 (DEA Physical Security Controls), Schedule II controlled substances must be stored in a substantially constructed cabinet or safe with a double-lock mechanism — two separate locks requiring two separate keys or combinations. Verify: (1) Schedule II drugs (fentanyl, morphine, oxycodone, hydromorphone, etc.) are stored in a cabinet or safe with two independent locking mechanisms, (2) both locks are engaged when the cabinet is not being accessed, (3) the cabinet itself is substantially constructed (not a simple lockbox). Photograph the double-lock setup (without showing contents).',
            cmsTag: '21CFR1301.03',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'medstorage-dea-lock-schedule-ii-double-lock',
                text: 'Is a substantially constructed cabinet or safe with limited key access used to store Schedule II drugs?',
                appendixLRef: '21CFR1301.03',
              },
              {
                id: 'medstorage-dea-lock-schedule-ii-double-lock-requirement',
                text: 'Are two separate keys or locks required for access to Schedule II drugs, with no single individual having control of both?',
                appendixLRef: '21CFR1301.03',
              },
            ],
          },
          {
            label: 'Key/combination control and access log',
            narrative:
              'Per 21 CFR 1301.72, access to controlled substances must be limited to authorized personnel. Verify: (1) a log documents who has keys or combinations to the controlled substance storage, (2) key check-out records are maintained if keys are shared or transferred between shifts, (3) combinations are changed when personnel with access leave the organization, (4) the number of individuals with access is minimized and each is authorized by name. Ask: "Who has access to the controlled substance cabinet? How do you manage key distribution? When was the combination last changed?"',
            cmsTag: '21CFR1301.03',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'medstorage-dea-keys-key-combination-control',
                text: 'Is the Schedule II drug storage cabinet or safe equipped with a key/combination lock that restricts access to authorized personnel only?',
                appendixLRef: '21CFR1301.03',
              },
              {
                id: 'medstorage-dea-keys-access-log-maintenance',
                text: 'Are access logs maintained for the Schedule II drug storage cabinet or safe, documenting each entry and exit by authorized personnel?',
                appendixLRef: '21CFR1301.03',
              },
            ],
          },
          {
            label: 'Biennial DEA physical inventory',
            narrative:
              'Per 21 CFR 1304.11, every DEA registrant must perform a complete physical inventory of all controlled substances every two years (biennial inventory). Verify: (1) the last inventory date — it must be within the preceding 2 years, (2) for Schedule II substances, the count must be exact (not estimated), (3) for Schedule III-V substances, an estimated count is acceptable unless the container holds more than 1,000 dosage units, (4) the inventory is signed and dated by the person who conducted it. Ask: "When was your last DEA biennial inventory? Can I see the records? Who conducted it?"',
            cmsTag: '21CFR1301.02',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'medstorage-dea-inventory-biennial-inventory',
                text: 'Is a complete physical inventory of controlled substances conducted every two years?',
                appendixLRef: '21CFR1301.03',
              },
              {
                id: 'medstorage-dea-inventory-inventory-records',
                text: 'Are the records of the biennial physical inventory maintained for at least two years?',
                appendixLRef: '21CFR1301.03',
              },
            ],
          },
          {
            label: 'Perpetual inventory log for Schedule II: daily reconciliation',
            narrative:
              'Per 21 CFR 1304.21 and DEA best practices, a perpetual inventory (running count) for Schedule II controlled substances allows real-time accountability. Verify: (1) a perpetual inventory log is maintained for every Schedule II drug, (2) every transaction (received, administered, wasted, returned) is recorded with the date, time, patient name or purpose, quantity, and the name of the person responsible, (3) counts are reconciled at least daily — typically at each shift change, (4) the running balance on the log matches the physical count. Perform a spot-check: pick a Schedule II drug and compare the log count to the actual count in the cabinet.',
            cmsTag: '21CFR1301.02',
            evidence: ['document', 'observation'],
            checklistPrompts: [
              {
                id: 'medstorage-dea-perpetual-perpetual-inventory-log',
                text: 'Is a perpetual receipt/administration/waste log maintained for Schedule II drugs?',
                appendixLRef: '21CFR1301.03',
              },
              {
                id: 'medstorage-dea-perpetual-biennial-inventory',
                text: 'Is a running Schedule II perpetual inventory maintained with daily transaction entries and reconciled balances, with discrepancy investigations signed by two staff?',
                appendixLRef: '21CFR1301.03',
              },
            ],
          },
          {
            label: 'Controlled substance discrepancy: 24-hour investigation policy',
            narrative:
              'Per DEA guidance and best practice, any discrepancy in controlled substance counts must be investigated promptly. Verify: (1) the facility has a written policy requiring investigation of discrepancies within 24 hours of discovery, (2) documentation of the investigation process, findings, and resolution is maintained, (3) unresolved discrepancies are reported to the DEA and to facility leadership, (4) review past discrepancy reports if available — look for patterns (same drug, same shift, same individual). Ask: "What happens when your controlled substance count is off? Walk me through the investigation process. Can I see a recent discrepancy report?"',
            cmsTag: '21CFR1301.02',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'med-med-controlled-substance-discrepancy-controlled-substance-discrepancy-policy',
                text: 'Is there a written policy requiring investigation of controlled substance discrepancies within 24 hours of discovery?',
                appendixLRef: '21CFR1301.02',
              },
              {
                id: 'med-med-controlled-substance-discrepancy-discrepancy-investigation-docs',
                text: 'Are documentation of investigation process, findings, and resolution maintained for past discrepancies?',
                appendixLRef: '21CFR1301.02',
              },
            ],
          },
          {
            label: 'Witnessed wasting of controlled substances',
            narrative:
              'Per DEA regulations and best practice, when a controlled substance is partially administered and the remainder must be wasted (destroyed), the wasting must be witnessed by a second authorized person. Verify: (1) the facility policy requires a witness for all controlled substance wasting, (2) the witness is a licensed or authorized individual (RN, MD, PharmD), (3) documentation for each waste event includes: drug name, quantity wasted, date/time, name of the person wasting, name of the witness, and signatures of both, (4) review 3-5 recent wasting entries for completeness. Ask: "Who witnesses controlled substance wasting? Can I see your wasting documentation?"',
            cmsTag: '21CFR1301.03',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'med-med-witnessed-wasting-controlled-witnessed-wasting-policy',
                text: 'Is there a facility policy that requires a second authorized person to witness all controlled substance wasting events?',
                appendixLRef: '21CFR1301.03',
              },
              {
                id: 'med-med-witnessed-wasting-controlled-wasting-documentation-completeness',
                text: 'Does the documentation for each controlled substance waste event include the drug name, quantity wasted, date/time, names of the person wasting and the witness, and both signatures?',
                appendixLRef: '21CFR1301.03',
              },
            ],
          },
          {
            label: 'DEA-authorized disposal: reverse distributor or registered collector',
            narrative:
              'Per 21 CFR 1317 (DEA Disposal of Controlled Substances), controlled substances must be disposed of through a DEA-authorized method. Verify: (1) the facility uses a DEA-registered reverse distributor or registered collector for controlled substance disposal, (2) a current contract or agreement with the reverse distributor is on file, (3) chain-of-custody documentation exists for all controlled substances sent for destruction. Ask: "How do you dispose of controlled substances? Who is your reverse distributor? Can I see the contract and recent disposal records?"',
            cmsTag: '21CFR1301.03',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'med-med-dea-authorized-disposal-dea-authorized-disposal-reverse-distrib',
                text: 'Is the facility using a DEA-registered reverse distributor or registered collector for the disposal of controlled substances?',
                appendixLRef: '21CFR1301.03',
              },
              {
                id: 'med-med-dea-authorized-disposal-dea-contract-agreement-reverse-distrib',
                text: 'Is a current contract or agreement with the DEA-registered reverse distributor for controlled substance disposal on file at the facility?',
                appendixLRef: '21CFR1301.03',
              },
            ],
          },
          {
            label: 'Expired controlled substances: segregated, destruction documented',
            narrative:
              'Per 21 CFR 1307.21, expired controlled substances must be segregated from active stock and disposed of through DEA-authorized channels using DEA Form 41 (Registrant\'s Inventory of Drugs Surrendered). Verify: (1) expired controlled substances are physically separated from active inventory — ideally in a separately locked container clearly marked "Expired — Awaiting Destruction," (2) a timeline for destruction is documented and followed — expired drugs should not accumulate indefinitely, (3) DEA Form 41 has been submitted and approved for any pending destructions, (4) records of completed destructions are retained. Ask: "Do you have any expired controlled substances on hand? Where are they stored? When is the next scheduled destruction?"',
            cmsTag: '21CFR1301.03',
            evidence: ['document', 'observation'],
            checklistPrompts: [
              {
                id: 'med-med-expired-controlled-substances-expired-substances-segregation',
                text: "Are expired controlled substances physically separated from active inventory in a separately locked container clearly marked 'Expired \u2014 Awaiting Destruction'?",
                appendixLRef: '21CFR1301.03',
              },
              {
                id: 'med-med-expired-controlled-substances-expired-substances-destruction-timeline',
                text: 'Is there a documented timeline for the destruction of expired controlled substances, with a schedule that prevents indefinite accumulation?',
                appendixLRef: '21CFR1301.03',
              },
            ],
          },
          {
            label: 'Temperature monitoring log for medication storage area',
            narrative:
              'Per ASHRAE guidelines and USP standards, medication storage areas must maintain appropriate temperature ranges to ensure drug stability and potency. Verify: (1) a temperature monitoring log or continuous digital monitoring device is present in the medication storage room, (2) room temperature medications are stored at controlled room temperature (68-77°F/20-25°C per USP), (3) the log is current — readings are recorded daily or the digital monitor is functioning, (4) any out-of-range excursions are documented with corrective actions. Photograph the monitoring log or digital display.',
            cmsTag: 'ASHRAE170-2017',
            evidence: ['photo', 'document'],
            checklistPrompts: [
              {
                id: 'med-med-temperature-monitoring-log-temperature-monitoring-log',
                text: 'Is a temperature monitoring log or continuous digital monitoring device present in the medication storage room?',
                appendixLRef: 'ASHRAE170-2017',
              },
              {
                id: 'med-med-temperature-monitoring-log-temperature-log-current',
                text: 'Is the temperature monitoring log current with daily readings or is the digital monitor functioning properly?',
                appendixLRef: 'ASHRAE170-2017',
              },
            ],
          },
        ],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // CLEAN UTILITY
  // ═══════════════════════════════════════════════════════════════════════════
  {
    area: 'Clean Utility',
    areaLabel: 'Clean Utility / Supply Room',
    introduction:
      'The clean utility room stores clean and sterile supplies. Check storage conditions, supply integrity, and separation from contaminated items.',
    walkOrder:
      'Enter and scan the room. Check shelving from top to bottom. Open a few cabinets. Check the floor and ceiling.',
    requiredPhotos: [...FOUR_WALL_BASELINE],
    estimatedMinutes: 10,
    sections: [
      {
        domain: 'IC',
        domainLabel: 'Infection Control',
        items: [
          {
            label: 'Sterile supply storage conditions',
            narrative:
              'Verify proper storage: (1) all sterile supplies are stored off the floor (at least 8-10 inches), (2) supplies are at least 2 inches from outside walls (to prevent condensation contact), (3) at least 18 inches below sprinkler heads, (4) shelving is clean and in good repair (no rust, no exposed wood or cardboard — must be wipeable), (5) no torn or wet packaging on sterile items, (6) supplies are organized so older stock is used first (FIFO — first in, first out). Photograph any storage violations.',
            cmsTag: '§416.51(b)',
            tjcRef: 'IC.02.02.01',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'sterile-supplies-stored-to-protect-sterility',
                text: 'Are sterile supplies stored in a clean designated area so sterility is not compromised?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'spd-sterile-storage-off-floor-walls',
                text: 'Are sterile supplies stored at least 8–10 inches off the floor and at least 2 inches from outside walls to prevent condensation contact?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'spd-sterile-storage-sprinkler-clearance',
                text: 'Are sterile supplies stored at least 18 inches below sprinkler heads?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'spd-sterile-storage-shelving-wipeable',
                text: 'Is shelving clean and in good repair with no rust and no exposed wood or cardboard (surfaces wipeable)?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'spd-sterile-storage-package-integrity-fifo',
                text: 'Are sterile packages free of tears or wet damage, and is stock rotated FIFO so older items are used first?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
          {
            label: 'Clean and sterile separation',
            narrative:
              'Verify that clean utility is truly clean: (1) no dirty/contaminated items stored here, (2) no personal items (food, drinks, personal belongings), (3) no biohazard containers or soiled linen, (4) check for multi-use items that should have been reprocessed (e.g., used but uncleaned reusable items sitting on clean shelves). The door should remain closed.',
            cmsTag: '§416.51(a)',
            tjcRef: 'IC.02.02.01',
            evidence: ['observation'],
            checklistPrompts: [
              {
                id: 'clean-utility-free-of-contamination',
                text: 'Is the clean utility area free of dirty items, biohazard waste, personal food, and contaminated supplies?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'clean-utility-no-personal-items',
                text: 'Is the clean utility area free of personal items (food, drinks, personal belongings)?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'clean-utility-no-used-reusables',
                text: 'Is the clean utility area free of used but uncleaned reusable items that should have been reprocessed?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'clean-utility-door-closed',
                text: 'Is the clean utility room door kept closed to maintain environmental separation?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
        ],
      },
      {
        domain: 'ENV',
        domainLabel: 'Environment of Care',
        items: [
          {
            label: 'Room condition and maintenance',
            narrative:
              'Check the physical condition: (1) ceiling tiles intact with no water stains, (2) walls free of holes, cracks, or damage, (3) flooring intact and cleanable, (4) lighting adequate, (5) temperature and humidity appropriate for supply storage. Note any items stored on the floor, leaning against walls, or stacked precariously. Check for dust accumulation on top shelves — indicates inadequate cleaning schedule.',
            cmsTag: '§416.44(a)(1)',
            tjcRef: 'EC.02.06.01',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'cutil-env-room-condition-maintenance-ceiling-tiles',
                text: 'Are all ceiling tiles intact with no water stains?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'cutil-env-walls-free-of-damage',
                text: 'Are the clean utility walls free of holes, cracks, or damage that would compromise cleanability?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'cutil-env-flooring-cleanable',
                text: 'Is the flooring intact and cleanable with no tears, gouges, or unsealed seams?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'cutil-env-items-not-on-floor',
                text: 'Are stored items off the floor, not leaning against walls, and not stacked precariously?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'cutil-env-room-condition-maintenance-dust-accumulation',
                text: 'Are the top shelves in the clean utility room free of dust accumulation that would indicate an inadequate cleaning schedule?',
                appendixLRef: '416.44(a)(1)',
              },
            ],
          },
        ],
      },
      {
        domain: 'ENV',
        domainLabel: 'Environmental Controls — Clean Utility',
        items: [
          {
            label: 'Positive pressure verification',
            narrative:
              'The clean utility / supply room must maintain positive pressure relative to corridors and adjacent spaces per §416.44(a)(1) and ASHRAE 170. This prevents corridor air and contaminants from entering the clean supply storage area. Perform a tissue test at the door — the tissue should blow outward (away from the clean room). Minimum 4 air changes per hour. Check that temperature and humidity are appropriate for supply storage — excessive heat or humidity can compromise sterile packaging integrity. Ask: "How do you verify this room is under positive pressure?"',
            cmsTag: '§416.44(a)(1), ASHRAE 170',
            tjcRef: 'EC.02.05.01',
            aaahcRef: 'Ch. 8.II — HVAC and Ventilation',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'cutil-env-positive-pressure-verification-positive-pressure-verification',
                text: 'Is a tissue test performed at the door to confirm air is blowing outward from the clean utility room?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'cutil-env-positive-pressure-differential',
                text: 'Are there records showing the room meets the minimum 4 air changes per hour requirement?',
                appendixLRef: '416.44(a)(1)',
              },
            ],
          },
        ],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // SOILED UTILITY
  // ═══════════════════════════════════════════════════════════════════════════
  {
    area: 'Soiled Utility',
    areaLabel: 'Soiled Utility Room',
    introduction:
      'The soiled utility room handles contaminated items. Check for proper containment, biohazard compliance, and separation from clean areas. This room should have negative pressure relative to corridors.',
    walkOrder:
      'Enter carefully — PPE may be required. Check biohazard containers, sharps disposal, linen handling, and waste segregation.',
    requiredPhotos: [...FOUR_WALL_BASELINE],
    estimatedMinutes: 10,
    sections: [
      {
        domain: 'IC',
        domainLabel: 'Infection Control',
        items: [
          {
            label: 'Biohazard waste management',
            narrative:
              'Verify proper biohazard waste handling: (1) red biohazard bags are in rigid containers with lids, (2) bags are not overfilled (should close without forcing), (3) biohazard labels are on all containers, (4) regulated medical waste is separated from regular trash, (5) a spill kit is available. Check that biohazard waste is not stored in areas accessible to patients or visitors. Ask: "Who is your medical waste hauler? How often is waste picked up? Can I see the manifest?"',
            cmsTag: '§416.51(a)',
            tjcRef: 'IC.02.02.01, EC.02.02.01',
            evidence: ['photo', 'observation', 'interview'],
            checklistPrompts: [
              {
                id: 'biohazard-waste-contained-separated',
                text: 'Is regulated medical waste contained, labeled, and separated from regular trash?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'biohazard-waste-rigid-containers-lids',
                text: 'Are red biohazard bags placed in rigid containers with lids?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'biohazard-waste-bags-not-overfilled',
                text: 'Are biohazard bags filled no higher than allows them to close without forcing?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'biohazard-waste-spill-kit-available',
                text: 'Is a biohazard spill kit available and accessible near areas where regulated medical waste is handled?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'biohazard-waste-inaccessible-to-public',
                text: 'Is biohazard waste stored away from areas accessible to patients or visitors?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
          {
            label: 'Sharps container compliance',
            narrative:
              'Check sharps containers throughout the facility but especially here: (1) containers are mounted securely and at appropriate height, (2) not filled above the fill line, (3) lids are functioning (not propped open), (4) containers are labeled with biohazard symbol. Note: sharps containers should be at the point of use in clinical areas, not just in the soiled utility room.',
            cmsTag: '§416.51(a)',
            tjcRef: 'IC.02.02.01',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'sharps-containers-puncture-resistant-not-overfilled',
                text: 'Are sharps disposed of in puncture-resistant containers that are replaced at the fill line?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'sharps-mounted-secure-height',
                text: 'Are sharps containers mounted securely at an appropriate height for the care area?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'sharps-lids-functioning',
                text: 'Are sharps container lids functioning and not propped open?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'sharps-biohazard-labeled-point-of-use',
                text: 'Are sharps containers labeled with the biohazard symbol and present at the point of use in clinical areas (not only in the soiled utility room)?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
        ],
      },
      {
        domain: 'ENV',
        domainLabel: 'Environment of Care',
        items: [
          {
            label: 'Soiled utility room condition',
            narrative:
              'Check: (1) the room has a hands-free handwashing sink, (2) surfaces are cleanable and in good repair, (3) the room has negative air pressure (or at minimum, the door is kept closed), (4) no clean or sterile supplies stored here, (5) soiled linen is contained in covered or closed hampers, (6) an eyewash station is available if chemical processing occurs here. Note any odors or visible contamination on surfaces.',
            cmsTag: '§416.44(a)(1)',
            tjcRef: 'EC.02.06.01',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'sutil-env-soiled-utility-room-soiled-utility-room-hygiene',
                text: 'Is there a hands-free handwashing sink in the soiled utility room?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'sutil-env-soiled-utility-room-soiled-utility-room-containment',
                text: 'Are all soiled linens contained in covered or closed hampers in the soiled utility room?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'sutil-env-surfaces-cleanable-good-repair',
                text: 'Are the surfaces in the soiled utility room cleanable and in good repair with no visible contamination?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'sutil-env-no-clean-sterile-supplies',
                text: 'Is the soiled utility room free of clean or sterile supplies that should be stored elsewhere?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'sutil-env-eyewash-station-if-chemicals',
                text: 'Is an eyewash station available in the soiled utility room if chemical processing occurs there?',
                appendixLRef: '416.44(a)(1)',
              },
            ],
          },
        ],
      },
      {
        domain: 'ENV',
        domainLabel: 'Environmental Controls — Soiled Utility',
        items: [
          {
            label: 'Negative pressure verification',
            narrative:
              'The soiled utility room must maintain negative pressure relative to corridors and adjacent spaces per §416.44(a)(1) and ASHRAE 170. This prevents contaminated air from escaping into clean areas. Perform a tissue test at the closed door — the tissue should pull inward (toward the soiled room), confirming negative pressure. Minimum 10 air changes per hour. The door should remain closed when not in active use. If there is no engineered negative pressure, verify the room has direct exhaust ventilation and that the door self-closes. Ask: "How do you verify this room is under negative pressure? Is the exhaust system on a separate circuit from the clean areas?"',
            cmsTag: '§416.44(a)(1), ASHRAE 170',
            tjcRef: 'EC.02.05.01',
            aaahcRef: 'Ch. 8.II — HVAC and Ventilation',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'sutil-env-negative-pressure-verification-negative-pressure-verification',
                text: 'Is a tissue test performed at the closed door to confirm negative pressure in the soiled utility room?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'sutil-env-negative-pressure-verification-exhaust-system-separate-circuit',
                text: 'Is the exhaust system for the soiled utility room on a separate electrical circuit from the clean areas?',
                appendixLRef: '416.44(a)(1)',
              },
            ],
          },
        ],
      },
      {
        domain: 'WASTE' as SurveyDomain,
        domainLabel: 'Medical Waste Management',
        items: [
          {
            label: 'Medical waste classification signage',
            narrative:
              'Per EPA medical waste regulations and state-specific requirements (RCRA Subtitle C for hazardous, state programs for medical waste), the soiled utility room should have clear signage identifying waste categories and the correct container for each. Check: (1) biohazard waste — red bags in rigid containers with the biohazard symbol, (2) sharps — puncture-resistant containers labeled with biohazard symbol, not overfilled past the fill line, (3) pharmaceutical waste — separate container for non-hazardous pharma waste (typically blue or black), (4) general/non-regulated waste — regular trash containers clearly distinguished from regulated medical waste. Signage should be posted at eye level near waste containers showing which items go in which container. Photograph the signage and container setup.',
            cmsTag: '416.51(a)',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'sutil-waste-medical-waste-classification-medical-waste-signage',
                text: 'Is there clear signage at eye level near each waste container that identifies which waste categories go in which container?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'sutil-waste-medical-waste-classification-medical-waste-container-labels',
                text: 'Are all waste containers labeled with the correct symbols and colors as specified for their waste category, such as red bags with biohazard symbols for biohazard waste?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
          {
            label: 'Segregation compliance: correct containers, no cross-contamination',
            narrative:
              'Per EPA and state medical waste regulations, waste must be properly segregated at the point of generation to prevent cross-contamination and ensure correct treatment and disposal. Inspect each container in the soiled utility room: (1) red biohazard containers should only contain regulated medical waste (blood-soaked items, pathological waste) — not general trash like food wrappers or paper, (2) sharps containers should only contain sharps — no soft waste, (3) pharmaceutical waste containers should not contain infectious waste, (4) no regulated medical waste should be in general trash containers. Lift lids and inspect the top layer of contents where safely possible. Cross-contamination is a common finding. Ask: "How do you train staff on proper waste segregation?"',
            cmsTag: '416.51(a)',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'sutil-waste-segregation-compliance-correct-segregation-compliance-red-containers',
                text: 'Are red biohazard containers in the soiled utility room used exclusively for regulated medical waste, such as blood-soaked items and pathological waste, and do they contain no general trash like food wrappers or paper?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'sutil-waste-segregation-compliance-correct-segregation-compliance-sharps-containers',
                text: 'Are sharps containers in the soiled utility room used exclusively for sharps, and do they contain no soft waste or other non-sharp items?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
          {
            label: 'Waste manifest/pickup records and chain of custody',
            narrative:
              'Per EPA and DOT regulations, regulated medical waste must be tracked from generation through final disposal via manifests or tracking documents. Verify: (1) the waste hauler has a current license/permit for medical waste transportation in your state, (2) a pickup schedule is established and followed — check the last several pickup dates, (3) waste manifests or tracking documents are maintained showing: date of pickup, quantity/weight, hauler name and permit number, destination treatment facility, (4) signed chain-of-custody documentation is retained for the required period (typically 3 years). Ask: "Who is your medical waste hauler? Can I see the contract and recent manifests? How often is waste picked up?"',
            cmsTag: '416.51(a)',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'sutil-waste-waste-manifest-pickup-waste-manifests-chain-custody',
                text: "Is the waste hauler's current license/permit for medical waste transportation in the state on file and valid?",
                appendixLRef: '416.51(a)',
              },
              {
                id: 'sutil-waste-waste-manifest-pickup-pickup-schedule-compliance',
                text: 'Are pickup dates for medical waste consistent with the established pickup schedule and documented in the waste manifests?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
          {
            label: 'EPA-registered disinfectants: registration numbers on containers',
            narrative:
              'Per EPA FIFRA requirements, all disinfectants used in the soiled utility room must be EPA-registered and used according to label directions. Check: (1) each disinfectant container displays a visible EPA registration number (format: EPA Reg. No. XXXXX-XX), (2) the product is approved for healthcare/hospital use, (3) the product is effective against the organisms of concern (look for claims against bloodborne pathogens, TB, and common healthcare-associated organisms on the label). Photograph the EPA registration numbers on product containers.',
            cmsTag: '416.51(a)',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'sutil-waste-epa-registered-disinfectants-epa-registration-numbers',
                text: "Is the EPA registration number on the disinfectant container visible and formatted as 'EPA Reg. No. XXXXX-XX'?",
                appendixLRef: '416.51(a)',
              },
              {
                id: 'sutil-waste-epa-registered-disinfectants-epa-registration-approval',
                text: 'Does the disinfectant container display an EPA registration number that is approved for healthcare/hospital use?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
          {
            label: 'Contact time reference at point of use',
            narrative:
              'Per EPA label requirements, disinfectants must remain wet on the surface for the full manufacturer-specified contact time (dwell time) to be effective. Verify: (1) a contact time reference chart or card is posted at the point of use — near the disinfectant dispenser or on the wall where cleaning products are stored, (2) the posted contact time matches the product label, (3) staff can verbalize the correct contact time when asked. Ask a staff member: "What disinfectant do you use in this room? What is the required contact time? Where can you find that information?"',
            cmsTag: '416.51(a)',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'sutil-waste-contact-time-reference-contact-time-reference',
                text: 'Is a contact time reference chart or card posted at the point of use, such as near the disinfectant dispenser or on the wall where cleaning products are stored?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'sutil-waste-contact-time-reference-contact-time-accuracy',
                text: 'Does the posted contact time for disinfectants match the manufacturer-specified time listed on the product label?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
        ],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // MECHANICAL
  // ═══════════════════════════════════════════════════════════════════════════
  {
    area: 'Mechanical',
    areaLabel: 'Mechanical / Electrical / HVAC Room',
    introduction:
      'The mechanical room houses critical building systems. Focus on electrical panel access, medical gas storage, HVAC maintenance records, and fire safety.',
    walkOrder:
      'Check the door (should be labeled and accessible). Enter and check electrical panels, medical gas systems, HVAC equipment, and emergency generator if present.',
    requiredPhotos: [...FOUR_WALL_BASELINE],
    estimatedMinutes: 15,
    sections: [
      {
        domain: 'LSC',
        domainLabel: 'Life Safety Code',
        items: [
          {
            label: 'Electrical panel clearance and labeling',
            narrative:
              'Check electrical panels: (1) 36 inches of clear space in front of every panel (NFPA 70 requirement), (2) panel doors are closed and not blocked, (3) circuit directory is current, complete, and legible — every breaker should be labeled, (4) no storage within the required clearance zone, (5) red floor tape may mark the clearance zone. Photograph any obstructed panels or missing/illegible directories.',
            cmsTag: '§416.44(b), NFPA 70',
            tjcRef: 'LS.02.01.20',
            evidence: ['photo'],
            checklistPrompts: [
              {
                id: 'mech-lsc-electrical-panel-clearance-electrical-panel-clearance',
                text: 'Is there at least 36 inches of clear space in front of every electrical panel?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'mech-lsc-electrical-panel-closed-not-blocked',
                text: 'Are electrical panel doors closed and not blocked by stored items or equipment?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'mech-lsc-electrical-panel-no-storage-in-zone',
                text: 'Is the required clearance zone (typically marked with red floor tape) kept free of storage?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'mech-lsc-electrical-panel-clearance-panel-labeling-completeness',
                text: 'Is the circuit directory for the electrical panel current, complete, and legible with every breaker labeled?',
                appendixLRef: '416.44(b)',
              },
            ],
          },
          {
            label: 'Medical gas storage and safety',
            narrative:
              'If medical gases are stored here (bulk or cylinder): (1) full and empty cylinders must be separated and secured (chained or in racks), (2) oxidizing gases (O2, N2O) must be stored at least 20 feet from flammable gases or separated by a fire barrier, (3) "No Smoking" and "Oxygen" warning signs must be posted, (4) cylinders must not be stored near heat sources, (5) the storage area must be ventilated. Check main medical gas shutoff valves — they should be labeled and accessible.',
            cmsTag: '§416.44(b), NFPA 99',
            tjcRef: 'EC.02.05.01',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'mech-lsc-medical-gas-storage-medical-gas-storage-separation',
                text: 'Are full and empty medical gas cylinders separated and secured (chained or in racks) in the storage area?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'mech-lsc-medical-gas-oxidizer-separation',
                text: 'Are oxidizing gases (oxygen, nitrous oxide) stored at least 20 feet from flammable gases, or separated by a fire barrier?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'mech-lsc-medical-gas-no-smoking-signage',
                text: 'Are "No Smoking" and "Oxygen" warning signs posted at the medical gas storage area?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'mech-lsc-medical-gas-no-heat-source',
                text: 'Are medical gas cylinders stored away from heat sources, with the main shutoff valves labeled and accessible?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'mech-lsc-medical-gas-storage-medical-gas-storage-ventilation',
                text: 'Is the medical gas storage area properly ventilated as required by the safety regulations?',
                appendixLRef: '416.44(b)',
              },
            ],
          },
          {
            label: 'Emergency power and generator',
            narrative:
              'If the facility has an emergency generator: (1) ask for the generator test log — it must be tested at least weekly under load for 30 minutes per NFPA 110, (2) monthly load testing records, (3) annual 4-hour load test, (4) fuel level is adequate, (5) transfer switch operates automatically. If no generator, ask: "What is your plan for power failure during a surgical case? How do you ensure life safety systems remain operational?" Document the answer.',
            cmsTag: '§416.44(b), NFPA 99/110',
            tjcRef: 'EC.02.05.03',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'mech-lsc-emergency-power-generator-emergency-generator-test-log',
                text: 'Is the emergency generator tested at least weekly under load for 30 minutes, with records maintained in a test log?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'mech-lsc-emergency-power-monthly-load-test',
                text: 'Are monthly load-testing records maintained for the emergency generator?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'mech-lsc-emergency-power-annual-4hr-test',
                text: 'Is an annual 4-hour load test documented for the emergency generator per NFPA 110?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'mech-lsc-emergency-power-fuel-adequate',
                text: 'Is the generator fuel level adequate for the required run time with documented refill schedule?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'mech-lsc-emergency-power-generator-generator-transfer-switch',
                text: 'Does the emergency generator\'s transfer switch operate automatically to switch power during a failure?',
                appendixLRef: '416.44(b)',
              },
            ],
          },
        ],
      },
      {
        domain: 'ENV',
        domainLabel: 'Environmental Controls — HVAC Documentation',
        items: [
          {
            label: 'HVAC air balancing report',
            narrative:
              'Request the most recent HVAC air balancing report. Per §416.44(a)(1), the facility must maintain proper ventilation throughout. The balancing report should document: (1) air changes per hour (ACH) for each area — OR must show 15 ACH with 3 fresh, SPD decontam 6 ACH, recovery areas 6 ACH, (2) pressure relationships for each room — OR positive, SPD decontam negative, soiled utility negative, clean utility positive, (3) the report should be performed at commissioning and after any HVAC system modification, (4) some accrediting bodies expect periodic re-balancing. Ask: "When was the last air balancing report done? Who performed it? Were any deficiencies identified?"',
            cmsTag: '§416.44(a)(1), ASHRAE 170',
            tjcRef: 'EC.02.05.01',
            aaahcRef: 'Ch. 8.II — HVAC and Ventilation',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'mech-env-hvac-air-balancing-hvac-air-balancing-report',
                text: 'Was the last HVAC air balancing report completed within the required timeframe and are any deficiencies documented?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'mech-env-hvac-balancing-currency',
                text: 'Does the most recent HVAC air balancing report document the required air changes per hour for each area, including the operating room?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'mech-env-hvac-balancing-ach-area-breakdown',
                text: 'Does the balancing report document ACH for each area type (OR ≥15 with ≥3 outdoor, SPD decontam ≥6, recovery ≥6)?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'mech-env-hvac-balancing-pressure-relationships',
                text: 'Does the report document pressure relationships (OR positive, SPD decontam negative, soiled utility negative, clean utility positive)?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'mech-env-hvac-balancing-after-modification',
                text: 'Was air balancing performed at commissioning and re-performed after any HVAC system modification?',
                appendixLRef: '416.44(a)(1)',
              },
            ],
          },
          {
            label: 'HVAC preventive maintenance and filter records',
            narrative:
              'Request HVAC preventive maintenance records. Verify: (1) filters are changed on schedule — OR air handling units should use HEPA or high-efficiency filters, (2) belt inspections and replacements are logged, (3) coils are cleaned regularly, (4) outside air intakes are not located near exhaust vents, loading docks, dumpsters, or cooling towers, (5) condensate drain pans are clean and draining properly (standing water breeds bacteria). Ask: "When were the HVAC filters last changed? What filter efficiency rating do you use for OR supply air?"',
            cmsTag: '§416.44(a)(1)',
            tjcRef: 'EC.02.05.01',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'mech-env-hvac-preventive-maintenance-hvac-preventive-maintenance-records',
                text: 'Are HVAC preventive maintenance records available for review, including filter change schedules and belt inspections?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'mech-env-hvac-coils-cleaned',
                text: 'Are HVAC coils cleaned on a regular schedule per facility policy?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'mech-env-hvac-outside-air-intake-separation',
                text: 'Are outside air intakes located away from exhaust vents, loading docks, dumpsters, and cooling towers?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'mech-env-hvac-condensate-drain-pans',
                text: 'Are condensate drain pans clean and draining properly, with no standing water that could support bacterial growth?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'mech-env-hvac-preventive-maintenance-hvac-filter-efficiency',
                text: 'Is the filter efficiency rating for OR supply air at least HEPA or high-efficiency as required?',
                appendixLRef: '416.44(a)(1)',
              },
            ],
          },
          {
            label: 'Water management program — Legionella prevention',
            narrative:
              'Per §416.44(a)(1) and CMS Survey & Certification memo S&C 17-30, healthcare facilities must have a water management program to reduce the risk of Legionella and other waterborne pathogens. Request the facility\'s written water management plan. Verify: (1) a water management team has been identified, (2) a risk assessment has been performed — mapping the water system, identifying dead legs, low-flow fixtures, and areas where water stagnates, (3) temperature controls are in place — hot water above 120°F at the heater, cold water below 68°F, (4) a flushing protocol exists for low-use or dead-end fixtures (e.g., weekly flushing of unused sinks/showers), (5) testing records — has the facility performed Legionella culture testing? How often?, (6) decorative fountains or water features are prohibited or have specific maintenance protocols. Ask: "Do you have a water management plan? Who is responsible for it? Have you ever tested for Legionella?"',
            cmsTag: '§416.44(a)(1)',
            tjcRef: 'EC.02.05.01',
            aaahcRef: 'Ch. 8 — Facilities and Environment',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'mech-env-water-management-program-water-management-plan',
                text: 'Is there a written water management plan in place to reduce the risk of Legionella and other waterborne pathogens?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'mech-env-water-management-program-legionella-testing-records',
                text: 'Has the facility performed Legionella culture testing in the past 12 months?',
                appendixLRef: '416.44(a)(1)',
              },
            ],
          },
          {
            label: 'ASHRAE 170 compliance: air balancing report (ACH and pressure)',
            narrative:
              'Per ASHRAE Standard 170 — Ventilation of Health Care Facilities, the facility must maintain documented air balancing for all critical areas. Request the air balancing report and verify: (1) air changes per hour (ACH) are documented for each area — ORs ≥15, PACU ≥6, SPD decontam ≥6, SPD clean ≥4, soiled utility ≥10, (2) pressure relationships are documented — ORs positive, soiled utility negative, SPD decontam negative, clean utility positive, (3) the report was performed by a qualified HVAC contractor or engineer, (4) the date of the last report — it should be current or within the last 1-2 years. Ask: "When was the last air balancing performed? Were any deficiencies found? How were they corrected?"',
            cmsTag: 'ASHRAE170-2017',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'mech-env-balance-air-balancing-report',
                text: 'Is there a current air balance report for the operating room that confirms it meets the minimum 15 total air changes per hour with at least 3 outdoor air changes per ASHRAE 170?',
                appendixLRef: 'ASHRAE170-2017',
              },
              {
                id: 'mech-env-balance-ach-all-areas',
                text: 'Does the balancing report document ACH for all critical areas (PACU ≥6, SPD clean ≥4, soiled utility ≥10)?',
                appendixLRef: 'ASHRAE170-2017',
              },
              {
                id: 'mech-env-balance-qualified-engineer',
                text: 'Was the air balancing report performed by a qualified HVAC contractor or engineer, with credentials documented?',
                appendixLRef: 'ASHRAE170-2017',
              },
              {
                id: 'mech-env-balance-report-date-current',
                text: 'Is the last air balancing report dated within the past 1–2 years, with any identified deficiencies corrected?',
                appendixLRef: 'ASHRAE170-2017',
              },
            ],
          },
          {
            label: 'Pressure differential documentation',
            narrative:
              'Per ASHRAE Standard 170, specific pressure relationships must be maintained throughout the facility: operating rooms must be positive, soiled utility rooms must be negative, clean utility must be positive, and SPD decontamination must be negative relative to adjacent spaces. Verify: (1) the air balancing report documents pressure relationships for each critical space, (2) if continuous monitoring is installed, check that gauges or indicators are functioning and reading correctly, (3) any deviations from required pressure relationships should trigger corrective maintenance. Ask: "How do you verify pressure relationships are maintained on an ongoing basis — daily monitoring, periodic testing, or continuous gauges?"',
            cmsTag: 'ASHRAE170-2017',
            evidence: ['document', 'observation'],
            checklistPrompts: [
              {
                id: 'mech-env-pressure-pressure-differential-docs',
                text: 'Is there documentation showing the operating room maintains a positive pressure relationship to adjacent spaces?',
                appendixLRef: 'ASHRAE170-2017',
              },
              {
                id: 'mech-env-pressure-continuous-monitoring',
                text: 'If continuous pressure monitoring is installed, are gauges or indicators functioning and reading correctly for each critical space?',
                appendixLRef: 'ASHRAE170-2017',
              },
              {
                id: 'mech-env-pressure-deviation-corrective',
                text: 'Are deviations from required pressure relationships escalated to corrective maintenance with documented closure?',
                appendixLRef: 'ASHRAE170-2017',
              },
            ],
          },
          {
            label: 'ASHRAE 188 Legionella risk assessment',
            narrative:
              'Per ASHRAE Standard 188 — Legionellosis: Risk Management for Building Water Systems, healthcare facilities must conduct a documented risk assessment for Legionella. Verify: (1) a written risk assessment exists that maps the building water system and identifies conditions conducive to Legionella growth — dead legs, low-flow areas, water temperatures between 77-113°F (25-45°C), sediment, and biofilm, (2) triggers for response are identified (e.g., positive culture results, clinical cases, system changes), (3) the risk assessment is reviewed and updated periodically or after any significant changes to the water system. Ask: "Has a Legionella risk assessment been performed per ASHRAE 188? When was it last updated? Who conducted it?"',
            cmsTag: 'ASHRAE170-2017',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'mech-env-ashrae-188-legionella-risk-assessment-exists',
                text: 'Is there a written Legionella risk assessment document that maps the building water system and identifies conditions conducive to Legionella growth?',
                appendixLRef: 'ASHRAE170-2017',
              },
              {
                id: 'mech-env-ashrae-188-legionella-risk-assessment-updated',
                text: 'Has the Legionella risk assessment been reviewed and updated within the past 12 months or after any significant changes to the water system?',
                appendixLRef: 'ASHRAE170-2017',
              },
            ],
          },
          {
            label: 'Hot water storage and return temperatures for Legionella control',
            narrative:
              'Per ASHRAE 188 and CDC guidelines for Legionella prevention, hot water system temperatures are a primary control measure. Verify: (1) hot water storage (at the water heater) is maintained above 120°F (49°C) — Legionella cannot survive above 122°F, (2) hot water return temperature is maintained above 110°F (43°C) to prevent colonization in the recirculation loop, (3) temperature logs are maintained and current. Check the water heater thermostat setting and review the temperature log for the last 30 days. Ask: "What is your hot water storage temperature? Do you monitor return loop temperature?"',
            cmsTag: 'ASHRAE170-2017',
            evidence: ['document', 'observation'],
            checklistPrompts: [
              {
                id: 'mech-env-hot-water-storage-hot-water-storage-temp',
                text: 'Is the hot water storage temperature at the water heater maintained above 120°F (49°C) to prevent Legionella growth?',
                appendixLRef: 'ASHRAE170-2017',
              },
              {
                id: 'mech-env-hot-water-storage-hot-water-return-temp',
                text: 'Is the hot water return temperature in the recirculation loop maintained above 110°F (43°C) with current temperature logs available?',
                appendixLRef: 'ASHRAE170-2017',
              },
            ],
          },
          {
            label: 'Cooling tower/decorative fountain program',
            narrative:
              'Per ASHRAE 188, if the facility has a cooling tower, evaporative condenser, or decorative fountain, a specific maintenance and water treatment program must be in place, as these are high-risk sources for Legionella aerosolization. Verify: (1) cooling tower water treatment program exists with documented chemical treatment schedule, (2) cooling tower is cleaned and inspected per manufacturer schedule, (3) Legionella culture testing of the cooling tower water is performed periodically, (4) decorative fountains (if present) have a maintenance program or have been removed. If no cooling tower or fountain is present, document that finding. Ask: "Does this facility have a cooling tower or any decorative water features?"',
            cmsTag: 'ASHRAE170-2017',
            evidence: ['document', 'interview'],
            checklistPrompts: [
              {
                id: 'mech-env-cooling-tower-decorative-cooling-tower-program',
                text: 'Is there a documented chemical treatment schedule for the cooling tower water treatment program?',
                appendixLRef: 'ASHRAE170-2017',
              },
              {
                id: 'mech-env-cooling-tower-decorative-legionella-testing',
                text: 'Is Legionella culture testing performed periodically on the cooling tower water?',
                appendixLRef: 'ASHRAE170-2017',
              },
            ],
          },
          {
            label: 'Potable water test results: EPA SDWA compliance',
            narrative:
              'Per the EPA Safe Drinking Water Act (SDWA) and state drinking water regulations, the facility\'s potable water supply must meet EPA primary drinking water standards. Verify: (1) annual water quality testing has been performed — check for coliform bacteria, lead, copper, and other contaminants as required, (2) test results are on file and meet EPA maximum contaminant levels (MCLs), (3) if the facility is served by a public water system, the annual Consumer Confidence Report (water quality report) should be available. Ask: "Do you test your potable water? Can I see the most recent test results?"',
            cmsTag: 'AAMIST108-2014',
            evidence: ['document'],
            checklistPrompts: [
              {
                id: 'mech-env-potable-water-test-potable-water-testing',
                text: 'Is annual water quality testing performed for contaminants such as coliform bacteria, lead, and copper?',
                appendixLRef: 'AAMIST108-2014',
              },
              {
                id: 'mech-env-potable-water-test-test-results-on-file',
                text: 'Are the most recent potable water test results on file and do they meet the EPA maximum contaminant levels?',
                appendixLRef: 'AAMIST108-2014',
              },
            ],
          },
          {
            label: 'Hot water temperature at patient outlets: anti-scald and anti-Legionella',
            narrative:
              'Per EPA and state plumbing code requirements, hot water at patient-accessible outlets must be maintained between 110-120°F (43-49°C) to balance anti-scald protection with Legionella prevention. Verify: (1) thermostatic mixing valves (TMVs) are installed on patient-accessible fixtures to prevent scalding while allowing higher temperatures in the distribution system for Legionella control, (2) hot water temperature at patient outlets has been measured and documented — it should not exceed 120°F at the fixture, (3) if you have a digital thermometer, spot-check a patient sink. Ask: "What temperature is your hot water set to at patient sinks? Do you have thermostatic mixing valves installed?"',
            cmsTag: 'AAMIST108-2014',
            evidence: ['observation', 'document'],
            checklistPrompts: [
              {
                id: 'mech-env-hot-water-temperature-hot-water-anti-scald',
                text: 'Are thermostatic mixing valves installed on all patient-accessible hot water fixtures to prevent scalding?',
                appendixLRef: 'AAMIST108-2014',
              },
              {
                id: 'mech-env-hot-water-temperature-hot-water-legionella-control',
                text: 'Has the hot water temperature at patient outlets been measured and documented to confirm it does not exceed 120°F at the fixture?',
                appendixLRef: 'AAMIST108-2014',
              },
            ],
          },
        ],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // CORRIDOR
  // ═══════════════════════════════════════════════════════════════════════════
  {
    area: 'Corridor',
    areaLabel: 'Corridors and Hallways',
    introduction:
      'Walk every corridor in the facility. Corridors are egress paths and must be clear. This is also where you catch wall damage, improper storage, blocked fire doors, and missing signage.',
    walkOrder:
      'Walk every hallway from end to end. Check both walls, ceiling, and floor. Note fire doors, pull stations, extinguishers, and any equipment or supplies stored in corridors.',
    requiredPhotos: [
      { label: 'Corridor — end to end (each direction)', description: 'Stand at one end of each corridor and photograph the full length. Repeat from the other end. Shows egress clearance, signage, and overall condition.', angle: 'entry', minCount: 2 },
    ],
    estimatedMinutes: 15,
    sections: [
      {
        domain: 'LSC',
        domainLabel: 'Life Safety Code',
        items: [
          {
            label: 'Corridor clearance and egress path',
            narrative:
              'Walk every corridor and verify: (1) corridors are clear of obstructions — no equipment, carts, chairs, or supplies blocking the path, (2) minimum corridor width is maintained (typically 44 inches for patient transport, 28 inches for others), (3) fire doors are not propped open (unless held by magnetic hold-open devices tied to the fire alarm system), (4) exit signs are illuminated at every turn and intersection, (5) fire pull stations are visible and accessible (not blocked by equipment), (6) no items stored within 18 inches of the corridor ceiling. Photograph any obstructions.',
            cmsTag: '§416.44(b), NFPA 101',
            tjcRef: 'LS.02.01.20',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'corr-lsc-corridor-clearance-egress-corridor-obstructions',
                text: 'Are all corridors free of obstructions such as equipment, carts, chairs, or supplies that could block the egress path?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'corr-lsc-corridor-clearance-egress-fire-doors-propped-open',
                text: 'Are all fire doors either fully closed and latched, or held open only by approved magnetic hold-open devices connected to the fire alarm system?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'corr-lsc-corridor-minimum-width',
                text: 'Is the minimum corridor width maintained (44 inches where patient transport occurs, 28 inches elsewhere)?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'corr-lsc-corridor-exit-signs-illuminated',
                text: 'Are exit signs illuminated at every corridor turn and intersection?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'corr-lsc-corridor-pull-stations-accessible',
                text: 'Are fire pull stations visible and accessible, not blocked by equipment?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'corr-lsc-corridor-ceiling-clearance',
                text: 'Are stored items kept at least 18 inches below the corridor ceiling?',
                appendixLRef: '416.44(b)',
              },
            ],
          },
          {
            label: 'Fire door and smoke barrier integrity',
            narrative:
              'Check every fire door in the corridor: (1) door closes completely and latches on its own (test by opening and releasing), (2) no wedges, doorstops, or non-approved hold-open devices, (3) no gaps greater than 1/8 inch between door and frame, (4) fire-rated doors are labeled (look for the metal tag on the hinge edge), (5) no penetrations through fire/smoke walls that are unsealed — check around pipes, conduit, and ductwork that pass through rated walls. Photograph any propped-open fire doors or unsealed penetrations.',
            cmsTag: '§416.44(b), NFPA 101',
            tjcRef: 'LS.02.01.10',
            evidence: ['photo'],
            checklistPrompts: [
              {
                id: 'corr-lsc-fire-door-smoke-fire-door-closure',
                text: 'Is the fire door in the corridor able to close completely and latch on its own when opened and released?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'corr-lsc-fire-door-smoke-fire-door-gaps',
                text: 'Are gaps between the fire door and its frame 1/8 inch or less with no excessive clearance present?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'corr-lsc-fire-door-no-wedges',
                text: 'Are fire doors free of wedges, doorstops, or non-approved hold-open devices?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'corr-lsc-fire-door-rated-label-present',
                text: 'Are fire-rated doors labeled with the required metal tag on the hinge edge?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'corr-lsc-smoke-barrier-penetrations-sealed',
                text: 'Are penetrations through fire or smoke walls (around pipes, conduit, ductwork) properly sealed with fire-rated materials?',
                appendixLRef: '416.44(b)',
              },
            ],
          },
        ],
      },
      {
        domain: 'ENV',
        domainLabel: 'Environment of Care',
        items: [
          {
            label: 'Wall, floor, and ceiling condition',
            narrative:
              'As you walk corridors, note: (1) wall damage — scuff marks, holes, bumper damage from cart traffic, peeling paint, tape repairs (tape is not an acceptable repair — it compromises cleanability), (2) ceiling tiles — stains, missing tiles, tiles that don\'t sit flat, (3) flooring — cracked tiles, loose seams, tripping hazards, worn areas. Each of these can be an infection control issue (damaged surfaces cannot be properly cleaned) and an environment of care deficiency. Photograph significant damage.',
            cmsTag: '§416.44(a)(1)',
            tjcRef: 'EC.02.06.01',
            evidence: ['photo'],
            checklistPrompts: [
              {
                id: 'corr-env-wall-floor-ceiling-wall-damage',
                text: 'Are corridor walls free from scuff marks, holes, bumper damage from cart traffic, peeling paint, and tape repairs that would compromise cleanability?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'corr-env-wall-floor-ceiling-ceiling-tiles',
                text: 'Are ceiling tiles present, sitting flat, and free from stains or missing tiles?',
                appendixLRef: '416.44(a)(1)',
              },
            ],
          },
          {
            label: 'Hazardous material signage (HAZMAT/SDS)',
            narrative:
              'Check for Safety Data Sheet (SDS) availability. Per OSHA, SDSs must be accessible to all employees for every hazardous chemical in the workplace. Common locations: near housekeeping closets, SPD, or nurse stations. Ask: "Where do you keep your SDSs? Can you show me?" Verify they are readily available (not locked in an office that may be closed after hours). Check for secondary container labeling — all spray bottles and containers with chemicals transferred from original containers must be labeled with contents and hazard warnings.',
            cmsTag: '§416.44(a)',
            tjcRef: 'EC.02.02.01',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'corr-env-hazardous-material-signage-hazmat-sds-availability',
                text: 'Are Safety Data Sheets (SDSs) for all hazardous chemicals in the workplace readily available to employees, including during after-hours periods?',
                appendixLRef: '416.44(a)',
              },
              {
                id: 'corr-env-hazardous-material-signage-secondary-container-labeling',
                text: 'Are all secondary containers, such as spray bottles or transferred chemical containers, properly labeled with the contents and hazard warnings?',
                appendixLRef: '416.44(a)',
              },
            ],
          },
        ],
      },
      {
        domain: 'IC' as SurveyDomain,
        domainLabel: 'Infection Control — Hand Hygiene',
        items: [
          {
            label: 'Hand hygiene compliance monitoring',
            narrative:
              'Per CDC Hand Hygiene in Healthcare Settings guidelines, alcohol-based hand rub (ABHR) dispensers should be readily accessible at key locations throughout corridors — at the entrance to every patient care area, at nursing stations, and at other high-traffic transition points. Walk the corridors and verify: (1) ABHR dispensers are mounted at strategic locations and are not empty, (2) dispensers are functioning (pump a small amount to test), (3) if the facility tracks hand hygiene compliance rates, ask to see the most recent data — is the compliance rate posted for staff visibility?, (4) signage encouraging hand hygiene is visible in corridors near patient care area entrances. Ask: "Do you perform hand hygiene compliance audits? What is your current compliance rate? Is it posted?"',
            cmsTag: '416.51(a)',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'corridor-hand-hygiene-accessible',
                text: 'Are hand hygiene supplies readily accessible at entrances to patient care areas and other key corridor locations?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'hand-hygiene-compliance-monitored',
                text: 'Does the ASC regularly assess hand hygiene compliance?',
                appendixLRef: '416.51(b)',
              },
            ],
          },
        ],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // RESTROOM
  // ═══════════════════════════════════════════════════════════════════════════
  {
    area: 'Restroom',
    areaLabel: 'Restrooms (Patient and Staff)',
    introduction:
      'Check at least one patient restroom and one staff restroom. Restrooms reveal a lot about infection control culture and facility maintenance.',
    walkOrder:
      'Enter restroom. Check handwashing, supplies, cleanliness, safety features, and ADA compliance.',
    requiredPhotos: [...FOUR_WALL_BASELINE],
    estimatedMinutes: 5,
    sections: [
      {
        domain: 'IC',
        domainLabel: 'Infection Control',
        items: [
          {
            label: 'Hand hygiene supplies and signage',
            narrative:
              'Verify: (1) soap dispenser is present and filled, (2) paper towels or hand dryer available, (3) hand hygiene signage posted (proper handwashing technique), (4) no cloth towel dispensers (single-use only), (5) trash receptacle present and not overflowing. In patient restrooms used post-operatively, check for call button or pull cord accessibility.',
            cmsTag: '§416.51(a)',
            tjcRef: 'IC.02.01.01',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'restroom-hand-hygiene-supplies-available',
                text: 'Are restroom soap, drying supplies, trash receptacles, and hand hygiene signage available and functional?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'restroom-single-use-dispensers-only',
                text: 'Are all hand-drying dispensers single-use paper or air dryers, with no cloth towel dispensers present?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'restroom-trash-not-overflowing',
                text: 'Are trash receptacles serviced so they are not overflowing?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'restroom-post-op-call-device',
                text: 'In patient restrooms used post-operatively, is a call button or pull cord accessible for the patient to summon staff?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
        ],
      },
      {
        domain: 'ENV',
        domainLabel: 'Environment of Care',
        items: [
          {
            label: 'Restroom safety and ADA compliance',
            narrative:
              'Check: (1) grab bars are present and securely mounted in patient restrooms, (2) floor is non-slip and dry, (3) lighting is adequate, (4) no slip/trip hazards, (5) door is operable from outside in case of emergency (not deadbolted from inside without override), (6) general cleanliness — check under and behind fixtures. In patient restrooms, verify a call device (cord or button) is accessible from the floor and the toilet.',
            cmsTag: '§416.44(a)(1)',
            tjcRef: 'EC.02.06.01',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'rr-env-restroom-safety-ada-grab-bars-present',
                text: 'Are grab bars present and securely mounted in all patient restrooms?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'rr-env-restroom-safety-ada-call-device-accessible',
                text: 'Is a call device (cord or button) accessible from the floor and the toilet in patient restrooms?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'rr-env-restroom-floor-non-slip',
                text: 'Is the restroom floor non-slip, dry, and free of slip or trip hazards?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'rr-env-restroom-lighting-adequate',
                text: 'Is restroom lighting adequate for safe patient use?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'rr-env-restroom-door-emergency-override',
                text: 'Is the restroom door operable from outside in case of emergency (not deadbolted without an override)?',
                appendixLRef: '416.44(a)(1)',
              },
            ],
          },
        ],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // STAFF LOUNGE
  // ═══════════════════════════════════════════════════════════════════════════
  {
    area: 'Staff Lounge',
    areaLabel: 'Staff Lounge / Break Room',
    introduction:
      'A quick check of the break room is useful. You are verifying separation between food/personal areas and clinical areas, and checking for any improper storage.',
    walkOrder:
      'Quick walk-through. Check refrigerator, counters, and any storage areas.',
    requiredPhotos: [...FOUR_WALL_BASELINE],
    estimatedMinutes: 5,
    sections: [
      {
        domain: 'IC',
        domainLabel: 'Infection Control',
        items: [
          {
            label: 'Separation of food and clinical items',
            narrative:
              'Verify: (1) no medications, specimens, or clinical supplies stored in the break room refrigerator or anywhere in the break room, (2) the break room refrigerator is labeled "No Medications or Specimens", (3) no clinical equipment or supplies stored in the break room, (4) staff are not wearing PPE (gloves, gowns) in the break room, (5) scrub attire is laundered through the organization or a health care-accredited laundry rather than at home. This is a common finding — staff sometimes store medications in the nearest refrigerator.',
            cmsTag: '§416.51(a), §416.48(a)',
            tjcRef: 'IC.02.02.01',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'food-separated-from-clinical-items',
                text: 'Are food and personal items separated from medications, specimens, and clinical supplies?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'no-medications-in-break-room',
                text: 'Are medications absent from the break room refrigerator and staff food areas?',
                appendixLRef: '416.48(a)',
              },
              {
                id: 'scrubs-not-home-laundered',
                text: 'Are scrubs laundered at a health care-accredited facility or by the health care organization rather than at home?',
                appendixLRef: '416.51(b)',
              },
            ],
          },
          {
            label: 'Hand hygiene: sink or hand sanitizer available',
            narrative:
              'Per CDC Hand Hygiene in Healthcare Settings guidelines, hand hygiene facilities should be available wherever staff transition between clinical and non-clinical areas. Verify: (1) a handwashing sink with soap and paper towels is present in or immediately adjacent to the break room, (2) alternatively, an alcohol-based hand rub dispenser is available at the break room entrance, (3) a hand hygiene compliance reminder is posted — reminding staff to perform hand hygiene when returning to clinical duties from break. Ask: "Where do staff wash their hands before returning to patient care from the break room?"',
            cmsTag: '416.51(a)',
            evidence: ['observation', 'interview'],
            checklistPrompts: [
              {
                id: 'staff-lounge-hand-hygiene-available',
                text: 'Is handwashing or alcohol-based hand rub available where staff transition from the lounge back to clinical duties?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'staff-lounge-handwash-sink-supplies',
                text: 'Is a handwashing sink with soap and paper towels present in or immediately adjacent to the break room?',
                appendixLRef: '416.51(a)',
              },
              {
                id: 'staff-lounge-hand-hygiene-reminder',
                text: 'Is a hand-hygiene reminder posted to prompt staff to perform hand hygiene before returning to clinical duties?',
                appendixLRef: '416.51(a)',
              },
            ],
          },
        ],
      },
      {
        domain: 'ENV',
        domainLabel: 'Environment of Care',
        items: [
          {
            label: 'General condition',
            narrative:
              'Quick check: (1) room is clean and orderly, (2) no pest evidence, (3) handwashing sink available, (4) food waste properly contained. Note but don\'t overweight — this is a staff area, not a patient area. Focus your time on clinical spaces.',
            cmsTag: '§416.44(a)(1)',
            evidence: ['observation'],
            checklistPrompts: [
              {
                id: 'lounge-env-general-condition-room-cleanliness',
                text: 'Is the room clean and orderly, with no visible dirt, debris, or clutter?',
                appendixLRef: '416.44(a)(1)',
              },
              {
                id: 'lounge-env-general-condition-pest-evidence',
                text: 'Are there any signs of pest infestation, such as droppings, gnaw marks, or insect carcasses?',
                appendixLRef: '416.44(a)(1)',
              },
            ],
          },
        ],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // EXTERIOR
  // ═══════════════════════════════════════════════════════════════════════════
  {
    area: 'Exterior',
    areaLabel: 'Exterior and Grounds',
    introduction:
      'Walk the outside of the building. Check signage, emergency access, medical gas storage, waste storage, and general safety.',
    walkOrder:
      'Walk the perimeter. Check the main entrance, emergency exit paths, medical gas bulk storage, dumpster/waste area, and loading dock if present.',
    requiredPhotos: [
      { label: 'Building front', description: 'Photograph the main building facade including signage and main entrance.', angle: 'entry', minCount: 1 },
      { label: 'Each building side', description: 'Photograph each side of the building exterior showing condition, exits, air intakes, and grounds.', angle: 'overview', minCount: 3 },
    ],
    estimatedMinutes: 10,
    sections: [
      {
        domain: 'LSC',
        domainLabel: 'Life Safety Code',
        items: [
          {
            label: 'Emergency exit paths and exterior doors',
            narrative:
              'Verify: (1) all exit doors open freely from inside without special knowledge or tools, (2) exit discharge paths are clear to a public way — no obstructions, debris, or locked gates between exit doors and the street/parking area, (3) exterior exit lighting is functional (test during daytime by looking for fixtures), (4) exit doors are not chained, padlocked, or barricaded from outside, (5) emergency vehicle access is unobstructed. Note any accumulation of snow, ice, or debris on exit paths.',
            cmsTag: '§416.44(b), NFPA 101',
            tjcRef: 'LS.02.01.20',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'exterior-lsc-emerg-emergency-exit-paths',
                text: 'Are all emergency exit paths unobstructed by dumpsters, parked vehicles, or other objects, and do they lead to a public way?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'exterior-lsc-emerg-exterior-doors-operation',
                text: 'Do all exterior egress doors swing outward, open freely from the inside without a key, and have functional panic hardware if required by occupancy?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'exterior-lsc-emerg-exit-lighting',
                text: 'Is exterior exit lighting functional at all exit discharge points (test fixtures for operation)?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'exterior-lsc-emerg-no-locks-chains',
                text: 'Are exit doors free of chains, padlocks, or outside barricades that would impede emergency egress?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'exterior-lsc-emerg-vehicle-access',
                text: 'Is emergency vehicle access to the facility unobstructed and clear of snow, ice, or debris?',
                appendixLRef: '416.44(b)',
              },
            ],
          },
          {
            label: 'Medical gas bulk storage (if exterior)',
            narrative:
              'If there is a bulk oxygen tank or cylinder storage outside: (1) verify the enclosure is secure (fenced or caged), (2) "No Smoking" and "Oxygen" signs are posted, (3) no combustible materials stored within 20 feet, (4) cylinders are secured upright, (5) full and empty cylinders separated. Check that the bulk system has a current inspection from the gas supplier.',
            cmsTag: '§416.44(b), NFPA 99',
            tjcRef: 'EC.02.05.01',
            evidence: ['photo'],
            checklistPrompts: [
              {
                id: 'exterior-lsc-medic-bulk-oxygen-storage',
                text: 'Is the bulk oxygen storage area enclosed with a secure fence or cage to prevent unauthorized access?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'exterior-lsc-medic-oxygen-cylinder-security',
                text: 'Are all oxygen cylinders stored upright and physically secured to prevent tipping or falling?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'exterior-lsc-medic-no-smoking-signage',
                text: 'Are "No Smoking" and "Oxygen" warning signs posted at the bulk storage area?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'exterior-lsc-medic-combustibles-20ft',
                text: 'Is the bulk storage area free of combustible materials within 20 feet?',
                appendixLRef: '416.44(b)',
              },
              {
                id: 'exterior-lsc-medic-full-empty-separated',
                text: 'Are full and empty cylinders separated at the bulk storage area, with a current inspection on file from the gas supplier?',
                appendixLRef: '416.44(b)',
              },
            ],
          },
        ],
      },
      {
        domain: 'ENV',
        domainLabel: 'Environment of Care',
        items: [
          {
            label: 'Exterior maintenance and pest control',
            narrative:
              'Check: (1) building exterior is in good repair — no visible structural damage, (2) dumpster area is clean and enclosed, (3) no evidence of pest harborage (gaps around doors, uncovered vents, standing water), (4) outdoor air intakes are not near dumpsters, loading docks, or standing water, (5) smoking area (if any) is at least 25 feet from entrances and air intakes, (6) signage — the facility name, address, and "Ambulatory Surgical Center" designation should be visible.',
            cmsTag: '§416.44(a)(1)',
            tjcRef: 'EC.02.06.01',
            evidence: ['photo', 'observation'],
          
    checklistPrompts: [
      {
        id: 'ext-env-maint-pest-control',
        text: 'Are pest control measures in place and properly maintained to prevent infestations that could compromise the safety and sterility of the operating rooms?',
        appendixLRef: '416.44(a)(1)',
      },
      {
        id: 'ext-env-maint-exterior-conditions',
        text: 'Is the exterior of the facility, including areas surrounding operating rooms, maintained in a condition that prevents the accumulation of debris, moisture, or other environmental factors that could impact the safety and sterility of the ORs',
        appendixLRef: '416.44(a)(1)',
      },
      {
        id: 'ext-env-building-structural-repair',
        text: 'Is the building exterior in good repair with no visible structural damage?',
        appendixLRef: '416.44(a)(1)',
      },
      {
        id: 'ext-env-dumpster-enclosed-clean',
        text: 'Is the dumpster area clean and enclosed?',
        appendixLRef: '416.44(a)(1)',
      },
      {
        id: 'ext-env-air-intake-separation',
        text: 'Are outdoor air intakes located away from dumpsters, loading docks, and standing water?',
        appendixLRef: '416.44(a)(1)',
      },
      {
        id: 'ext-env-smoking-area-distance',
        text: 'Is the designated smoking area (if any) at least 25 feet from entrances and outdoor air intakes?',
        appendixLRef: '416.44(a)(1)',
      },
    ],
},
        ],
      },
      {
        domain: 'ADA' as SurveyDomain,
        domainLabel: 'ADA Accessibility',
        items: [
          {
            label: 'ADA parking: van-accessible spaces, ISA signage, access aisle',
            narrative:
              'Per ADA Accessibility Guidelines (ADAAG), verify the parking lot has the required number of accessible spaces based on total lot capacity. Check: (1) van-accessible spaces are present with an access aisle of at least 96 inches (8 feet), (2) the International Symbol of Accessibility (ISA) is posted on a vertical sign at each accessible space — ground-only markings are insufficient, (3) "Van Accessible" designation is posted on van spaces, (4) the access aisle is free of obstructions (no shopping carts, landscape debris, or encroaching vehicles), (5) spaces are located on the shortest accessible route to the building entrance. Photograph the accessible parking area, signage, and access aisles.',
            cmsTag: '28CFR36.502',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'ada-parking-van-accessible-spaces',
                text: 'Are van-accessible parking spaces present with 96-inch (8-foot) access aisles?',
                appendixLRef: '28CFR36.502',
              },
              {
                id: 'ada-parking-isa-signage',
                text: 'Is the International Symbol of Accessibility (ISA) posted on a vertical sign at each accessible space?',
                appendixLRef: '28CFR36.502',
              },
              {
                id: 'ada-parking-access-aisle-clear',
                text: 'Is the access aisle free of obstructions (vehicles, landscape debris, shopping carts)?',
                appendixLRef: '28CFR36.502',
              },
            ],
          },
          {
            label: 'Path of travel: smooth surface, curb cuts, no obstructions',
            narrative:
              'Per ADAAG, the path of travel from accessible parking to the building entrance must be firm, stable, slip-resistant, and free of barriers. Walk the path and verify: (1) the surface is smooth and continuous — no heaving sidewalk sections, loose gravel, or uneven pavement that would impede a wheelchair, (2) curb cuts with detectable warning surfaces (truncated domes) are present at all transitions from parking to sidewalk, (3) the path is at least 36 inches wide (44 inches preferred) and free of obstructions — no parked vehicles, planters, sandwich boards, or landscape features encroaching on the path, (4) cross slopes do not exceed 1:48 (2%). Photograph any barriers or trip hazards along the path.',
            cmsTag: '28CFR36.502',
            checklistPrompts: [
              {
                id: 'ada-path-surface-smooth-continuous',
                text: 'Is the path of travel surface smooth, continuous, and free of heaving, loose gravel, or uneven pavement?',
                appendixLRef: '28CFR36.502',
              },
              {
                id: 'ada-path-curb-cuts-present',
                text: 'Are curb cuts with detectable warning surfaces (truncated domes) present at parking-to-sidewalk transitions?',
                appendixLRef: '28CFR36.502',
              },
              {
                id: 'ada-path-width-clear',
                text: 'Is the path at least 36 inches wide and free of obstructions (vehicles, planters, landscape features)?',
                appendixLRef: '28CFR36.502',
              },
            ],
            evidence: ['photo', 'observation'],
          },
          {
            label: 'Emergency egress: accessible exit routes, visual + audible alarms',
            narrative:
              'Per ADA and NFPA 101, emergency egress routes must be accessible to individuals with disabilities. Walk the exterior emergency exit paths and verify: (1) accessible exit routes from the building to a public way are designated and free of barriers, (2) exit discharge surfaces are firm, stable, and navigable by wheelchair, (3) both visual (strobe) and audible alarm coverage extends to outdoor areas where patients or staff may be during an emergency — check for alarm notification appliances at exit discharge points, (4) emergency exit doors are accessible (lever hardware, adequate clear width, manageable opening force). Photograph any barriers on exterior exit routes.',
            cmsTag: '28CFR36.502',
            evidence: ['photo', 'observation'],
            checklistPrompts: [
              {
                id: 'ext-ada-emergency-egress-accessible-emergency-egress-routes',
                text: 'Are all exterior emergency exit paths free of barriers and clearly designated as accessible routes from the building to a public way?',
                appendixLRef: '28CFR36.502',
              },
              {
                id: 'ext-ada-emergency-egress-accessible-alarm-coverage-outdoor',
                text: 'Is there both visual (strobe) and audible alarm coverage at all outdoor exit discharge points where patients or staff may be during an emergency?',
                appendixLRef: '28CFR36.502',
              },
            ],
          },
        ],
      },
    ],
  },
]

// ── Inject common life safety section into areas that lack one ─────────────

for (const area of SURVEY_WALKTHROUGH) {
  const hasLSC = area.sections.some((s) => s.domain === ('LSC' as SurveyDomain))
  if (!hasLSC) {
    area.sections.push(commonLifeSafetySection(area.area))
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Total number of inspection items across all areas */
export function totalInspectionItems(): number {
  return SURVEY_WALKTHROUGH.reduce(
    (sum, area) => sum + area.sections.reduce((s2, sec) => s2 + sec.items.length, 0),
    0,
  )
}

/** Get flat list of all areas in suggested walk order */
export function walkOrderAreas(): FacilityArea[] {
  return SURVEY_WALKTHROUGH.map((aw) => aw.area)
}

// ── Tracer Templates ────────────────────────────────────────────────────────

export interface TracerStep {
  label: string
  area: FacilityArea
  whatToObserve: string
  questionsToAsk: string[]
  standardRefs: string[]
}

export interface TracerTemplate {
  id: string
  name: string
  type: 'patient' | 'system'
  description: string
  steps: TracerStep[]
}

export const PATIENT_TRACER_TEMPLATE: TracerTemplate = {
  id: 'patient-surgical',
  name: 'Surgical Patient Tracer',
  type: 'patient',
  description: 'Follow a surgical patient through the entire care journey — from pre-op admission through OR procedure to PACU recovery and discharge. Verify that each handoff is safe, documented, and compliant.',
  steps: [
    {
      label: 'Pre-Op Admission & Assessment',
      area: 'Pre-Op',
      whatToObserve: 'Observe the patient intake process. Verify H&P is current, consent is signed before sedation, allergies are documented, medication reconciliation is complete, and the patient is properly identified (two identifiers).',
      questionsToAsk: [
        'How do you verify patient identity?',
        'When is consent obtained — before or after sedation?',
        'Walk me through your medication reconciliation process.',
        'How do you screen for pregnancy?',
        'What happens if the H&P is missing or expired?',
      ],
      standardRefs: ['§416.52(a)', 'PC.01.02.03', 'NPSG.01.01.01'],
    },
    {
      label: 'Pre-Procedure Verification & Site Marking',
      area: 'Pre-Op',
      whatToObserve: 'Verify the Universal Protocol is followed: correct patient, correct procedure, correct site. Check that the operative site is marked by the surgeon. Look for the pre-procedure verification checklist.',
      questionsToAsk: [
        'Who marks the site — the surgeon or a designee?',
        'What happens if there is a discrepancy between the consent and the schedule?',
        'How do you handle patients who cannot participate in verification?',
      ],
      standardRefs: ['UP.01.01.01', 'UP.01.02.01'],
    },
    {
      label: 'OR Handoff & Time-Out',
      area: 'OR 1',
      whatToObserve: 'Observe the handoff from pre-op to OR team. Watch the time-out: all team members pause, patient/procedure/site are verified verbally, and any relevant safety concerns (allergies, antibiotic timing, implants) are stated.',
      questionsToAsk: [
        'Who leads the time-out?',
        'What information is communicated during the handoff?',
        'When are prophylactic antibiotics administered relative to incision?',
        'How do you verify correct implants?',
      ],
      standardRefs: ['UP.01.03.01', '§416.42(a)', 'NPSG.07.05.01'],
    },
    {
      label: 'Intraoperative Care',
      area: 'OR 1',
      whatToObserve: 'If observing a case: check sterile field maintenance, medication labeling on the field, specimen handling, accurate count procedures (sponge/instrument/sharps), and documentation of intraoperative events.',
      questionsToAsk: [
        'How do you label medications on the sterile field?',
        'Walk me through your count procedure.',
        'What happens if there is a count discrepancy?',
        'How are specimens labeled and handled?',
      ],
      standardRefs: ['NPSG.03.04.01', 'IC.02.02.01', '§416.42(a)'],
    },
    {
      label: 'PACU Handoff & Recovery',
      area: 'PACU',
      whatToObserve: 'Observe the OR-to-PACU handoff. Verify vital signs are taken on arrival, a post-anesthesia assessment is performed, pain is assessed and managed, and the Aldrete score (or equivalent) is used for discharge readiness.',
      questionsToAsk: [
        'What information is communicated during the OR-to-PACU handoff?',
        'How often are vitals assessed in PACU?',
        'What discharge readiness tool do you use?',
        'What score is required before discharge?',
      ],
      standardRefs: ['§416.52(b)', 'PC.03.01.03'],
    },
    {
      label: 'Discharge',
      area: 'PACU',
      whatToObserve: 'Review discharge documentation. Verify written instructions are provided, a responsible adult is identified, follow-up is scheduled, and an after-hours emergency contact number is given. Check that discharge criteria are met and documented.',
      questionsToAsk: [
        'What happens if a patient does not meet discharge criteria?',
        'How do you verify the responsible adult is present?',
        'What if a patient needs to be transferred to a hospital?',
      ],
      standardRefs: ['§416.52(c)', 'PC.04.01.05'],
    },
  ],
}

export const SYSTEM_TRACER_TEMPLATES: TracerTemplate[] = [
  {
    id: 'system-medication',
    name: 'Medication Management Tracer',
    type: 'system',
    description: 'Follow the medication lifecycle across the facility — from procurement/storage through ordering, preparation, administration, and monitoring. Verify compliance at each step.',
    steps: [
      {
        label: 'Medication Procurement & Storage',
        area: 'Med Storage',
        whatToObserve: 'Check medication storage security, temperature monitoring, controlled substance counts, and expiration date management.',
        questionsToAsk: ['Who manages your drug formulary?', 'How do you handle drug recalls?', 'How are controlled substances reconciled?'],
        standardRefs: ['§416.48(a)', 'MM.03.01.01'],
      },
      {
        label: 'Medication Ordering & Preparation',
        area: 'Pre-Op',
        whatToObserve: 'Observe medication ordering process. Check for verbal order read-back, allergy verification, and proper labeling of prepared medications.',
        questionsToAsk: ['How are verbal orders documented?', 'What is your LASA medication strategy?'],
        standardRefs: ['§416.48', 'MM.04.01.01', 'NPSG.03.04.01'],
      },
      {
        label: 'Medication Administration — OR',
        area: 'OR 1',
        whatToObserve: 'Check anesthesia cart medication labeling, sterile field medication labeling, and two-patient-identifier verification before administration.',
        questionsToAsk: ['How are medications labeled on the sterile field?', 'What is the process for multi-dose vials?'],
        standardRefs: ['NPSG.03.04.01', 'MM.05.01.07'],
      },
      {
        label: 'Medication Administration — PACU',
        area: 'PACU',
        whatToObserve: 'Observe pain medication administration. Check documentation of drug, dose, route, time, and patient response.',
        questionsToAsk: ['How is pain reassessed after medication administration?', 'Where are reversal agents stored?'],
        standardRefs: ['§416.48', 'MM.05.01.01'],
      },
    ],
  },
  {
    id: 'system-ic',
    name: 'Infection Control Tracer',
    type: 'system',
    description: 'Follow infection control practices across all areas — hand hygiene, PPE use, environmental cleaning, instrument reprocessing, and surgical site infection prevention.',
    steps: [
      {
        label: 'Hand Hygiene Compliance',
        area: 'Pre-Op',
        whatToObserve: 'Observe hand hygiene at point of care. Check availability of alcohol-based hand rub and handwashing sinks. Note compliance/non-compliance events.',
        questionsToAsk: ['How do you monitor hand hygiene compliance?', 'What is your compliance rate?'],
        standardRefs: ['§416.51(a)', 'IC.02.01.01', 'NPSG.07.01.01'],
      },
      {
        label: 'Instrument Reprocessing',
        area: 'SPD',
        whatToObserve: 'Follow an instrument set through the full reprocessing cycle. Verify IFU compliance, biological indicator monitoring, and sterile storage conditions.',
        questionsToAsk: ['Where are your IFUs?', 'What was your last BI failure and what did you do?'],
        standardRefs: ['§416.51(b)', 'IC.02.02.01'],
      },
      {
        label: 'OR Sterile Technique',
        area: 'OR 1',
        whatToObserve: 'Observe surgical attire compliance, sterile field maintenance, traffic patterns, and environmental cleaning between cases.',
        questionsToAsk: ['What disinfectant do you use for turnover?', 'What is the contact time?'],
        standardRefs: ['§416.51(a)', 'IC.02.02.01'],
      },
      {
        label: 'Environmental Services',
        area: 'Corridor',
        whatToObserve: 'Check cleaning schedules, chemical dilution verification, linen handling, and waste segregation throughout the facility.',
        questionsToAsk: ['How do you verify disinfectant dilution?', 'What is your terminal cleaning protocol?'],
        standardRefs: ['§416.51(a)', 'IC.02.02.01'],
      },
    ],
  },
  {
    id: 'system-em',
    name: 'Emergency Management Tracer',
    type: 'system',
    description: 'Trace the facility\'s emergency preparedness across all areas — plans, drills, equipment, training, and communication systems.',
    steps: [
      {
        label: 'Emergency Management Plan Review',
        area: 'Admin Office',
        whatToObserve: 'Review the all-hazards EMP. Check for fire, weather, utility failure, active threat, and mass casualty scenarios. Verify roles and communication plan.',
        questionsToAsk: ['When was the plan last updated?', 'How are staff notified in an emergency?'],
        standardRefs: ['§416.41(b)', 'EM.02.01.01'],
      },
      {
        label: 'Emergency Equipment Check',
        area: 'Pre-Op',
        whatToObserve: 'Verify crash cart contents, daily checks, defibrillator function, emergency airway equipment, and MH kit accessibility.',
        questionsToAsk: ['Where is the MH kit?', 'What is the dose of Dantrolene?', 'When was the last code?'],
        standardRefs: ['§416.42(b)', 'EC.02.04.01'],
      },
      {
        label: 'Fire Safety Systems',
        area: 'Mechanical',
        whatToObserve: 'Check fire alarm panel, sprinkler system, fire extinguishers, emergency generator, and medical gas shutoffs.',
        questionsToAsk: ['When was the last fire drill?', 'How often is the generator tested?'],
        standardRefs: ['§416.44(b)', 'LS.02.01.30', 'EC.02.05.03'],
      },
      {
        label: 'Staff Emergency Competency',
        area: 'OR 1',
        whatToObserve: 'Ask clinical staff about their role during various emergencies. Verify awareness of evacuation routes, rally points, and code procedures.',
        questionsToAsk: ['What is the evacuation route from this OR?', 'What is your role during a fire?', 'Walk me through an airway fire protocol.'],
        standardRefs: ['§416.41(b)', 'EM.03.01.03'],
      },
    ],
  },
]

// ── Appendix-L scaffolding (issue #1227) ────────────────────────────────────

/**
 * Stable deterministic id for an `InspectionItem` derived from its
 * position in `SURVEY_WALKTHROUGH`. Used by the `UNMAPPED_ITEMS`
 * registry and the coverage test so PR-B child stories can target
 * specific items without depending on label collisions.
 *
 * Format: `<area>|<domain>|<index>` (area + domain are stable
 * categorical fields; index is the item's position in its
 * DomainSection.items array).
 */
export function inspectionItemId(area: FacilityArea, domain: SurveyDomain, indexInSection: number): string {
  return `${area}|${domain}|${indexInSection}`
}

function buildAllInspectionItemIds(): readonly string[] {
  const ids: string[] = []
  for (const walkthrough of SURVEY_WALKTHROUGH) {
    for (const section of walkthrough.sections) {
      for (let i = 0; i < section.items.length; i += 1) {
        ids.push(inspectionItemId(walkthrough.area, section.domain, i))
      }
    }
  }
  return ids
}

/**
 * Total InspectionItem count across every area × domain × section.
 * Pinned by the coverage test so an accidental add/remove without an
 * Appendix-L mapping update fails CI loudly. Update intentionally
 * alongside any catalog changes.
 */
export const TOTAL_INSPECTION_ITEMS = buildAllInspectionItemIds().length

/**
 * Every current `InspectionItem` id that has NO authored
 * `checklistPrompts` mapping yet. PR-A merges with all 168 items in
 * this set. Each child story (#1228–#1231) shrinks the set as it
 * authors per-domain prompts.
 */
export const UNMAPPED_ITEMS: readonly string[] = (() => {
  const unmapped: string[] = []
  for (const walkthrough of SURVEY_WALKTHROUGH) {
    for (const section of walkthrough.sections) {
      for (let i = 0; i < section.items.length; i += 1) {
        const item = section.items[i]
        if (!item.checklistPrompts || item.checklistPrompts.length === 0) {
          unmapped.push(inspectionItemId(walkthrough.area, section.domain, i))
        }
      }
    }
  }
  return unmapped
})()

/**
 * Render-path for guided-survey checklist rows.
 *
 * - When an item has authored `checklistPrompts`, those are returned
 *   verbatim and carry their `appendixLRef` for citation-badge
 *   rendering.
 * - Otherwise, fall back to the legacy enumerated-narrative parser
 *   (e.g., "(1) … (2) … (3) …" guidance text) and return prompts
 *   without an `appendixLRef`. The UI renders the citation badge only
 *   when the field is present.
 *
 * Migrated from `frontend/src/pages/survey/SurveyWalkthrough.tsx` so
 * the rendering rule lives next to the type definition.
 */
export function getInspectionChecklistPrompts(item: InspectionItem): RenderedChecklistPrompt[] {
  if (item.checklistPrompts && item.checklistPrompts.length > 0) {
    return item.checklistPrompts
  }
  const matches = Array.from(item.narrative.matchAll(/\((\d+)\)\s+([\s\S]*?)(?=\s+\(\d+\)\s+|$)/g))
    .map((match): NarrativeChecklistPrompt => ({
      id: `check-${match[1]}`,
      text: match[2]
        .replace(/\s+/g, ' ')
        .replace(/[,.?;:]+$/, '')
        .trim(),
    }))
    .filter((prompt) => prompt.text.length > 0)

  if (matches.length > 1) return matches
  return [{ id: 'check-main', text: item.label }]
}
